import {
  DS9Context,
  DS9Provider,
  ThemeManager,
  ThemeOption,
  progress_default,
  spinner_default,
  toast_default,
  useDS9Context,
  useMedia,
  useSSRDisabled,
  useToast,
  use_media_default
} from "./chunk-YT6WIMWL.js";
import {
  isValidYoutubeUrl
} from "./chunk-GALEMFKX.js";
import {
  dig,
  hexToRgb,
  isBlank,
  isColorDark,
  mergeRefs,
  objEmpty,
  sinceHuman,
  timeHuman
} from "./chunk-Y3WXUM7H.js";
import {
  __name,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-CRQHCYH4.js";

// src/components/autocomplete.tsx
import React2, { useId } from "react";
import { FloatingPortal, offset, size, useFloating } from "@floating-ui/react";
import cn2 from "classnames";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";

// src/components/label.tsx
import "react";
import cn from "classnames";
import { jsx } from "react/jsx-runtime";
var Label = /* @__PURE__ */ __name(({ children, className, capitalize, skeleton, onClick }) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "mb-2 font-medium text-ds9-500 dark:text-ds9-400 cursor-default",
        {
          "uppercase text-xs leading-5": !capitalize,
          "capitalize text-[13px] leading-none": capitalize
        },
        className
      ),
      onClick,
      children: /* @__PURE__ */ jsx("span", { className: cn("inline", { "ds9-skeleton": skeleton }), children })
    }
  );
}, "Label");
var label_default = Label;

// src/components/autocomplete.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var Option = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  props.className = ({ active }) => cn2(
    "cursor-pointer px-2 sm:px-5 py-2 h-12 sm:h-auto flex items-center leading-5 text-sm",
    {
      "text-ds9-500 dark:text-ds9-400": !active,
      "bg-ds9-100 dark:bg-ds9-800 text-ds9-foreground rounded sm:rounded-none": active
    },
    customClassName
  );
  return /* @__PURE__ */ jsx2(Combobox.Option, __spreadProps(__spreadValues({}, props), { children }));
}, "Option");
var Autocomplete = /* @__PURE__ */ __name(({
  id,
  name,
  value,
  setQuery,
  onSelected,
  displayValue,
  label,
  className,
  error,
  disabled,
  loading,
  skeleton,
  capitalize,
  placeholder,
  help,
  children,
  inputRef
}) => {
  const { x, y, strategy, refs } = useFloating({
    middleware: [
      offset(8),
      size({
        apply({ availableWidth, availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight - 10}px`,
            width: `${rects.reference.width}px`
          });
        }
      })
    ]
  });
  const onChange = /* @__PURE__ */ __name((value2) => {
    if (value2 === null) {
      return;
    }
    onSelected(value2);
  }, "onChange");
  const desktop = use_media_default(`(min-width: 640px)`, true);
  const empty = React2.Children.count(children) === 0 || children === false;
  const defaultId = useId();
  id = id || defaultId;
  return /* @__PURE__ */ jsx2(
    Combobox,
    {
      as: "div",
      className: cn2("relative", { "ds9-skeleton": skeleton }, className),
      onChange,
      nullable: true,
      disabled,
      value,
      children: ({ open }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("label", { htmlFor: id, className: "w-full", children: [
          label ? /* @__PURE__ */ jsx2(label_default, { capitalize, children: label }) : null,
          /* @__PURE__ */ jsxs("div", { className: "w-full relative", children: [
            /* @__PURE__ */ jsx2(
              Combobox.Input,
              {
                id,
                name,
                "aria-invalid": "false",
                placeholder,
                onChange: (e) => setQuery && setQuery(e.target.value),
                displayValue,
                ref: (r) => {
                  refs.setReference(r);
                  if (inputRef) {
                    inputRef.current = r;
                  }
                },
                className: cn2(
                  "text-sm leading-normal w-full h-10 pl-10 pr-12 inline-flex rounded border transition-colors focus:outline-none focus:ring-0",
                  {
                    "dark:bg-ds9-background": !disabled,
                    "bg-ds9-50 dark:bg-ds9-900 cursor-not-allowed": disabled,
                    "border-ds9-200 dark:border-ds9-700 focus:border-ds9-500 dark:focus:border-ds9-400 placeholder:text-[rgb(102,102,102)]": !error,
                    "hover:border-ds9-500 dark:hover:border-ds9-400": !error && !disabled,
                    "border-ds9-error dark:border-ds9-error-light placeholder:text-ds9-error dark:placeholder:text-ds9-error-light": error
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx2("div", { className: "absolute inset-y-3 left-3 flex items-center justify-center text-ds9-300 dark:text-ds9-600 pointer-events-none", children: /* @__PURE__ */ jsx2(MagnifyingGlassIcon, { className: "w-4 h-4" }) }),
            loading ? /* @__PURE__ */ jsx2("div", { className: "absolute inset-y-3 right-8 flex items-center justify-center text-ds9-300 dark:text-ds9-600", children: /* @__PURE__ */ jsx2(spinner_default, { variant: "line" }) }) : null,
            /* @__PURE__ */ jsx2(Combobox.Button, { className: "absolute inset-y-3 right-3 flex items-center justify-center text-ds9-300 dark:text-ds9-600", children: /* @__PURE__ */ jsx2(
              ChevronDownIcon,
              {
                className: cn2("w-5 h-5 transition-transform", {
                  "rotate-180": open,
                  "hover:text-ds9-foreground": !disabled
                })
              }
            ) })
          ] }),
          help ? /* @__PURE__ */ jsx2("p", { className: "mt-2.5 text-[0.8125rem] leading-3 text-ds9-500 dark:text-ds9-400", children: help }) : null
        ] }),
        /* @__PURE__ */ jsx2(FloatingPortal, { children: desktop ? open && /* @__PURE__ */ jsxs(
          Combobox.Options,
          {
            className: "z-40 w-full origin-top overflow-y-auto rounded-ds9 bg-ds9-background py-1 shadow-ds9-md dark:shadow-ds9-dark-md focus:outline-none",
            ref: refs.setFloating,
            style: {
              position: strategy,
              top: y != null ? y : 0,
              left: x != null ? x : 0,
              width: "max-content"
            },
            static: true,
            children: [
              empty && !loading ? /* @__PURE__ */ jsx2("div", { className: "py-2 text-center text-sm text-ds9-500 dark:text-ds9-400", children: "No results found." }) : null,
              loading && empty ? /* @__PURE__ */ jsx2("div", { className: "py-2 text-center text-sm text-ds9-500 dark:text-ds9-400", children: "Loading..." }) : null,
              children
            ]
          }
        ) : /* @__PURE__ */ jsxs(Transition, { show: open, children: [
          /* @__PURE__ */ jsx2(
            Transition.Child,
            {
              as: React2.Fragment,
              enter: "transition-opacity ease-linear duration-150",
              enterFrom: "opacity-0",
              enterTo: "opacity-100",
              leave: "transition-opacity ease-linear duration-150",
              leaveFrom: "opacity-100",
              leaveTo: "opacity-0",
              children: /* @__PURE__ */ jsx2("div", { className: "fixed inset-0 bg-ds9-black bg-opacity-25 dark:bg-opacity-75 z-40" })
            }
          ),
          /* @__PURE__ */ jsx2(
            Transition.Child,
            {
              as: React2.Fragment,
              enter: "transition duration-150 ease-out",
              enterFrom: "transform translate-y-full",
              enterTo: "transform translate-y-0",
              leave: "transition duration-150 ease-out",
              leaveFrom: "transform translate-y-0",
              leaveTo: "transform translate-y-full",
              children: /* @__PURE__ */ jsx2(
                Combobox.Options,
                {
                  static: true,
                  className: "rounded-t-lg shadow-ds9-md dark:shadow-[0_0_0_1px] dark:shadow-ds9-700 bg-ds9-background ring-0 focus:outline-none fixed z-40 bottom-0 top-auto inset-x-0",
                  children: /* @__PURE__ */ jsxs("div", { className: "rounded-ds9 border border-ds9-200 dark:border-ds9-700", children: [
                    /* @__PURE__ */ jsxs("div", { className: "w-full relative", children: [
                      /* @__PURE__ */ jsx2(
                        Combobox.Input,
                        {
                          name,
                          "aria-invalid": "false",
                          placeholder,
                          onChange: (e) => setQuery && setQuery(e.target.value),
                          className: cn2(
                            "text-sm leading-normal w-full h-10 pl-10 pr-12 inline-flex rounded-t-ds9 border-0 border-b transition-colors focus:outline-none focus:ring-0",
                            {
                              "dark:bg-ds9-background": !disabled,
                              "bg-ds9-50 dark:bg-ds9-900 cursor-not-allowed": disabled,
                              "border-ds9-200 dark:border-ds9-700 placeholder:text-[rgb(102,102,102)] dark:placeholder:text-[rgb(117,117,117)]": !error,
                              "border-ds9-error dark:border-ds9-error-light placeholder:text-[rgba(238,0,0,0.4)] dark:placeholder:text-[rgba(255,0,0,0.4)]": error
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx2("div", { className: "absolute inset-y-3 left-3 flex items-center justify-center text-ds9-300 dark:text-ds9-600 pointer-events-none", children: /* @__PURE__ */ jsx2(MagnifyingGlassIcon, { className: "w-4 h-4" }) }),
                      /* @__PURE__ */ jsx2(Combobox.Button, { className: "absolute inset-y-3 right-3 flex items-center justify-center text-ds9-300 hover:text-ds9-foreground dark:text-ds9-600", children: /* @__PURE__ */ jsx2(ChevronDownIcon, { className: cn2("w-5 h-5 transition-transform", { "rotate-180": open }) }) })
                    ] }),
                    /* @__PURE__ */ jsxs("div", { className: "p-2 rounded-b-ds9 overflow-y-auto h-[75dvh]", children: [
                      empty && !loading ? /* @__PURE__ */ jsx2("div", { className: "py-2 text-center text-sm text-ds9-500 dark:text-ds9-400", children: "No results found." }) : null,
                      loading && empty ? /* @__PURE__ */ jsx2("div", { className: "py-2 text-center text-sm text-ds9-500 dark:text-ds9-400", children: "Loading..." }) : null,
                      children
                    ] })
                  ] })
                }
              )
            }
          )
        ] }) })
      ] })
    }
  );
}, "Autocomplete");
Autocomplete.Option = Option;
var autocomplete_default = Autocomplete;

// src/components/avatar.tsx
import { useMemo } from "react";
import cn3 from "classnames";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var palette = [
  "#6dccb1",
  "#79aad9",
  "#ee789d",
  "#a987d1",
  "#e4a6c7",
  "#f1d86f",
  "#d2c0a0",
  "#f5a35c",
  "#c47c6c",
  "#ff7e62"
];
var Avatar = /* @__PURE__ */ __name(({ name, children, className, color, skeleton }) => {
  const initials = useMemo(() => {
    const parts = name.split(" ");
    if (parts.length > 1) {
      return [parts[0], parts[parts.length - 1]].filter((p) => !!p).map((p) => p && p[0]).join("");
    }
    return parts[0] ? parts[0][0] : "";
  }, [name]);
  const assignedColor = color || palette[Math.floor(name.length % palette.length)];
  const textColor = isColorDark(...hexToRgb(assignedColor || "")) ? "text-ds9-white" : "text-ds9-black";
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: cn3(
        "flex items-center justify-center rounded-full uppercase select-none",
        { "ds9-skeleton ds9-skeleton-round": skeleton },
        textColor,
        className
      ),
      style: { backgroundColor: assignedColor },
      children: [
        !children ? /* @__PURE__ */ jsx3("span", { children: initials }) : null,
        children
      ]
    }
  );
}, "Avatar");
var avatar_default = Avatar;

// src/components/badge.tsx
import "react";
import cn4 from "classnames";
import { jsx as jsx4 } from "react/jsx-runtime";
var Badge = /* @__PURE__ */ __name(({ children, variant, size: size6, skeleton, className }) => {
  return /* @__PURE__ */ jsx4(
    "div",
    {
      className: cn4(
        "flex items-center rounded-full tnum max-w-fit font-medium",
        {
          "px-2.5 text-xs leading-none h-6": size6 === "m" || !size6,
          "px-1.5 text-[11px] leading-none h-5": size6 === "s",
          "bg-ds9-800 text-ds9-background dark:bg-ds9-300 dark:text-ds9-900": variant === "default" || !variant,
          "bg-[rgb(143,143,143)] text-ds9-background dark:text-[rgb(10,10,10)]": variant === "gray",
          "bg-[rgb(235,235,235)] text-[rgb(23,23,23)] dark:bg-[rgb(31,31,31)] dark:text-[rgb(237,237,237)]": variant === "gray-subtle",
          "bg-[rgb(0,114,245)] text-ds9-background dark:text-[rgb(10,10,10)]": variant === "blue",
          "bg-[rgb(235,245,255)] text-[rgb(0,104,214)] dark:bg-[rgb(16,35,61)] dark:text-[rgb(82,168,255)]": variant === "blue-subtle",
          "bg-[rgb(255,178,36)] text-ds9-black": variant === "amber",
          "bg-[rgb(255,244,214)] text-[rgb(163,82,0)] dark:bg-[rgb(51,27,8)] dark:text-[rgb(255,153,10)]": variant === "amber-subtle",
          "bg-[rgb(229,72,77)] text-ds9-background": variant === "red",
          "bg-[rgb(255,235,235)] text-[rgb(203,42,47)] dark:bg-[rgb(60,22,24)] dark:text-[rgb(255,97,102)]": variant === "red-subtle",
          "bg-[rgb(69,165,87)] text-ds9-background dark:text-[rgb(10,10,10)]": variant === "green",
          "bg-[rgb(235,250,235)] text-[rgb(41,122,58)] dark:bg-[rgb(15,46,24)] dark:text-[rgb(98,192,115)]": variant === "green-subtle",
          "ds9-skeleton ds9-skeleton-round": skeleton
        },
        className
      ),
      children
    }
  );
}, "Badge");
var badge_default = Badge;

// src/components/button.tsx
import React5 from "react";
import twcn from "classnames";
import { jsx as jsx5 } from "react/jsx-runtime";
var ButtonContent = /* @__PURE__ */ __name(({ children }) => {
  return /* @__PURE__ */ jsx5("span", { className: "truncate inline-block", children });
}, "ButtonContent");
var ForwardableButton = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { children, variant, size: size6, dark, skeleton, skipSSRSkeleton } = _b, props = __objRest(_b, ["children", "variant", "size", "dark", "skeleton", "skipSSRSkeleton"]);
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  props.disabled = props.disabled || skeleton;
  const className = twcn(
    "rounded-ds9 font-medium px-3 max-w-full flex items-center justify-center gap-2 transition",
    {
      "h-10 text-sm": !size6 || size6 === "m",
      "h-8 text-sm": size6 === "s",
      "h-6 text-xs": size6 === "xs",
      "text-[rgb(23,23,23)] bg-ds9-background shadow-[0_0_0_1px_hsl(0deg_0%_0%_/_8%)] hover:bg-[rgb(242,242,242)] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12)] dark:bg-[rgb(10,10,10)] dark:shadow-[0_0_0_1px_rgba(255_255_255_/_14%)] dark:text-[rgb(237,237,237)] dark:hover:text-[rgb(237,237,237)] dark:hover:bg-[rgb(31,31,31)] dark:hover:shadow-[0_0_0_1px_hsla(0,0%,100%,.17)]": !props.disabled && (!variant || variant === "default" || variant === "secondary") && !dark,
      "bg-[rgb(10,10,10)] shadow-[0_0_0_1px_rgba(255_255_255_/_14%)] text-[rgb(237,237,237)] hover:text-[rgb(237,237,237)] hover:bg-[rgb(31,31,31)] hover:shadow-[0_0_0_1px_hsla(0,0%,100%,.17)]": !props.disabled && (!variant || variant === "default" || variant === "secondary") && dark,
      "bg-[rgb(23,23,23)] text-ds9-background hover:bg-[rgb(56,56,56)] hover:shadow-[0_0_0_1px_rgb(235,235,235)] dark:bg-[rgb(237,237,237)] dark:text-[rgb(10,10,10)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0)] dark:hover:bg-[rgb(204,204,204)] dark:hover:shadow-[0_0_0_1px_rgb(31,31,31)]": !props.disabled && variant === "primary" && !dark,
      "bg-[rgb(237,237,237)] text-[rgb(10,10,10)] shadow-[0_0_0_1px_rgba(0,0,0,0)] hover:bg-[rgb(204,204,204)] hover:shadow-[0_0_0_1px_rgb(31,31,31)]": !props.disabled && variant === "primary" && dark,
      "bg-[rgb(218,47,53)] text-ds9-100 hover:bg-[rgb(174,41,47)] dark:bg-[rgb(217,48,54)] dark:text-ds9-foreground dark:hover:bg-[rgb(255,97,102)]": !props.disabled && variant === "danger" && !dark,
      "bg-[rgb(217,48,54)] text-ds9-white hover:bg-[rgb(255,97,102)]": !props.disabled && variant === "danger" && dark,
      "cursor-not-allowed bg-[rgb(242,242,242)] shadow-[0_0_0_1px_rgb(235,235,235)] text-[rgb(143,143,143)] dark:bg-[rgb(26,26,26)] dark:shadow-[0_0_0_1px_rgb(46,46,46)]": props.disabled,
      "ds9-skeleton ds9-skeleton-rounded": skeleton
    },
    props.className
  );
  props.type = props.type || "button";
  return (
    // eslint-disable-next-line react/button-has-type
    /* @__PURE__ */ jsx5("button", __spreadProps(__spreadValues({}, props), { ref, className, children }))
  );
}, "ForwardableButton");
var Button = React5.forwardRef(ForwardableButton);
ForwardableButton.Content = ButtonContent;
var button_default = Button;

// src/components/button_link.tsx
import React7 from "react";
import twcn2 from "classnames";

// src/components/router_components.tsx
import React6 from "react";
import { Link as RouterLink, NavLink as RouterNavLink } from "react-router-dom";
import { jsx as jsx6 } from "react/jsx-runtime";
var ForwardableNavLink = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { children, __ds9_docs, __ds9_docs_active } = _b, props = __objRest(_b, ["children", "__ds9_docs", "__ds9_docs_active"]);
  if (__ds9_docs) {
    const state = { isActive: __ds9_docs_active != null ? __ds9_docs_active : false, isPending: false, isTransitioning: false };
    return /* @__PURE__ */ jsx6(
      "a",
      __spreadProps(__spreadValues({}, props), {
        href: typeof props.to === "string" ? props.to : props.to.pathname,
        className: typeof props.className === "function" ? props.className(state) : props.className,
        style: typeof props.style === "function" ? props.style(state) : props.style,
        children: typeof children === "function" ? children(state) : children
      })
    );
  }
  return /* @__PURE__ */ jsx6(RouterNavLink, __spreadProps(__spreadValues({}, props), { ref, children }));
}, "ForwardableNavLink");
var NavLink = React6.forwardRef(ForwardableNavLink);
var ForwardableLink = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { children, __ds9_docs } = _b, props = __objRest(_b, ["children", "__ds9_docs"]);
  if (__ds9_docs) {
    return /* @__PURE__ */ jsx6("a", __spreadProps(__spreadValues({}, props), { href: typeof props.to === "string" ? props.to : props.to.pathname, children }));
  }
  return /* @__PURE__ */ jsx6(RouterLink, __spreadProps(__spreadValues({}, props), { ref, children }));
}, "ForwardableLink");
var Link = React6.forwardRef(ForwardableLink);

// src/components/button_link.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var ForwardableButtonLink = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { children, variant, size: size6, skeleton, disabled, skipSSRSkeleton, __ds9_docs } = _b, props = __objRest(_b, ["children", "variant", "size", "skeleton", "disabled", "skipSSRSkeleton", "__ds9_docs"]);
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  disabled = disabled || skeleton;
  const className = twcn2(
    "rounded-ds9 text-sm font-medium px-3 max-w-full flex items-center justify-center gap-2 transition cursor-pointer",
    {
      "h-10 text-sm": !size6 || size6 === "m",
      "h-8 text-sm": size6 === "s",
      "h-6 text-xs": size6 === "xs",
      "text-[rgb(23,23,23)] bg-ds9-background shadow-[0_0_0_1px_hsl(0deg_0%_0%_/_8%)] hover:bg-[rgb(242,242,242)] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12)] dark:bg-[rgb(10,10,10)] dark:shadow-[0_0_0_1px_rgba(255_255_255_/_14%)] dark:text-[rgb(237,237,237)] dark:hover:text-[rgb(237,237,237)] dark:hover:bg-[rgb(31,31,31)] dark:hover:shadow-[0_0_0_1px_hsla(0,0%,100%,.17)]": !disabled && (!variant || variant === "default" || variant === "secondary"),
      "bg-[rgb(23,23,23)] text-ds9-background hover:bg-[rgb(56,56,56)] hover:shadow-[0_0_0_1px_rgb(235,235,235)] dark:bg-[rgb(237,237,237)] dark:text-[rgb(10,10,10)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0)] dark:hover:bg-[rgb(204,204,204)] dark:hover:shadow-[0_0_0_1px_rgb(31,31,31)]": !disabled && variant === "primary",
      "bg-[rgb(218,47,53)] text-ds9-100 hover:bg-[rgb(174,41,47)] dark:bg-[rgb(217,48,54)] dark:text-ds9-foreground dark:hover:bg-[rgb(255,97,102)]": !disabled && variant === "danger",
      "cursor-not-allowed bg-[rgb(242,242,242)] shadow-[0_0_0_1px_rgb(235,235,235)] text-[rgb(143,143,143)] dark:bg-[rgb(26,26,26)] dark:shadow-[0_0_0_1px_rgb(46,46,46)]": disabled,
      "ds9-skeleton ds9-skeleton-rounded": skeleton
    },
    props.className
  );
  return /* @__PURE__ */ jsx7(Link, __spreadProps(__spreadValues({}, props), { ref, className, __ds9_docs, children }));
}, "ForwardableButtonLink");
var ButtonLink = React7.forwardRef(ForwardableButtonLink);
ForwardableButtonLink.Content = ButtonContent;
var button_link_default = ButtonLink;

// src/components/checkbox.tsx
import React8, { useCallback, useId as useId2, useState } from "react";
import cn5 from "classnames";
import { CheckIcon } from "@heroicons/react/20/solid";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var useCheckbox = /* @__PURE__ */ __name((defaultChecked) => {
  const [checked, setValue] = useState(defaultChecked != null ? defaultChecked : false);
  const toggle = useCallback((e) => {
    setValue(e.target.checked);
  }, []);
  return [checked, toggle];
}, "useCheckbox");
var ForwardableCheckbox = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, {
    registerFields,
    label,
    className,
    inputClassName,
    error,
    skeleton,
    capitalize,
    skipSSRSkeleton,
    fullWidth,
    children,
    indeterminate
  } = _b, props = __objRest(_b, [
    "registerFields",
    "label",
    "className",
    "inputClassName",
    "error",
    "skeleton",
    "capitalize",
    "skipSSRSkeleton",
    "fullWidth",
    "children",
    "indeterminate"
  ]);
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  props.disabled = props.disabled || skeleton;
  const defaultId = useId2();
  props.id = props.id || defaultId;
  return /* @__PURE__ */ jsx8("div", { className: cn5("ds9-checkbox", className), children: /* @__PURE__ */ jsxs3("label", { htmlFor: props.id, className: cn5("group/ds9-checkbox", { "pointer-events-none": props.disabled }), children: [
    label ? /* @__PURE__ */ jsx8(label_default, { capitalize, skeleton, children: label }) : null,
    /* @__PURE__ */ jsxs3(
      "span",
      {
        className: cn5("text-sm inline-flex items-start", {
          "cursor-pointer": !props.disabled,
          "w-full": fullWidth,
          "text-ds9-400 dark:text-ds9-500": props.disabled
        }),
        children: [
          /* @__PURE__ */ jsxs3("span", { className: "flex items-center p-0.5 -m-0.5", children: [
            "\u200B",
            /* @__PURE__ */ jsx8(
              "input",
              __spreadProps(__spreadValues(__spreadValues({
                className: "sr-only peer"
              }, registerFields), props), {
                ref: mergeRefs(ref, registerFields == null ? void 0 : registerFields.ref),
                type: "checkbox"
              })
            ),
            indeterminate ? /* @__PURE__ */ jsx8("span", { className: "relative w-4 h-4 flex items-center justify-center rounded-[4px] transition border border-[rgb(143,143,143)] group-hover/ds9-checkbox:bg-ds9-200 group-hover/ds9-checkbox:dark:bg-ds9-800 text-ds9-background peer-disabled:opacity-50", children: /* @__PURE__ */ jsx8(
              "svg",
              {
                fill: "none",
                height: "16",
                viewBox: "0 0 20 20",
                width: "16",
                className: "stroke-ds9-700 dark:stroke-ds9-400",
                children: /* @__PURE__ */ jsx8(
                  "line",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: "2",
                    x1: "5",
                    x2: "15",
                    y1: "10",
                    y2: "10"
                  }
                )
              }
            ) }) : /* @__PURE__ */ jsx8("span", { className: "relative w-4 h-4 flex items-center justify-center rounded-[4px] transition border border-[rgb(143,143,143)] group-hover/ds9-checkbox:bg-ds9-200 group-hover/ds9-checkbox:dark:bg-ds9-800 peer-checked:bg-ds9-foreground peer-checked:dark:bg-ds9-foreground peer-checked:border-ds9-foreground [&_svg]:hidden peer-checked:[&_svg]:block text-ds9-background peer-disabled:opacity-50", children: /* @__PURE__ */ jsx8(CheckIcon, { className: "w-3 h-3" }) })
          ] }),
          /* @__PURE__ */ jsx8("span", { className: "ml-2", children })
        ]
      }
    )
  ] }) });
}, "ForwardableCheckbox");
var Checkbox = React8.forwardRef(ForwardableCheckbox);
var checkbox_default = Checkbox;

// src/components/container.tsx
import "react";
import cn6 from "classnames";
import { jsx as jsx9 } from "react/jsx-runtime";
var Container = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx9("div", { className: cn6("mx-auto max-w-7xl px-6 lg:px-12", className), children });
}, "Container");
var container_default = Container;

// src/components/divider.tsx
import "react";
import cn7 from "classnames";
import { jsx as jsx10 } from "react/jsx-runtime";
var Divider = /* @__PURE__ */ __name(({ className }) => {
  return /* @__PURE__ */ jsx10("hr", { className: cn7("border-t border-ds9-200 dark:border-ds9-700", className) });
}, "Divider");
var divider_default = Divider;

// src/components/empty_state.tsx
import React11 from "react";
import cn8 from "classnames";
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var EmptyState = /* @__PURE__ */ __name(({ title, actions, variant, size: size6, className, children }) => {
  const hasChildren = React11.Children.count(children) > 0;
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: cn8(
        "border border-ds9-200 dark:border-ds9-700 rounded-ds9 px-4 py-8 flex flex-col items-center justify-center",
        {
          "bg-ds9-50 dark:bg-ds9-900": variant === "default" || !variant,
          "bg-ds9-background": variant === "inset",
          "h-80 gap-8": size6 === "m" || !size6,
          "gap-6": size6 === "s",
          "h-80 gap-3": size6 === "xs"
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsxs4("div", { className: "flex flex-col items-center justify-start gap-2", children: [
          title ? /* @__PURE__ */ jsx11("div", { className: "text-sm text-ds9-800 dark:text-ds9-foreground font-medium", children: title }) : null,
          hasChildren ? /* @__PURE__ */ jsx11("div", { className: "text-sm text-ds9-600 dark:text-ds9-300 text-center", children }) : null
        ] }),
        actions ? /* @__PURE__ */ jsx11("div", { className: "flex items-center justify-center", children: actions }) : null
      ]
    }
  );
}, "EmptyState");
var empty_state_default = EmptyState;

// src/components/fieldset.tsx
import "react";
import cn9 from "classnames";
import { jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var Body = /* @__PURE__ */ __name(({ title, className, children }) => {
  return /* @__PURE__ */ jsxs5("div", { className: cn9("py-6 px-4 sm:p-6 text-sm leading-ds9", className), children: [
    title ? /* @__PURE__ */ jsx12("h2", { className: "text-xl leading-6 font-semibold capitalize", children: title }) : null,
    children
  ] });
}, "Body");
var Footer = /* @__PURE__ */ __name(({ className, children }) => {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: cn9(
        "flex rounded-b-md px-4 py-3 bg-ds9-50 dark:bg-ds9-900 border-t border-ds9-200 dark:border-ds9-700 sm:px-6",
        className
      ),
      children
    }
  );
}, "Footer");
var Fieldset = /* @__PURE__ */ __name(({ children, className, danger }) => {
  return /* @__PURE__ */ jsx12("section", { children: /* @__PURE__ */ jsx12(
    "div",
    {
      className: cn9("rounded-ds9 border", className, {
        "border-ds9-error": danger,
        "border-ds9-200 dark:border-ds9-700": !danger
      }),
      children
    }
  ) });
}, "Fieldset");
Fieldset.Body = Body;
Fieldset.Footer = Footer;
var fieldset_default = Fieldset;

// src/components/flag.tsx
import "react";
import cn10 from "classnames";

// src/icons/locales/at.tsx
import { jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
var SvgAt = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx13("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs6("g", { fillRule: "evenodd", children: [
  /* @__PURE__ */ jsx13("path", { fill: "#fff", d: "M640 480H0V0h640z" }),
  /* @__PURE__ */ jsx13("path", { fill: "#c8102e", d: "M640 480H0V320h640zm0-319.9H0V.1h640z" })
] }) })), "SvgAt");
var at_default = SvgAt;

// src/icons/locales/au.tsx
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var SvgAu = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs7("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx14("path", { fill: "#00008B", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx14(
    "path",
    {
      fill: "#fff",
      d: "m37.5 0 122 90.5L281 0h39v31l-120 89.5 120 89V240h-40l-120-89.5L40.5 240H0v-30l119.5-89L0 32V0z"
    }
  ),
  /* @__PURE__ */ jsx14(
    "path",
    {
      fill: "red",
      d: "M212 140.5 320 220v20l-135.5-99.5zm-92 10 3 17.5-96 72H0zM320 0v1.5l-124.5 94 1-22L295 0zM0 0l119.5 88h-30L0 21z"
    }
  ),
  /* @__PURE__ */ jsx14("path", { fill: "#fff", d: "M120.5 0v240h80V0h-80ZM0 80v80h320V80H0Z" }),
  /* @__PURE__ */ jsx14("path", { fill: "red", d: "M0 96.5v48h320v-48zM136.5 0v240h48V0z" }),
  /* @__PURE__ */ jsx14(
    "path",
    {
      fill: "#fff",
      d: "m527 396.7-20.5 2.6 2.2 20.5-14.8-14.4-14.7 14.5 2-20.5-20.5-2.4 17.3-11.2-10.9-17.5 19.6 6.5 6.9-19.5 7.1 19.4 19.5-6.7-10.7 17.6 17.4 11.1Zm-3.7-117.2 2.7-13-9.8-9 13.2-1.5 5.5-12.1 5.5 12.1 13.2 1.5-9.8 9 2.7 13-11.6-6.6-11.6 6.6Zm-104.1-60-20.3 2.2 1.8 20.3-14.4-14.5-14.8 14.1 2.4-20.3-20.2-2.7 17.3-10.8-10.5-17.5 19.3 6.8L387 178l6.7 19.3 19.4-6.3-10.9 17.3 17.1 11.2ZM623 186.7l-20.9 2.7 2.3 20.9-15.1-14.7-15 14.8 2.1-21-20.9-2.4 17.7-11.5-11.1-17.9 20 6.7 7-19.8 7.2 19.8 19.9-6.9-11 18 17.8 11.3Zm-96.1-83.5-20.7 2.3 1.9 20.8-14.7-14.8-15.1 14.4 2.4-20.7-20.7-2.8 17.7-11L467 73.5l19.7 6.9 7.3-19.5 6.8 19.7 19.8-6.5-11.1 17.6 17.4 11.5ZM234 385.7l-45.8 5.4 4.6 45.9-32.8-32.4-33 32.2 4.9-45.9-45.8-5.8 38.9-24.8-24-39.4 43.6 15 15.8-43.4 15.5 43.5 43.7-14.7-24.3 39.2 38.8 25.1Z"
    }
  )
] })), "SvgAu");
var au_default = SvgAu;

// src/icons/locales/be.tsx
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var SvgBe = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx15("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs8("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx15("path", { d: "M0 0h213.3v480H0z" }),
  /* @__PURE__ */ jsx15("path", { fill: "#ffd90c", d: "M213.3 0h213.4v480H213.3z" }),
  /* @__PURE__ */ jsx15("path", { fill: "#f31830", d: "M426.7 0H640v480H426.7z" })
] }) })), "SvgBe");
var be_default = SvgBe;

// src/icons/locales/ch.tsx
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var SvgCh = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx16("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs9("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx16("path", { fill: "red", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsxs9("g", { fill: "#fff", children: [
    /* @__PURE__ */ jsx16("path", { d: "M170 195h300v90H170z" }),
    /* @__PURE__ */ jsx16("path", { d: "M275 90h90v300h-90z" })
  ] })
] }) })), "SvgCh");
var ch_default = SvgCh;

// src/icons/locales/cz.tsx
import { jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
var SvgCz = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs10("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx17("path", { fill: "#fff", d: "M0 0h640v240H0z" }),
  /* @__PURE__ */ jsx17("path", { fill: "#d7141a", d: "M0 240h640v240H0z" }),
  /* @__PURE__ */ jsx17("path", { fill: "#11457e", d: "M360 240 0 0v480z" })
] })), "SvgCz");
var cz_default = SvgCz;

// src/icons/locales/de.tsx
import { jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
var SvgDe = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs11("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx18("path", { fill: "#ffce00", d: "M0 320h640v160H0z" }),
  /* @__PURE__ */ jsx18("path", { d: "M0 0h640v160H0z" }),
  /* @__PURE__ */ jsx18("path", { fill: "#d00", d: "M0 160h640v160H0z" })
] })), "SvgDe");
var de_default = SvgDe;

// src/icons/locales/dk.tsx
import { jsx as jsx19, jsxs as jsxs12 } from "react/jsx-runtime";
var SvgDk = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs12("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx19("path", { fill: "#c8102e", d: "M0 0h640.1v480H0z" }),
  /* @__PURE__ */ jsx19("path", { fill: "#fff", d: "M205.7 0h68.6v480h-68.6z" }),
  /* @__PURE__ */ jsx19("path", { fill: "#fff", d: "M0 205.7h640.1v68.6H0z" })
] })), "SvgDk");
var dk_default = SvgDk;

// src/icons/locales/ee.tsx
import { jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
var SvgEe = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx20("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs13("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx20("rect", { width: 640, height: 477.9, rx: 0, ry: 0 }),
  /* @__PURE__ */ jsx20("rect", { width: 640, height: 159.3, y: 320.7, fill: "#fff", rx: 0, ry: 0 }),
  /* @__PURE__ */ jsx20("path", { fill: "#1791ff", d: "M0 0h640v159.3H0z" })
] }) })), "SvgEe");
var ee_default = SvgEe;

// src/icons/locales/es.tsx
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var SvgEs = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs14("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx21("path", { fill: "#AA151B", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#F1BF00", d: "M0 120h640v240H0z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "m127.3 213.3-.8-.1-1-1-.7-.4-.6-.8s-.7-1.1-.4-2c.3-.9.9-1.2 1.4-1.5a12 12 0 0 1 1.5-.5l1-.4 1.3-.3.5-.3c.2 0 .7 0 1-.2l1-.2 1.6.1h4.8c.4 0 1.2.3 1.4.4a35 35 0 0 0 2 .7c.5.1 1.6.3 2.2.6.5.3.9.7 1.1 1l.5 1v1.1l-.5.8-.6 1-.8.6s-.5.5-1 .4c-.4 0-4.8-.8-7.6-.8s-7.3.9-7.3.9"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "m127.3 213.3-.8-.1-1-1-.7-.4-.6-.8s-.7-1.1-.4-2c.3-.9.9-1.2 1.4-1.5a12 12 0 0 1 1.5-.5l1-.4 1.3-.3.5-.3c.2 0 .7 0 1-.2l1-.2 1.6.1h4.8c.4 0 1.2.3 1.4.4a35 35 0 0 0 2 .7c.5.1 1.6.3 2.2.6.5.3.9.7 1.1 1l.5 1v1.1l-.5.8-.6 1-.8.6s-.5.5-1 .4c-.4 0-4.8-.8-7.6-.8s-7.3.9-7.3.9z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M133.3 207c0-1.3.6-2.3 1.3-2.3.8 0 1.4 1 1.4 2.4 0 1.3-.6 2.4-1.4 2.4s-1.3-1.1-1.3-2.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M133.3 207c0-1.3.6-2.3 1.3-2.3.8 0 1.4 1 1.4 2.4 0 1.3-.6 2.4-1.4 2.4s-1.3-1.1-1.3-2.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M134 207c0-1.2.3-2.1.7-2.1.3 0 .6 1 .6 2.1 0 1.3-.3 2.2-.6 2.2-.4 0-.6-1-.6-2.2" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134 207c0-1.2.3-2.1.7-2.1.3 0 .6 1 .6 2.1 0 1.3-.3 2.2-.6 2.2-.4 0-.6-1-.6-2.2z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M133.8 204.5c0-.4.4-.8.8-.8s1 .4 1 .8c0 .5-.5.9-1 .9s-.8-.4-.8-.9" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M135.3 204.2v.6h-1.4v-.6h.5V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M135.3 204.2v.6h-1.4v-.6h.5V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h.4"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M135.9 204.2v.6h-2.5v-.6h1V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M135.9 204.2v.6h-2.5v-.6h1V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.9 203.7c.4.1.6.4.6.8 0 .5-.4.9-.8.9s-1-.4-1-.9c0-.4.3-.7.7-.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M134.7 213.2H130v-1.1l-.3-1.2-.2-1.5c-1.3-1.7-2.5-2.8-2.9-2.5.1-.3.2-.6.5-.7 1.1-.7 3.5 1 5.2 3.6l.5.7h3.8l.4-.7c1.8-2.7 4.1-4.3 5.2-3.6.3.1.4.4.5.7-.4-.3-1.6.8-2.9 2.5l-.2 1.5-.2 1.2-.1 1.1h-4.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.7 213.2H130v-1.1l-.3-1.2-.2-1.5c-1.3-1.7-2.5-2.8-2.9-2.5.1-.3.2-.6.5-.7 1.1-.7 3.5 1 5.2 3.6l.5.7h3.8l.4-.7c1.8-2.7 4.1-4.3 5.2-3.6.3.1.4.4.5.7-.4-.3-1.6.8-2.9 2.5l-.2 1.5-.2 1.2-.1 1.1h-4.7z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M126.8 206.8c1-.5 3 1.1 4.6 3.6m11-3.6c-.8-.5-2.8 1.1-4.5 3.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m127.8 215.3-.5-1a27.3 27.3 0 0 1 14.7 0l-.5.8a5.7 5.7 0 0 0-.3.8 22.9 22.9 0 0 0-6.6-.8c-2.6 0-5.2.3-6.5.8l-.3-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m127.8 215.3-.5-1a27.3 27.3 0 0 1 14.7 0l-.5.8a5.7 5.7 0 0 0-.3.8 22.9 22.9 0 0 0-6.6-.8c-2.6 0-5.2.3-6.5.8l-.3-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M134.6 217.7c2.4 0 5-.4 5.9-.6.6-.2 1-.5 1-.8 0-.2-.2-.3-.4-.4-1.4-.5-4-.8-6.5-.8s-5 .3-6.4.8c-.2 0-.3.2-.4.3 0 .4.3.7 1 .9 1 .2 3.5.6 5.8.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.6 217.7c2.4 0 5-.4 5.9-.6.6-.2 1-.5 1-.8 0-.2-.2-.3-.4-.4-1.4-.5-4-.8-6.5-.8s-5 .3-6.4.8c-.2 0-.3.2-.4.3 0 .4.3.7 1 .9 1 .2 3.5.6 5.8.6z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m142.1 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7c-.7 0-1-.6-1-.6s-.7.5-1.3.4c-.6 0-1.2-.8-1.2-.8s-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.4.6-1.1.7-1.4-.6-1.4-.6-.5.7-1 1c-.5 0-1.2-.4-1.2-.4l-.2.5-.3.1.2.5a27 27 0 0 1 7.2-.9c3 0 5.5.4 7.4 1l.2-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m142.1 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7c-.7 0-1-.6-1-.6s-.7.5-1.3.4c-.6 0-1.2-.8-1.2-.8s-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.4.6-1.1.7-1.4-.6-1.4-.6-.5.7-1 1c-.5 0-1.2-.4-1.2-.4l-.2.5-.3.1.2.5a27 27 0 0 1 7.2-.9c3 0 5.5.4 7.4 1l.2-.6z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M134.7 210.7h.2a1 1 0 0 0 0 .4c0 .6.4 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4c.1.5.6.8 1.1.8.6 0 1-.4 1-1v-.1l.4-.4.2.5a.9.9 0 0 0-.1.4 1 1 0 0 0 1 1c.4 0 .7-.2.9-.5l.2-.2v.3c0 .3.1.6.4.7 0 0 .4 0 1-.4l.7-.7v.4s-.5.8-1 1c-.2.2-.5.4-.8.3-.3 0-.6-.3-.7-.6-.2.2-.4.2-.7.2-.6 0-1.2-.3-1.4-.8-.3.3-.7.5-1.1.5a1.6 1.6 0 0 1-1.2-.6 1.6 1.6 0 0 1-1 .4 1.6 1.6 0 0 1-1.3-.6 1.6 1.6 0 0 1-2.4.2 1.6 1.6 0 0 1-1.2.6 1.5 1.5 0 0 1-1.1-.5c-.2.5-.8.8-1.4.8-.2 0-.5 0-.7-.2-.1.3-.4.6-.7.6-.3 0-.6 0-.9-.2l-1-1 .1-.5.8.7c.5.4.9.4.9.4.3 0 .4-.4.4-.7v-.3l.2.2c.2.3.5.5.9.5a1 1 0 0 0 1-1 .9.9 0 0 0 0-.4v-.5l.4.4a.7.7 0 0 0 0 .1c0 .6.5 1 1 1 .6 0 1-.3 1.1-.9v-.3l.2.3c.2.4.6.7 1 .7.7 0 1.1-.4 1.1-1a1 1 0 0 0 0-.3h.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.7 210.7h.2a1 1 0 0 0 0 .4c0 .6.4 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4c.1.5.6.8 1.1.8.6 0 1-.4 1-1v-.1l.4-.4.2.5a.9.9 0 0 0-.1.4 1 1 0 0 0 1 1c.4 0 .7-.2.9-.5l.2-.2v.3c0 .3.1.6.4.7 0 0 .4 0 1-.4l.7-.7v.4s-.5.8-1 1c-.2.2-.5.4-.8.3-.3 0-.6-.3-.7-.6-.2.2-.4.2-.7.2-.6 0-1.2-.3-1.4-.8-.3.3-.7.5-1.1.5a1.6 1.6 0 0 1-1.2-.6 1.6 1.6 0 0 1-1 .4 1.6 1.6 0 0 1-1.3-.6 1.6 1.6 0 0 1-2.4.2 1.6 1.6 0 0 1-1.2.6 1.5 1.5 0 0 1-1.1-.5c-.2.5-.8.8-1.4.8-.2 0-.5 0-.7-.2-.1.3-.4.6-.7.6-.3 0-.6 0-.9-.2l-1-1 .1-.5.8.7c.5.4.9.4.9.4.3 0 .4-.4.4-.7v-.3l.2.2c.2.3.5.5.9.5a1 1 0 0 0 1-1 .9.9 0 0 0 0-.4v-.5l.4.4a.7.7 0 0 0 0 .1c0 .6.5 1 1 1 .6 0 1-.3 1.1-.9v-.3l.2.3c.2.4.6.7 1 .7.7 0 1.1-.4 1.1-1a1 1 0 0 0 0-.3h.3z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M134.6 213.3c-2.9 0-5.5.4-7.3 1l-.3-.2.1-.3a27 27 0 0 1 7.5-1c3 0 5.7.4 7.6 1 0 0 .2.2.1.3l-.3.2a27.3 27.3 0 0 0-7.4-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M134.6 213.3c-2.9 0-5.5.4-7.3 1l-.3-.2.1-.3a27 27 0 0 1 7.5-1c3 0 5.7.4 7.6 1 0 0 .2.2.1.3l-.3.2a27.3 27.3 0 0 0-7.4-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M131.8 214.4c0-.3.2-.4.5-.4a.4.4 0 0 1 .4.4c0 .2-.2.4-.4.4a.4.4 0 0 1-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M131.8 214.4c0-.3.2-.4.5-.4a.4.4 0 0 1 .4.4c0 .2-.2.4-.4.4a.4.4 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M134.7 214.5h-1c-.1 0-.3 0-.3-.3l.3-.3h2a.3.3 0 0 1 .2.3.3.3 0 0 1-.3.3h-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.7 214.5h-1c-.1 0-.3 0-.3-.3l.3-.3h2a.3.3 0 0 1 .2.3.3.3 0 0 1-.3.3h-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      d: "M130 214.9h-.7c-.1 0-.3 0-.3-.2a.3.3 0 0 1 .2-.3l.7-.1.7-.1c.2 0 .3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M130 214.9h-.7c-.1 0-.3 0-.3-.2a.3.3 0 0 1 .2-.3l.7-.1.7-.1c.2 0 .3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "m127.3 215.3.3-.4h.7l-.4.6-.6-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m127.3 215.3.3-.4h.7l-.4.6-.6-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M136.6 214.4c0-.3.2-.4.4-.4a.4.4 0 0 1 .5.4.4.4 0 0 1-.5.4.4.4 0 0 1-.4-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M136.6 214.4c0-.3.2-.4.4-.4a.4.4 0 0 1 .5.4.4.4 0 0 1-.5.4.4.4 0 0 1-.4-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      d: "M139.3 214.9h.6a.3.3 0 0 0 .4-.2.3.3 0 0 0-.3-.3l-.6-.1-.7-.1c-.2 0-.3 0-.4.2 0 .2.1.3.3.4h.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M139.3 214.9h.6a.3.3 0 0 0 .4-.2.3.3 0 0 0-.3-.3l-.6-.1-.7-.1c-.2 0-.3 0-.4.2 0 .2.1.3.3.4h.7"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "m142 215.4-.3-.5h-.7l.3.6.6-.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m142 215.4-.3-.5h-.7l.3.6.6-.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M134.6 217.1a25 25 0 0 1-6-.6 25.5 25.5 0 0 1 12.1 0c-1.6.4-3.7.6-6 .6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M134.6 217.1a25 25 0 0 1-6-.6 25.5 25.5 0 0 1 12.1 0c-1.6.4-3.7.6-6 .6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m142 212-.1-.3c-.2 0-.3 0-.4.2 0 .2 0 .4.2.4 0 0 .2 0 .3-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m142 212-.1-.3c-.2 0-.3 0-.4.2 0 .2 0 .4.2.4 0 0 .2 0 .3-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M137.3 211.2c0-.2 0-.4-.2-.4 0 0-.2.1-.2.3 0 .2 0 .4.2.4l.3-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M137.3 211.2c0-.2 0-.4-.2-.4 0 0-.2.1-.2.3 0 .2 0 .4.2.4l.3-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m132 211.2.1-.4c.2 0 .3.1.3.3 0 .2 0 .4-.2.4l-.2-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m132 211.2.1-.4c.2 0 .3.1.3.3 0 .2 0 .4-.2.4l-.2-.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m127.3 212 .1-.3c.2 0 .3 0 .4.2 0 .2 0 .4-.2.4 0 0-.2 0-.3-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m127.3 212 .1-.3c.2 0 .3 0 .4.2 0 .2 0 .4-.2.4 0 0-.2 0-.3-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m134.6 208.5-.8.5.6 1.3.2.1.2-.1.7-1.3-.9-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m134.6 208.5-.8.5.6 1.3.2.1.2-.1.7-1.3-.9-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m132.8 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m132.8 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m136.4 210.5-.3.5-1.3-.4-.2-.2.2-.2 1.3-.3.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m136.4 210.5-.3.5-1.3-.4-.2-.2.2-.2 1.3-.3.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m129.3 209-.7.7.9 1 .2.1.1-.1.3-1.3-.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m129.3 209-.7.7.9 1 .2.1.1-.1.3-1.3-.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m128 211.2.4.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m128 211.2.4.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m131.5 210.5-.3.6H130l-.2-.2.1-.3 1.2-.6.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m131.5 210.5-.3.6H130l-.2-.2.1-.3 1.2-.6.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M126.6 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M126.6 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M129.2 210.9c0-.3.2-.5.5-.5s.5.2.5.5a.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M129.2 210.9c0-.3.2-.5.5-.5s.5.2.5.5a.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m140 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m140 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m141.4 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m141.4 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m137.8 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m137.8 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m142.5 211.4.1.6 1.3.2.2-.1v-.2l-1-.9-.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m142.5 211.4.1.6 1.3.2.2-.1v-.2l-1-.9-.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M134.2 210.4a.5.5 0 0 1 .4-.4c.3 0 .5.2.5.4a.5.5 0 0 1-.5.5.5.5 0 0 1-.4-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M134.2 210.4a.5.5 0 0 1 .4-.4c.3 0 .5.2.5.4a.5.5 0 0 1-.5.5.5.5 0 0 1-.4-.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M139.1 210.9c0-.3.3-.5.5-.5a.5.5 0 0 1 .5.5.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M139.1 210.9c0-.3.3-.5.5-.5a.5.5 0 0 1 .5.5.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m124.8 212.2-.6-.7c-.2-.2-.7-.3-.7-.3 0-.1.3-.3.6-.3a.5.5 0 0 1 .4.2v-.2s.3 0 .4.3v1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m124.8 212.2-.6-.7c-.2-.2-.7-.3-.7-.3 0-.1.3-.3.6-.3a.5.5 0 0 1 .4.2v-.2s.3 0 .4.3v1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M124.8 212c.1-.2.4-.2.5 0 .2.1.3.3.2.5l-.5-.1c-.2-.1-.3-.4-.2-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M124.8 212c.1-.2.4-.2.5 0 .2.1.3.3.2.5l-.5-.1c-.2-.1-.3-.4-.2-.5z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m144.3 212.2.6-.7c.2-.2.7-.3.7-.3 0-.1-.3-.3-.6-.3a.6.6 0 0 0-.4.2v-.2s-.3 0-.4.3v.7l.1.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m144.3 212.2.6-.7c.2-.2.7-.3.7-.3 0-.1-.3-.3-.6-.3a.6.6 0 0 0-.4.2v-.2s-.3 0-.4.3v.7l.1.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M144.3 212c0-.2-.3-.2-.5 0-.2.1-.2.3-.1.5l.5-.1c.2-.1.2-.4.1-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M144.3 212c0-.2-.3-.2-.5 0-.2.1-.2.3-.1.5l.5-.1c.2-.1.2-.4.1-.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M124 223h21.4v-5.5H124v5.6z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M124 223h21.4v-5.5H124v5.6z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M126.2 226.8a1 1 0 0 1 .4 0h16.5a1.4 1.4 0 0 1-1-1.2c0-.6.5-1.1 1-1.3a1.7 1.7 0 0 1-.4 0h-16a1.4 1.4 0 0 1-.5 0c.6.2 1 .7 1 1.3a1.3 1.3 0 0 1-1 1.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M126.2 226.8a1 1 0 0 1 .4 0h16.5a1.4 1.4 0 0 1-1-1.2c0-.6.5-1.1 1-1.3a1.7 1.7 0 0 1-.4 0h-16a1.4 1.4 0 0 1-.5 0c.6.2 1 .7 1 1.3a1.3 1.3 0 0 1-1 1.2z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M126.6 226.8h16c.6 0 1 .3 1 .7 0 .4-.4.8-1 .8h-16c-.5 0-1-.4-1-.8s.5-.8 1-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M126.6 226.8h16c.6 0 1 .3 1 .7 0 .4-.4.8-1 .8h-16c-.5 0-1-.4-1-.8s.5-.8 1-.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M126.6 223h16c.6 0 1 .4 1 .7 0 .4-.4.6-1 .6h-16c-.5 0-1-.2-1-.6 0-.3.5-.6 1-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M126.6 223h16c.6 0 1 .4 1 .7 0 .4-.4.6-1 .6h-16c-.5 0-1-.2-1-.6 0-.3.5-.6 1-.6z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M149.6 317.4c-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8c-1.4 0-2.7.3-3.7.8a8.3 8.3 0 0 1-3.8.8c-1.5 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.7-.8 8 8 0 0 0-3.7.8 8.3 8.3 0 0 1-3.8.8v2.4c1.5 0 2.8-.4 3.8-.9a8.2 8.2 0 0 1 3.7-.8c1.4 0 2.7.3 3.7.8s2.2.9 3.7.9a8.4 8.4 0 0 0 3.8-.9c1-.5 2.3-.8 3.7-.8 1.5 0 2.8.3 3.8.8s2.2.9 3.7.9v-2.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M149.6 317.4c-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8c-1.4 0-2.7.3-3.7.8a8.3 8.3 0 0 1-3.8.8c-1.5 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.7-.8 8 8 0 0 0-3.7.8 8.3 8.3 0 0 1-3.8.8v2.4c1.5 0 2.8-.4 3.8-.9a8.2 8.2 0 0 1 3.7-.8c1.4 0 2.7.3 3.7.8s2.2.9 3.7.9a8.4 8.4 0 0 0 3.8-.9c1-.5 2.3-.8 3.7-.8 1.5 0 2.8.3 3.8.8s2.2.9 3.7.9v-2.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ccc",
      d: "M149.6 319.8a8 8 0 0 1-3.7-.9 8.3 8.3 0 0 0-3.8-.8c-1.4 0-2.7.3-3.7.8s-2.3.9-3.8.9-2.8-.4-3.7-.9a8.4 8.4 0 0 0-3.7-.8 8.2 8.2 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v2.3c1.5 0 2.8-.4 3.8-.9a8.1 8.1 0 0 1 3.7-.7c1.4 0 2.7.2 3.7.7a8.3 8.3 0 0 0 7.5 0 8.5 8.5 0 0 1 7.5.1 8.1 8.1 0 0 0 3.7.8v-2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M149.6 319.8a8 8 0 0 1-3.7-.9 8.3 8.3 0 0 0-3.8-.8c-1.4 0-2.7.3-3.7.8s-2.3.9-3.8.9-2.8-.4-3.7-.9a8.4 8.4 0 0 0-3.7-.8 8.2 8.2 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v2.3c1.5 0 2.8-.4 3.8-.9a8.1 8.1 0 0 1 3.7-.7c1.4 0 2.7.2 3.7.7a8.3 8.3 0 0 0 7.5 0 8.5 8.5 0 0 1 7.5.1 8.1 8.1 0 0 0 3.7.8v-2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M149.6 322a7 7 0 0 1-3.7-.8 8.3 8.3 0 0 0-3.8-.7c-1.4 0-2.7.2-3.7.7-1 .6-2.3.9-3.8.9s-2.8-.4-3.7-.9a8.4 8.4 0 0 0-3.7-.8 8 8 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v2.3c1.5 0 2.8-.3 3.8-.9a10.2 10.2 0 0 1 7.4 0 7 7 0 0 0 3.7.9 8.4 8.4 0 0 0 3.8-.8c1-.5 2.3-.8 3.7-.8 1.5 0 2.8.3 3.8.8s2.2.8 3.7.8V322"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M149.6 322a7 7 0 0 1-3.7-.8 8.3 8.3 0 0 0-3.8-.7c-1.4 0-2.7.2-3.7.7-1 .6-2.3.9-3.8.9s-2.8-.4-3.7-.9a8.4 8.4 0 0 0-3.7-.8 8 8 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v2.3c1.5 0 2.8-.3 3.8-.9a10.2 10.2 0 0 1 7.4 0 7 7 0 0 0 3.7.9 8.4 8.4 0 0 0 3.8-.8c1-.5 2.3-.8 3.7-.8 1.5 0 2.8.3 3.8.8s2.2.8 3.7.8V322"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ccc",
      d: "M149.6 326.7a8 8 0 0 1-3.7-.8c-1-.5-2.3-.8-3.7-.8a8.4 8.4 0 0 0-3.8.8c-1 .5-2.3.8-3.8.8a7 7 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.7-.7c-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v-2.3a8.3 8.3 0 0 0 3.8-.9 10.2 10.2 0 0 1 7.4 0 8 8 0 0 0 3.7.9 8.4 8.4 0 0 0 3.8-.8c1-.5 2.3-.8 3.8-.8 1.4 0 2.7.3 3.7.8s2.3.8 3.7.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M149.6 326.7a8 8 0 0 1-3.7-.8c-1-.5-2.3-.8-3.7-.8a8.4 8.4 0 0 0-3.8.8c-1 .5-2.3.8-3.8.8a7 7 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.7-.7c-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v-2.3a8.3 8.3 0 0 0 3.8-.9 10.2 10.2 0 0 1 7.4 0 8 8 0 0 0 3.7.9 8.4 8.4 0 0 0 3.8-.8c1-.5 2.3-.8 3.8-.8 1.4 0 2.7.3 3.7.8s2.3.8 3.7.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M149.6 329a8.1 8.1 0 0 1-3.7-.8c-1-.5-2.3-.8-3.7-.8a8.4 8.4 0 0 0-3.8.8c-1 .5-2.3.8-3.8.8a7 7 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.7-.7c-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v-2.3a8.3 8.3 0 0 0 3.8-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.7.3 3.7.7a8.4 8.4 0 0 0 7.5 0c1-.4 2.3-.7 3.8-.7 1.4 0 2.7.3 3.7.8s2.2.8 3.7.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M149.6 329a8.1 8.1 0 0 1-3.7-.8c-1-.5-2.3-.8-3.7-.8a8.4 8.4 0 0 0-3.8.8c-1 .5-2.3.8-3.8.8a7 7 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.7-.7c-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v-2.3a8.3 8.3 0 0 0 3.8-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.7.3 3.7.7a8.4 8.4 0 0 0 7.5 0c1-.4 2.3-.7 3.8-.7 1.4 0 2.7.3 3.7.8s2.2.8 3.7.8v2.3z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m126.2 308 .2.5c0 1.5-1.3 2.6-2.7 2.6h22a2.7 2.7 0 0 1-2.7-2.6v-.5a1.3 1.3 0 0 1-.3 0h-16a1.4 1.4 0 0 1-.5 0"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "m126.2 308 .2.5c0 1.5-1.3 2.6-2.7 2.6h22a2.7 2.7 0 0 1-2.7-2.6v-.5a1.3 1.3 0 0 1-.3 0h-16a1.4 1.4 0 0 1-.5 0z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M126.6 306.5h16c.6 0 1 .3 1 .8 0 .4-.4.7-1 .7h-16c-.5 0-1-.3-1-.8 0-.4.5-.7 1-.7" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M126.6 306.5h16c.6 0 1 .3 1 .8 0 .4-.4.7-1 .7h-16c-.5 0-1-.3-1-.8 0-.4.5-.7 1-.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M123.7 316.7h22V311h-22v5.6z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M123.7 316.7h22V311h-22v5.6z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M122 286.7c-2.2 1.2-3.7 2.5-3.4 3.2 0 .6.8 1 1.8 1.6 1.5 1.1 2.5 3 1.7 4a5.5 5.5 0 0 0-.1-8.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M122 286.7c-2.2 1.2-3.7 2.5-3.4 3.2 0 .6.8 1 1.8 1.6 1.5 1.1 2.5 3 1.7 4a5.5 5.5 0 0 0-.1-8.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ccc", d: "M126.8 305.6h15.6V229h-15.6v76.5z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M138 229.2v76.3m1.7-76.3v76.3m-12.9 0h15.6v-76.4h-15.6v76.5z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M158.4 257.7a49.6 49.6 0 0 0-23.3-2c-9.4 1.6-16.5 5.3-15.9 8.4v.2l-3.5-8.2c-.6-3.3 7.2-7.5 17.6-9.2a43 43 0 0 1 9.2-.7c6.6 0 12.4.8 15.8 2.1v9.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M158.4 257.7a49.6 49.6 0 0 0-23.3-2c-9.4 1.6-16.5 5.3-15.9 8.4v.2l-3.5-8.2c-.6-3.3 7.2-7.5 17.6-9.2a43 43 0 0 1 9.2-.7c6.6 0 12.4.8 15.8 2.1v9.4"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M126.8 267.3c-4.3-.3-7.3-1.4-7.6-3.2-.3-1.5 1.2-3 3.8-4.5 1.2.1 2.5.3 3.8.3v7.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M126.8 267.3c-4.3-.3-7.3-1.4-7.6-3.2-.3-1.5 1.2-3 3.8-4.5 1.2.1 2.5.3 3.8.3v7.4"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M142.5 261.5c2.7.4 4.7 1 5.7 1.9l.1.2c.5 1-1.9 3-5.9 5.4v-7.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M142.5 261.5c2.7.4 4.7 1 5.7 1.9l.1.2c.5 1-1.9 3-5.9 5.4v-7.5"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M117.1 282c-.4-1.2 3.8-3.6 9.8-5.8l7.8-3.2c8.3-3.7 14.4-7.9 13.6-9.4v-.2c.4.4 1 8 1 8 .8 1.3-4.8 5.5-12.4 9.1-2.5 1.2-7.6 3-10 4-4.4 1.4-8.7 4.3-8.3 5.3l-1.5-7.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M117.1 282c-.4-1.2 3.8-3.6 9.8-5.8l7.8-3.2c8.3-3.7 14.4-7.9 13.6-9.4v-.2c.4.4 1 8 1 8 .8 1.3-4.8 5.5-12.4 9.1-2.5 1.2-7.6 3-10 4-4.4 1.4-8.7 4.3-8.3 5.3l-1.5-7.7z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M125.8 254c1.9-.6 3.1-1.5 2.5-3-.4-1-1.4-1-2.8-.6l-2.6 1 2.3 5.8.8-.3.8-.3-1-2.5zm-1.2-2.7.7-.3c.5-.2 1.2.1 1.4.8.2.5.2 1-.5 1.5a4.4 4.4 0 0 1-.6.3l-1-2.3m7.3-2.5-.9.3h-.8l1.3 6.1 4.3-.8-.2-.4v-.4l-2.5.6-1.2-5.3m8.4 5.2c.8-2.2 1.7-4.3 2.7-6.4a5.3 5.3 0 0 1-1 0 54.8 54.8 0 0 1-1.8 4.6l-2.4-4.3-1 .1h-1a131.4 131.4 0 0 1 3.5 6h1m8.8-4.7.4-.9a3.4 3.4 0 0 0-1.7-.6c-1.7-.1-2.7.6-2.8 1.7-.2 2.1 3.2 2 3 3.4 0 .6-.7.9-1.4.8-.8 0-1.4-.5-1.4-1.2h-.3a7.3 7.3 0 0 1-.4 1.1 4 4 0 0 0 1.8.6c1.7.2 3-.5 3.2-1.7.2-2-3.3-2.1-3.1-3.4 0-.5.4-.8 1.3-.7.7 0 1 .4 1.2.9h.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M277.9 211.6s-.7.8-1.3.9c-.5 0-1.1-.5-1.1-.5s-.5.5-1 .6c-.6.1-1.4-.6-1.4-.6l-1 1c-.6 0-1.1-.3-1.1-.3s-.3.4-.7.6h-.4l-.6-.4-.7-.7-.5-.3-.4-1v-.5c-.1-.6.8-1.4 2.2-1.7a3.9 3.9 0 0 1 2 0c.5-.5 1.7-.8 3-.8s2.4.3 3 .7a5.5 5.5 0 0 1 2.9-.7c1.3 0 2.5.3 3 .8.5-.2 1.2-.2 2 0 1.4.3 2.3 1 2.2 1.7v.5l-.4 1-.6.3-.6.7-.6.3s-.3.2-.4 0c-.4-.1-.7-.5-.7-.5s-.6.4-1 .2c-.5-.2-1-1-1-1s-.9.8-1.4.7c-.6-.1-1-.6-1-.6s-.7.6-1.2.5c-.5-.1-1.2-.9-1.2-.9"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.9 211.6s-.7.8-1.3.9c-.5 0-1.1-.5-1.1-.5s-.5.5-1 .6c-.6.1-1.4-.6-1.4-.6l-1 1c-.6 0-1.1-.3-1.1-.3s-.3.4-.7.6h-.4l-.6-.4-.7-.7-.5-.3-.4-1v-.5c-.1-.6.8-1.4 2.2-1.7a3.9 3.9 0 0 1 2 0c.5-.5 1.7-.8 3-.8s2.4.3 3 .7a5.5 5.5 0 0 1 2.9-.7c1.3 0 2.5.3 3 .8.5-.2 1.2-.2 2 0 1.4.3 2.3 1 2.2 1.7v.5l-.4 1-.6.3-.6.7-.6.3s-.3.2-.4 0c-.4-.1-.7-.5-.7-.5s-.6.4-1 .2c-.5-.2-1-1-1-1s-.9.8-1.4.7c-.6-.1-1-.6-1-.6s-.7.6-1.2.5c-.5-.1-1.2-.9-1.2-.9z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M276.5 207.6c0-1 .6-2 1.3-2 .8 0 1.3 1 1.3 2s-.5 1.8-1.3 1.8c-.7 0-1.3-.8-1.3-1.9" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M276.5 207.6c0-1 .6-2 1.3-2 .8 0 1.3 1 1.3 2s-.5 1.8-1.3 1.8c-.7 0-1.3-.8-1.3-1.9z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M277.3 207.6c0-1 .2-1.8.5-1.8.4 0 .7.8.7 1.8s-.3 1.7-.6 1.7c-.4 0-.6-.8-.6-1.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.3 207.6c0-1 .2-1.8.5-1.8.4 0 .7.8.7 1.8s-.3 1.7-.6 1.7c-.4 0-.6-.8-.6-1.8z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M271 215.3a4.5 4.5 0 0 0-.5-1 27.4 27.4 0 0 1 14.8 0l-.6.8a5.2 5.2 0 0 0-.3.8 22.9 22.9 0 0 0-6.6-.8c-2.6 0-5.2.3-6.6.8l-.2-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M271 215.3a4.5 4.5 0 0 0-.5-1 27.4 27.4 0 0 1 14.8 0l-.6.8a5.2 5.2 0 0 0-.3.8 22.9 22.9 0 0 0-6.6-.8c-2.6 0-5.2.3-6.6.8l-.2-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M277.8 217.7c2.4 0 5-.4 5.9-.6.6-.2 1-.5 1-.8 0-.2-.2-.3-.4-.4a24.1 24.1 0 0 0-6.5-.8c-2.5 0-5 .3-6.4.8-.2 0-.3.2-.4.3 0 .4.3.7 1 .9 1 .2 3.5.6 5.8.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.8 217.7c2.4 0 5-.4 5.9-.6.6-.2 1-.5 1-.8 0-.2-.2-.3-.4-.4a24.1 24.1 0 0 0-6.5-.8c-2.5 0-5 .3-6.4.8-.2 0-.3.2-.4.3 0 .4.3.7 1 .9 1 .2 3.5.6 5.8.6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M283.5 208.4c0-.2.2-.4.4-.4s.5.2.5.4-.2.4-.5.4a.4.4 0 0 1-.4-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.2,
      d: "M283.5 208.4c0-.2.2-.4.4-.4s.5.2.5.4-.2.4-.5.4a.4.4 0 0 1-.4-.4zm-.2-1.4a.4.4 0 0 1 .4-.4c.2 0 .4.1.4.4s-.2.4-.4.4a.4.4 0 0 1-.4-.4zm-1.1-1c0-.2.2-.3.4-.3s.4.1.4.4c0 .2-.2.4-.4.4a.4.4 0 0 1-.4-.5zm-1.4-.4c0-.2.2-.4.4-.4.3 0 .5.2.5.4s-.2.4-.4.4-.5-.2-.5-.4zm-1.4 0c0-.2.2-.3.5-.3s.4.1.4.4c0 .2-.2.4-.4.4a.4.4 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinecap: "round",
      strokeWidth: 0.3,
      d: "m287.8 211.2.2-1a2.7 2.7 0 0 0-2.7-2.8c-.5 0-1 .1-1.3.3"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m283 209.2.2-.8c0-1.1-1.1-2-2.5-2-.6 0-1.2.2-1.6.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.2,
      d: "M288.2 210c0-.3.2-.5.4-.5s.4.2.4.4c0 .3-.2.4-.4.4s-.4-.1-.4-.4zm-.2-1.6c0-.2.2-.4.4-.4a.4.4 0 0 1 .5.4c0 .2-.2.4-.4.4-.3 0-.5-.2-.5-.4zm-1-1.1a.4.4 0 0 1 .5-.4c.2 0 .4.1.4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.5-.4zm-1.3-.7c0-.2.2-.4.5-.4s.4.2.4.4c0 .3-.2.5-.4.5a.4.4 0 0 1-.5-.5zm-1.4.1c0-.2.2-.4.5-.4s.4.2.4.4-.2.4-.4.4-.5-.2-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m285.3 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7c-.7 0-1-.6-1-.6s-.7.5-1.3.4c-.6 0-1.2-.8-1.2-.8s-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.3.6-1.1.7-1.4-.6-1.4-.6-.4.7-1 1c-.5 0-1.2-.4-1.2-.4l-.1.5-.3.1.1.5a27 27 0 0 1 7.3-.9c2.8 0 5.4.4 7.3 1l.2-.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m285.3 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7c-.7 0-1-.6-1-.6s-.7.5-1.3.4c-.6 0-1.2-.8-1.2-.8s-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.3.6-1.1.7-1.4-.6-1.4-.6-.4.7-1 1c-.5 0-1.2-.4-1.2-.4l-.1.5-.3.1.1.5a27 27 0 0 1 7.3-.9c2.8 0 5.4.4 7.3 1l.2-.6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M271.3 208.4c0-.2.2-.4.4-.4s.4.2.4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.4-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.2,
      d: "M271.3 208.4c0-.2.2-.4.4-.4s.4.2.4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.4-.4zm.2-1.4c0-.3.2-.4.4-.4s.5.1.5.4-.2.4-.5.4a.4.4 0 0 1-.4-.4zm1-1c0-.2.3-.3.5-.3s.5.1.5.4c0 .2-.2.4-.5.4a.4.4 0 0 1-.4-.5zm1.4-.4c0-.2.2-.4.5-.4s.4.2.4.4-.2.4-.4.4-.5-.2-.5-.4zm1.4 0c0-.2.2-.3.5-.3.2 0 .4.1.4.4 0 .2-.2.4-.4.4a.4.4 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinecap: "round",
      strokeWidth: 0.3,
      d: "M267.8 211.2a2.8 2.8 0 0 1-.2-1 2.7 2.7 0 0 1 2.7-2.8c.5 0 1 .1 1.4.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M272.7 209.2a1.7 1.7 0 0 1-.3-.8c0-1 1.2-2 2.6-2a3 3 0 0 1 1.5.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.2,
      d: "M266.6 210c0-.3.2-.5.4-.5.3 0 .4.2.4.4a.4.4 0 0 1-.4.4c-.2 0-.4-.1-.4-.4zm.1-1.6c0-.2.3-.4.5-.4s.4.2.4.4-.2.4-.4.4-.4-.2-.4-.4zm1-1.1c0-.3.2-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4.4.4 0 0 1-.5-.4zm1.3-.7c0-.2.2-.4.5-.4.2 0 .4.2.4.4 0 .3-.2.5-.4.5a.4.4 0 0 1-.5-.5zm1.4.1c0-.2.2-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4c-.3 0-.5-.2-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M277.9 210.7h.2a1 1 0 0 0 0 .4c0 .6.5 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4c.1.5.6.8 1.1.8.6 0 1-.4 1-1a.7.7 0 0 0 0-.1l.4-.4.2.5a1 1 0 0 0-.1.4 1 1 0 0 0 1 1c.4 0 .7-.2.9-.5l.2-.2v.3c0 .3.1.6.4.7 0 0 .4 0 1-.4s.7-.7.7-.7v.4s-.5.8-1 1c-.2.2-.5.4-.8.3-.3 0-.6-.3-.7-.6a1.5 1.5 0 0 1-.7.2c-.6 0-1.2-.3-1.4-.8a1.5 1.5 0 0 1-1.1.5c-.5 0-1-.2-1.2-.6a1.5 1.5 0 0 1-1 .4c-.6 0-1-.2-1.4-.6-.2.4-.7.6-1.2.6-.4 0-.8-.1-1-.4a1.6 1.6 0 0 1-1.3.6c-.4 0-.8-.2-1.1-.5-.2.5-.8.8-1.4.8-.2 0-.5 0-.7-.2-.1.3-.4.6-.7.6-.3 0-.6 0-.9-.2a4.2 4.2 0 0 1-1-1l.1-.5.8.7c.5.4.9.4.9.4.3 0 .4-.4.4-.7v-.3l.2.2c.2.3.5.5.9.5a1 1 0 0 0 1-1 1 1 0 0 0 0-.4v-.5l.4.4v.1c0 .6.5 1 1 1 .6 0 1-.3 1.1-.9v-.3l.2.3c.2.4.6.7 1 .7.6 0 1.1-.4 1.1-1a1 1 0 0 0 0-.3h.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.9 210.7h.2a1 1 0 0 0 0 .4c0 .6.5 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4c.1.5.6.8 1.1.8.6 0 1-.4 1-1a.7.7 0 0 0 0-.1l.4-.4.2.5a1 1 0 0 0-.1.4 1 1 0 0 0 1 1c.4 0 .7-.2.9-.5l.2-.2v.3c0 .3.1.6.4.7 0 0 .4 0 1-.4s.7-.7.7-.7v.4s-.5.8-1 1c-.2.2-.5.4-.8.3-.3 0-.6-.3-.7-.6a1.5 1.5 0 0 1-.7.2c-.6 0-1.2-.3-1.4-.8a1.5 1.5 0 0 1-1.1.5c-.5 0-1-.2-1.2-.6a1.5 1.5 0 0 1-1 .4c-.6 0-1-.2-1.4-.6-.2.4-.7.6-1.2.6-.4 0-.8-.1-1-.4a1.6 1.6 0 0 1-1.3.6c-.4 0-.8-.2-1.1-.5-.2.5-.8.8-1.4.8-.2 0-.5 0-.7-.2-.1.3-.4.6-.7.6-.3 0-.6 0-.9-.2a4.2 4.2 0 0 1-1-1l.1-.5.8.7c.5.4.9.4.9.4.3 0 .4-.4.4-.7v-.3l.2.2c.2.3.5.5.9.5a1 1 0 0 0 1-1 1 1 0 0 0 0-.4v-.5l.4.4v.1c0 .6.5 1 1 1 .6 0 1-.3 1.1-.9v-.3l.2.3c.2.4.6.7 1 .7.6 0 1.1-.4 1.1-1a1 1 0 0 0 0-.3h.2z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M277.8 213.3c-2.9 0-5.5.4-7.3 1l-.3-.2.1-.3c2-.6 4.6-1 7.5-1 3 0 5.7.4 7.6 1 0 0 .2.2.1.3l-.3.2a27 27 0 0 0-7.4-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.8 213.3c-2.9 0-5.5.4-7.3 1l-.3-.2.1-.3c2-.6 4.6-1 7.5-1 3 0 5.7.4 7.6 1 0 0 .2.2.1.3l-.3.2a27 27 0 0 0-7.4-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M275 214.4c0-.3.2-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4c-.3 0-.5-.2-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M275 214.4c0-.3.2-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4c-.3 0-.5-.2-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M277.9 214.5h-1c-.1 0-.3 0-.3-.3l.3-.3h2a.3.3 0 0 1 .2.3.3.3 0 0 1-.3.3h-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.9 214.5h-1c-.1 0-.3 0-.3-.3l.3-.3h2a.3.3 0 0 1 .2.3.3.3 0 0 1-.3.3h-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      d: "M273.2 214.9h-.6a.3.3 0 0 1-.4-.2.3.3 0 0 1 .3-.3l.6-.1.7-.1c.2 0 .3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M273.2 214.9h-.6a.3.3 0 0 1-.4-.2.3.3 0 0 1 .3-.3l.6-.1.7-.1c.2 0 .3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "m270.5 215.3.3-.4h.7l-.4.6-.6-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m270.5 215.3.3-.4h.7l-.4.6-.6-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M279.8 214.4c0-.3.2-.4.4-.4.3 0 .5.1.5.4 0 .2-.2.4-.5.4a.4.4 0 0 1-.4-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M279.8 214.4c0-.3.2-.4.4-.4.3 0 .5.1.5.4 0 .2-.2.4-.5.4a.4.4 0 0 1-.4-.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      d: "M282.5 214.9h.7a.3.3 0 0 0 .3-.2.3.3 0 0 0-.2-.3l-.7-.1-.7-.1c-.2 0-.3 0-.4.2 0 .2.1.3.3.4h.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M282.5 214.9h.7a.3.3 0 0 0 .3-.2.3.3 0 0 0-.2-.3l-.7-.1-.7-.1c-.2 0-.3 0-.4.2 0 .2.1.3.3.4h.7"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "m285.1 215.4-.2-.5h-.7l.3.6.6-.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m285.1 215.4-.2-.5h-.7l.3.6.6-.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M277.8 217.1a25 25 0 0 1-6-.6 25.4 25.4 0 0 1 6-.7c2.4 0 4.5.3 6.1.7-1.6.4-3.7.6-6 .6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M277.8 217.1a25 25 0 0 1-6-.6 25.4 25.4 0 0 1 6-.7c2.4 0 4.5.3 6.1.7-1.6.4-3.7.6-6 .6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m285.2 212-.1-.3c-.2 0-.3 0-.4.2l.1.4c.2 0 .3 0 .4-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m285.2 212-.1-.3c-.2 0-.3 0-.4.2l.1.4c.2 0 .3 0 .4-.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M280.6 211.2c0-.2-.1-.4-.3-.4 0 0-.2.1-.2.3 0 .2 0 .4.2.4l.3-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M280.6 211.2c0-.2-.1-.4-.3-.4 0 0-.2.1-.2.3 0 .2 0 .4.2.4l.3-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M275.2 211.2c0-.2 0-.4.2-.4l.3.3-.2.4c-.2 0-.3-.2-.3-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M275.2 211.2c0-.2 0-.4.2-.4l.3.3-.2.4c-.2 0-.3-.2-.3-.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m270.5 212 .1-.3c.2 0 .3 0 .4.2l-.1.4c-.2 0-.3 0-.4-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m270.5 212 .1-.3c.2 0 .3 0 .4.2l-.1.4c-.2 0-.3 0-.4-.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m277.8 208.5-.8.5.6 1.3.2.1.3-.1.6-1.3-.9-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m277.8 208.5-.8.5.6 1.3.2.1.3-.1.6-1.3-.9-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m276 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m276 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m279.6 210.5-.3.5-1.3-.4-.1-.2v-.2l1.4-.3.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m279.6 210.5-.3.5-1.3-.4-.1-.2v-.2l1.4-.3.4.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m272.5 209-.7.7.9 1 .2.1.2-.1.2-1.3-.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m272.5 209-.7.7.9 1 .2.1.2-.1.2-1.3-.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m271.1 211.2.5.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m271.1 211.2.5.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m274.7 210.5-.3.6h-1.3l-.2-.2.1-.3 1.2-.6.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m274.7 210.5-.3.6h-1.3l-.2-.2.1-.3 1.2-.6.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M269.8 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M269.8 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M272.4 210.9c0-.3.2-.5.5-.5a.5.5 0 0 1 .5.5.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M272.4 210.9c0-.3.2-.5.5-.5a.5.5 0 0 1 .5.5.5.5 0 0 1-.5.4.5.5 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m283.2 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m283.2 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m284.6 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m284.6 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m281 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m281 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M285.7 211.4v.6l1.4.2.2-.1v-.2l-1-.9-.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M285.7 211.4v.6l1.4.2.2-.1v-.2l-1-.9-.6.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M277.4 210.4c0-.2.2-.4.5-.4.2 0 .4.2.4.4 0 .3-.2.5-.4.5a.5.5 0 0 1-.5-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M277.4 210.4c0-.2.2-.4.5-.4.2 0 .4.2.4.4 0 .3-.2.5-.4.5a.5.5 0 0 1-.5-.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M282.3 210.9c0-.3.3-.5.5-.5.3 0 .5.2.5.5s-.2.4-.5.4a.5.5 0 0 1-.5-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M282.3 210.9c0-.3.3-.5.5-.5.3 0 .5.2.5.5s-.2.4-.5.4a.5.5 0 0 1-.5-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M277 205.4c0-.5.4-.8.8-.8s1 .3 1 .8-.5.8-1 .8a.9.9 0 0 1-.8-.8" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M278.5 205.1v.6H277v-.6h.4v-1.3h-.5v-.5h.5v-.6h.6v.6h.6v.6h-.6v1.2h.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M278.5 205.1v.6H277v-.6h.4v-1.3h-.5v-.5h.5v-.6h.6v.6h.6v.6h-.6v1.2h.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M279 205.1v.6h-2.4v-.6h1v-1.3h-.7v-.5h.6v-.6h.6v.6h.6v.6h-.6v1.2h1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M278.1 204.6c.4 0 .6.4.6.8 0 .5-.4.8-.9.8a.9.9 0 0 1-.8-.8c0-.4.2-.7.6-.8"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m268 212.2-.6-.7a2.3 2.3 0 0 0-.7-.3c0-.1.3-.3.6-.3.2 0 .3 0 .4.2v-.2s.3 0 .4.3v1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m268 212.2-.6-.7a2.3 2.3 0 0 0-.7-.3c0-.1.3-.3.6-.3.2 0 .3 0 .4.2v-.2s.3 0 .4.3v1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M268 212c.1-.2.4-.2.5 0 .2.1.3.3.1.5l-.5-.1c-.1-.1-.2-.4 0-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M268 212c.1-.2.4-.2.5 0 .2.1.3.3.1.5l-.5-.1c-.1-.1-.2-.4 0-.5z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m287.5 212.2.6-.7c.2-.2.7-.3.7-.3 0-.1-.3-.3-.6-.3a.6.6 0 0 0-.4.2v-.2s-.3 0-.4.3v.7l.1.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "m287.5 212.2.6-.7c.2-.2.7-.3.7-.3 0-.1-.3-.3-.6-.3a.6.6 0 0 0-.4.2v-.2s-.3 0-.4.3v.7l.1.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M287.5 212c-.1-.2-.3-.2-.5 0-.2.1-.2.3-.1.5l.5-.1c.2-.1.2-.4.1-.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M287.5 212c-.1-.2-.3-.2-.5 0-.2.1-.2.3-.1.5l.5-.1c.2-.1.2-.4.1-.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M267.2 223h21.4v-5.5h-21.4v5.6z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M267.2 223h21.4v-5.5h-21.4v5.6z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M286.3 226.8a1 1 0 0 0-.4 0h-16.5c.6-.2 1-.7 1-1.2 0-.6-.4-1.1-1-1.3h17-.1c-.6.2-1 .7-1 1.3 0 .5.4 1 1 1.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M286.3 226.8a1 1 0 0 0-.4 0h-16.5c.6-.2 1-.7 1-1.2 0-.6-.4-1.1-1-1.3h17-.1c-.6.2-1 .7-1 1.3 0 .5.4 1 1 1.2z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M269.9 226.8h16c.6 0 1 .3 1 .7 0 .4-.4.8-1 .8h-16c-.6 0-1-.4-1-.8s.5-.8 1-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M269.9 226.8h16c.6 0 1 .3 1 .7 0 .4-.4.8-1 .8h-16c-.6 0-1-.4-1-.8s.5-.8 1-.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M269.9 223h16c.6 0 1 .4 1 .7 0 .4-.4.6-1 .6h-16c-.6 0-1-.2-1-.6 0-.3.4-.6 1-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M269.9 223h16c.6 0 1 .4 1 .7 0 .4-.4.6-1 .6h-16c-.6 0-1-.2-1-.6 0-.3.4-.6 1-.6z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M263 317.4c1.4 0 2.7-.3 3.7-.8a8.4 8.4 0 0 1 3.7-.8c1.4 0 2.8.3 3.8.8s2.3.8 3.7.8c1.5 0 2.8-.3 3.8-.8a8.4 8.4 0 0 1 3.6-.8 8 8 0 0 1 3.7.8c1 .5 2.4.8 3.8.8v2.4a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.8c-1.4 0-2.7.3-3.6.8-1 .5-2.3.9-3.8.9a8 8 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v-2.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M263 317.4c1.4 0 2.7-.3 3.7-.8a8.4 8.4 0 0 1 3.7-.8c1.4 0 2.8.3 3.8.8s2.3.8 3.7.8c1.5 0 2.8-.3 3.8-.8a8.4 8.4 0 0 1 3.6-.8 8 8 0 0 1 3.7.8c1 .5 2.4.8 3.8.8v2.4a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.8c-1.4 0-2.7.3-3.6.8-1 .5-2.3.9-3.8.9a8 8 0 0 1-3.7-.9 8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.9-3.8.9v-2.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ccc",
      d: "M263 319.8c1.4 0 2.7-.4 3.7-.9s2.3-.8 3.7-.8c1.4 0 2.8.3 3.8.8s2.3.9 3.7.9a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.8c1.5 0 2.8.3 3.7.8 1 .5 2.4.9 3.8.9v2.3a8.3 8.3 0 0 1-3.8-.9 8.1 8.1 0 0 0-3.7-.7c-1.4 0-2.7.2-3.6.7-1 .5-2.3.9-3.8.9a7 7 0 0 1-3.7-.9c-1-.4-2.3-.7-3.8-.7a8.3 8.3 0 0 0-3.7.7 8.1 8.1 0 0 1-3.8.9v-2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M263 319.8c1.4 0 2.7-.4 3.7-.9s2.3-.8 3.7-.8c1.4 0 2.8.3 3.8.8s2.3.9 3.7.9a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.8c1.5 0 2.8.3 3.7.8 1 .5 2.4.9 3.8.9v2.3a8.3 8.3 0 0 1-3.8-.9 8.1 8.1 0 0 0-3.7-.7c-1.4 0-2.7.2-3.6.7-1 .5-2.3.9-3.8.9a7 7 0 0 1-3.7-.9c-1-.4-2.3-.7-3.8-.7a8.3 8.3 0 0 0-3.7.7 8.1 8.1 0 0 1-3.8.9v-2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M263 322c1.4 0 2.7-.2 3.7-.8 1-.4 2.3-.7 3.7-.7 1.4 0 2.8.2 3.8.7s2.3.9 3.7.9a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.8 8 8 0 0 1 3.7.8c1 .5 2.4.9 3.8.9v2.3a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.7c-1.4 0-2.7.3-3.6.7-1 .6-2.3.9-3.8.9-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.8-3.8.8V322"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M263 322c1.4 0 2.7-.2 3.7-.8 1-.4 2.3-.7 3.7-.7 1.4 0 2.8.2 3.8.7s2.3.9 3.7.9a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.8 8 8 0 0 1 3.7.8c1 .5 2.4.9 3.8.9v2.3a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.7c-1.4 0-2.7.3-3.6.7-1 .6-2.3.9-3.8.9-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.8-3.8.8V322"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ccc",
      d: "M263 326.7a8 8 0 0 0 3.7-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.8.3 3.8.8s2.3.8 3.7.8c1.5 0 2.8-.3 3.8-.9a8.4 8.4 0 0 1 3.6-.7c1.5 0 2.8.3 3.7.8a8.3 8.3 0 0 0 3.8.8v-2.3a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.7c-1.4 0-2.7.3-3.6.7-1 .5-2.3.9-3.8.9-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.8-3.8.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M263 326.7a8 8 0 0 0 3.7-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.8.3 3.8.8s2.3.8 3.7.8c1.5 0 2.8-.3 3.8-.9a8.4 8.4 0 0 1 3.6-.7c1.5 0 2.8.3 3.7.8a8.3 8.3 0 0 0 3.8.8v-2.3a8.3 8.3 0 0 1-3.8-.9 8.2 8.2 0 0 0-3.7-.7c-1.4 0-2.7.3-3.6.7-1 .5-2.3.9-3.8.9-1.4 0-2.8-.3-3.7-.8a8.4 8.4 0 0 0-3.8-.8 8.3 8.3 0 0 0-3.7.8c-1 .5-2.3.8-3.8.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      d: "M263 329a8.1 8.1 0 0 0 3.7-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.8.3 3.8.8s2.3.8 3.7.8a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.7c1.5 0 2.8.3 3.7.8 1 .5 2.4.8 3.8.8v-2.3a8.3 8.3 0 0 1-3.8-.8 8.2 8.2 0 0 0-3.7-.8 8.4 8.4 0 0 0-3.6.7 8.2 8.2 0 0 1-3.8.9c-1.4 0-2.8-.3-3.7-.8-1-.5-2.3-.8-3.8-.8-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v2.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M263 329a8.1 8.1 0 0 0 3.7-.8c1-.5 2.3-.8 3.7-.8 1.4 0 2.8.3 3.8.8s2.3.8 3.7.8a8.2 8.2 0 0 0 3.8-.9 8.4 8.4 0 0 1 3.6-.7c1.5 0 2.8.3 3.7.8 1 .5 2.4.8 3.8.8v-2.3a8.3 8.3 0 0 1-3.8-.8 8.2 8.2 0 0 0-3.7-.8 8.4 8.4 0 0 0-3.6.7 8.2 8.2 0 0 1-3.8.9c-1.4 0-2.8-.3-3.7-.8-1-.5-2.3-.8-3.8-.8-1.4 0-2.7.3-3.7.8s-2.3.8-3.8.8v2.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m286.3 308-.1.5c0 1.5 1.2 2.6 2.7 2.6h-22c1.5 0 2.7-1.2 2.7-2.6l-.1-.5h16.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "m286.3 308-.1.5c0 1.5 1.2 2.6 2.7 2.6h-22c1.5 0 2.7-1.2 2.7-2.6l-.1-.5h16.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M269.9 306.5h16c.6 0 1 .3 1 .8 0 .4-.4.7-1 .7h-16c-.6 0-1-.3-1-.8 0-.4.5-.7 1-.7" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M269.9 306.5h16c.6 0 1 .3 1 .8 0 .4-.4.7-1 .7h-16c-.6 0-1-.3-1-.8 0-.4.5-.7 1-.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M266.9 316.7h22V311h-22v5.6z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M266.9 316.7h22V311h-22v5.6z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M290.6 286.7c2.1 1.2 3.6 2.5 3.4 3.2-.1.6-.8 1-1.8 1.6-1.6 1.1-2.5 3-1.8 4a5.5 5.5 0 0 1 .2-8.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M290.6 286.7c2.1 1.2 3.6 2.5 3.4 3.2-.1.6-.8 1-1.8 1.6-1.6 1.1-2.5 3-1.8 4a5.5 5.5 0 0 1 .2-8.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ccc", d: "M270.1 305.6h15.6V229h-15.6v76.5z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M281.4 229.1v76.3m1.8-76.3v76.3m-13 .2h15.5V229h-15.6v76.5z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M254.2 257.7a49.6 49.6 0 0 1 23.3-2c9.3 1.6 16.4 5.3 15.9 8.4v.2l3.5-8.2c.6-3.3-7.3-7.5-17.6-9.2a53.5 53.5 0 0 0-9.2-.7c-6.7 0-12.4.8-15.9 2.1v9.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M254.2 257.7a49.6 49.6 0 0 1 23.3-2c9.3 1.6 16.4 5.3 15.9 8.4v.2l3.5-8.2c.6-3.3-7.3-7.5-17.6-9.2a53.5 53.5 0 0 0-9.2-.7c-6.7 0-12.4.8-15.9 2.1v9.4"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M285.7 267.3c4.4-.3 7.3-1.4 7.7-3.2.2-1.5-1.2-3-3.8-4.5-1.2.1-2.5.3-3.9.3v7.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M285.7 267.3c4.4-.3 7.3-1.4 7.7-3.2.2-1.5-1.2-3-3.8-4.5-1.2.1-2.5.3-3.9.3v7.4"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M270 261.5a13 13 0 0 0-5.7 1.9v.2c-.5 1 1.8 3 5.8 5.4v-7.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M270 261.5a13 13 0 0 0-5.7 1.9v.2c-.5 1 1.8 3 5.8 5.4v-7.5" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M295.4 282c.4-1.2-3.8-3.6-9.7-5.8-2.8-1-5-2-7.8-3.2-8.3-3.7-14.4-7.9-13.6-9.4v-.2c-.4.4-1 8-1 8-.8 1.3 4.8 5.5 12.4 9.1 2.4 1.2 7.6 3 10 4 4.3 1.4 8.7 4.3 8.3 5.3l1.4-7.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M295.4 282c.4-1.2-3.8-3.6-9.7-5.8-2.8-1-5-2-7.8-3.2-8.3-3.7-14.4-7.9-13.6-9.4v-.2c-.4.4-1 8-1 8-.8 1.3 4.8 5.5 12.4 9.1 2.4 1.2 7.6 3 10 4 4.3 1.4 8.7 4.3 8.3 5.3l1.4-7.7z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M263.9 254.4c.6-2.3 1.4-4.4 2.1-6.6h-.5a5.2 5.2 0 0 1-.5.1 52.8 52.8 0 0 1-1.4 4.8c-1-1.4-2-2.7-2.7-4.1l-1 .2h-1a131.3 131.3 0 0 1 4 5.7h.5l.5-.1m6-6.6h-1a8 8 0 0 1-.8 0v6.2h4.2v-.7h-2.6l.1-5.5m6.8 1 2 .3v-.7l-5.8-.5v.8a19.3 19.3 0 0 1 2 0l-.4 5.6h1.6l.5-5.4m2.4 6c.3 0 .5 0 .8.2l.8.2.7-2.9.6 1.2.8 2.1 1 .2c.4 0 .7.2 1 .3l-.3-.7c-.4-1-1-1.9-1.3-2.9 1 0 1.9-.3 2.1-1.2.1-.6 0-1-.7-1.5-.4-.3-1.2-.4-1.7-.5l-2.4-.5-1.4 6m3-5.2c.7.2 1.5.3 1.5 1v.5c-.3.9-1 1.2-2 .9l.5-2.4m8 7-.2 2 .8.5.9.5.5-7a3.4 3.4 0 0 1-.7-.3l-6.1 3.8.5.3.4.2 1.7-1.2 2.3 1.3zm-1.7-1.5 2-1.4-.2 2.3-1.8-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.1,
      d: "M182.2 192.4c0-1 1-2 2-2s2.2 1 2.2 2c0 1.1-1 2-2.1 2a2 2 0 0 1-2.1-2z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M205.7 175.4c6.3 0 12 1 15.7 2.4a31.7 31.7 0 0 0 14.6 2.3c2.7 0 6.5.8 10.3 2.4a27.3 27.3 0 0 1 7.4 4.7l-1.5 1.4-.4 3.8-4.1 4.7-2 1.8-5 3.9-2.5.2-.7 2.1-31.6-3.7-31.7 3.7-.8-2.1-2.5-.2-4.9-4-2-1.7-4.1-4.7-.5-3.8-1.5-1.4a27.6 27.6 0 0 1 7.5-4.7 26 26 0 0 1 10.2-2.4c2 .2 4.2.1 6.6-.2a30 30 0 0 0 8-2c3.7-1.5 9-2.5 15.5-2.5z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206.2 217.1c-11.8 0-22.4-1.4-29.9-3.6a1.1 1.1 0 0 1-.8-1.2c0-.5.3-1 .8-1.2a109 109 0 0 1 29.9-3.6c11.7 0 22.3 1.4 29.8 3.6a1.3 1.3 0 0 1 0 2.4c-7.5 2.2-18 3.6-29.8 3.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M206.1 215.6c-10.6 0-20.2-1.2-27.5-3.1 7.3-2 16.9-3 27.5-3.1a115 115 0 0 1 27.6 3c-7.3 2-17 3.2-27.6 3.2"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M206.9 215.7v-6.3m-1.7 6.3v-6.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.2, d: "M203.6 215.7v-6.3m-1.6 6.3v-6.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M200.6 215.7v-6.3m-2.8 5.9v-5.7m1.3 5.8v-6m-3.8 5.6v-5.2m1.3 5.4v-5.6"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M192 214.8V210m1 4.7V210m1.2 5v-5m-3.4 4.7v-4.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.5, d: "M189.7 214.5v-4.2m-1.2 4.1v-4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.6, d: "M186 214v-3m1.3 3.2v-3.5m-2.5 3.1V211" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.7, d: "M183.7 213.6v-2.3m-1.3 2v-1.8m-1.2 1.6v-1.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.9, d: "M179.8 212.8v-.7" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M213.7 215.3v-5.8m-2.9 6v-6.1m-2.1 6.2v-6.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206 207.4a108 108 0 0 0-30 3.9c.6-.3.5-1-.3-3-1-2.5-2.4-2.4-2.4-2.4 8.3-2.5 20-4 32.8-4a123 123 0 0 1 33 4s-1.5-.1-2.5 2.3c-.8 2-.8 2.8-.2 3-7.5-2.2-18.4-3.7-30.3-3.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206.1 201.9c-12.9 0-24.5 1.5-32.8 4a1 1 0 0 1-1.3-.6 1 1 0 0 1 .7-1.3 121 121 0 0 1 33.4-4.2c13.2 0 25.2 1.7 33.5 4.2.6.2.9.8.7 1.3-.2.5-.8.8-1.3.6-8.4-2.5-20-4-32.9-4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.4,
      d: "M206.1 215.6c-10.6 0-20.2-1.2-27.5-3.1 7.3-2 16.9-3 27.5-3.1a115 115 0 0 1 27.6 3c-7.3 2-17 3.2-27.6 3.2z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M197 204.8c0-.5.4-1 1-1 .5 0 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206.1 205.6H203a1 1 0 0 1 0-2h6.4c.5 0 1 .5 1 1s-.5 1-1 1h-3.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m190.3 206.5-2.3.2c-.6.1-1-.3-1.2-.8a1 1 0 0 1 1-1.1l2.2-.3 2.4-.3c.5 0 1 .3 1.1.9.1.5-.3 1-.9 1l-2.3.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M181 206.7c0-.6.5-1 1.1-1 .6 0 1 .4 1 1 0 .5-.4 1-1 1a1 1 0 0 1-1-1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", stroke: "#000", strokeWidth: 0.4, d: "m174 208.5 1.2-1.6 3.3.4-2.6 2-1.8-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m222 206.5 2.3.2c.5.1 1-.3 1.1-.8a1 1 0 0 0-.9-1.1l-2.2-.3-2.4-.3a1 1 0 0 0-1.1.9c-.1.5.3 1 .9 1l2.3.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M213.3 204.8c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1m15.8 1.9c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1 0 .5-.4 1-1 1a1 1 0 0 1-1-1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", stroke: "#000", strokeWidth: 0.4, d: "m238.2 208.5-1.1-1.6-3.3.4 2.6 2 1.8-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M177.3 212.8c7.4-2.1 17.6-3.4 28.8-3.4 11.3 0 21.4 1.3 28.9 3.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m182.3 183.8 1.4 1 2-3.2a7.4 7.4 0 0 1-3.6-7.2c.2-4.1 5.2-7.6 11.7-7.6 3.3 0 6.3 1 8.5 2.4 0-.6 0-1.2.2-1.8a17.4 17.4 0 0 0-8.7-2.1c-7.4 0-13.2 4.1-13.5 9.1a8.9 8.9 0 0 0 3 7.6l-1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m182.3 183.8 1.4 1 2-3.2a7.4 7.4 0 0 1-3.6-7.2c.2-4.1 5.2-7.6 11.7-7.6 3.3 0 6.3 1 8.5 2.4 0-.6 0-1.2.2-1.8a17.4 17.4 0 0 0-8.7-2.1c-7.4 0-13.2 4.1-13.5 9.1a8.9 8.9 0 0 0 3 7.6l-1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M182.4 183.8a9.3 9.3 0 0 1-4-7.3c0-3.2 2-6.1 5.3-8a8.5 8.5 0 0 0-3.4 6.8 8.9 8.9 0 0 0 3 6.7l-.9 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M182.4 183.8a9.3 9.3 0 0 1-4-7.3c0-3.2 2-6.1 5.3-8a8.5 8.5 0 0 0-3.4 6.8 8.9 8.9 0 0 0 3 6.7l-.9 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M160.1 187.1a8.8 8.8 0 0 1-2.3-5.9c0-1.3.3-2.6 1-3.8 2-4.2 8.4-7.2 16-7.2 2 0 4 .2 5.9.6l-1 1.4a25.5 25.5 0 0 0-4.9-.4c-7 0-12.8 2.7-14.5 6.3a7 7 0 0 0-.7 3.1 7.3 7.3 0 0 0 2.7 5.6l-2.6 4.1-1.3-1 1.7-2.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M160.1 187.1a8.8 8.8 0 0 1-2.3-5.9c0-1.3.3-2.6 1-3.8 2-4.2 8.4-7.2 16-7.2 2 0 4 .2 5.9.6l-1 1.4a25.5 25.5 0 0 0-4.9-.4c-7 0-12.8 2.7-14.5 6.3a7 7 0 0 0-.7 3.1 7.3 7.3 0 0 0 2.7 5.6l-2.6 4.1-1.3-1 1.7-2.8z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M162.7 173.3a10.5 10.5 0 0 0-4 4.1 8.6 8.6 0 0 0-.9 3.8c0 2.3.9 4.3 2.3 5.9l-1.5 2.5a10.4 10.4 0 0 1-2.3-6.5c0-4 2.5-7.5 6.4-9.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M162.7 173.3a10.5 10.5 0 0 0-4 4.1 8.6 8.6 0 0 0-.9 3.8c0 2.3.9 4.3 2.3 5.9l-1.5 2.5a10.4 10.4 0 0 1-2.3-6.5c0-4 2.5-7.5 6.4-9.8z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M206 164.4c1.7 0 3.2 1.1 3.5 2.6.3 1.4.4 2.9.4 4.5v1.1c.1 3.3.6 6.3 1.3 8.1l-5.2 5-5.2-5c.7-1.8 1.2-4.8 1.3-8.1v-1.1c0-1.6.2-3.1.4-4.5.3-1.5 1.8-2.6 3.5-2.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206 164.4c1.7 0 3.2 1.1 3.5 2.6.3 1.4.4 2.9.4 4.5v1.1c.1 3.3.6 6.3 1.3 8.1l-5.2 5-5.2-5c.7-1.8 1.2-4.8 1.3-8.1v-1.1c0-1.6.2-3.1.4-4.5.3-1.5 1.8-2.6 3.5-2.6z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M206 166c1 0 1.7.6 1.8 1.4.2 1.2.4 2.6.4 4.2v1c.1 3.2.6 6 1.2 7.7l-3.4 3.2-3.4-3.2c.7-1.7 1.1-4.5 1.2-7.7v-1a28.1 28.1 0 0 1 .4-4.2 2 2 0 0 1 1.8-1.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206 166c1 0 1.7.6 1.8 1.4.2 1.2.4 2.6.4 4.2v1c.1 3.2.6 6 1.2 7.7l-3.4 3.2-3.4-3.2c.7-1.7 1.1-4.5 1.2-7.7v-1a28.1 28.1 0 0 1 .4-4.2 2 2 0 0 1 1.8-1.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m229.7 183.8-1.3 1-2-3.2a7.4 7.4 0 0 0 3.6-6.3 7 7 0 0 0 0-.9c-.2-4.1-5.3-7.6-11.7-7.6a15 15 0 0 0-8.5 2.4 23 23 0 0 0-.2-1.8 17.4 17.4 0 0 1 8.7-2.1c7.4 0 13.2 4.1 13.4 9.1a8.9 8.9 0 0 1-3 7.6l1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m229.7 183.8-1.3 1-2-3.2a7.4 7.4 0 0 0 3.6-6.3 7 7 0 0 0 0-.9c-.2-4.1-5.3-7.6-11.7-7.6a15 15 0 0 0-8.5 2.4 23 23 0 0 0-.2-1.8 17.4 17.4 0 0 1 8.7-2.1c7.4 0 13.2 4.1 13.4 9.1a8.9 8.9 0 0 1-3 7.6l1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M229.6 183.8a9.1 9.1 0 0 0 4.1-7.3c0-3.2-2.1-6.1-5.3-8a8.5 8.5 0 0 1 3.4 6.8 8.9 8.9 0 0 1-3.2 6.7l1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M229.6 183.8a9.1 9.1 0 0 0 4.1-7.3c0-3.2-2.1-6.1-5.3-8a8.5 8.5 0 0 1 3.4 6.8 8.9 8.9 0 0 1-3.2 6.7l1 1.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M252 187.1a8.8 8.8 0 0 0 2.2-5.9 8.7 8.7 0 0 0-.9-3.8c-2-4.2-8.4-7.2-16-7.2a29 29 0 0 0-6 .6l1 1.4a25.4 25.4 0 0 1 5-.4c7 0 12.8 2.7 14.4 6.3.5 1 .7 2 .7 3.1a7.3 7.3 0 0 1-2.6 5.6l2.5 4.1 1.3-1-1.7-2.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M252 187.1a8.8 8.8 0 0 0 2.2-5.9 8.7 8.7 0 0 0-.9-3.8c-2-4.2-8.4-7.2-16-7.2a29 29 0 0 0-6 .6l1 1.4a25.4 25.4 0 0 1 5-.4c7 0 12.8 2.7 14.4 6.3.5 1 .7 2 .7 3.1a7.3 7.3 0 0 1-2.6 5.6l2.5 4.1 1.3-1-1.7-2.8z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M249.3 173.3a10.6 10.6 0 0 1 4 4.1 8.7 8.7 0 0 1 .9 3.8 8.8 8.8 0 0 1-2.3 5.9l1.6 2.5a10.4 10.4 0 0 0 2.3-6.5c0-4-2.6-7.5-6.5-9.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M249.3 173.3a10.6 10.6 0 0 1 4 4.1 8.7 8.7 0 0 1 .9 3.8 8.8 8.8 0 0 1-2.3 5.9l1.6 2.5a10.4 10.4 0 0 0 2.3-6.5c0-4-2.6-7.5-6.5-9.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M204.2 181.4c0-1 .8-1.8 1.8-1.8s1.9.8 1.9 1.8-.9 1.7-1.9 1.7a1.8 1.8 0 0 1-1.8-1.7" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M204.2 181.4c0-1 .8-1.8 1.8-1.8s1.9.8 1.9 1.8-.9 1.7-1.9 1.7a1.8 1.8 0 0 1-1.8-1.7z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M204.2 178c0-1 .8-1.8 1.8-1.8s1.9.8 1.9 1.8-.9 1.7-1.9 1.7a1.8 1.8 0 0 1-1.8-1.7m.4-3.7c0-.7.6-1.3 1.4-1.3.8 0 1.5.6 1.5 1.3 0 .8-.7 1.4-1.5 1.4s-1.4-.6-1.4-1.4m.4-3.3c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1m.2-2.8c0-.5.4-.8.8-.8.5 0 .9.3.9.8 0 .4-.4.8-.9.8a.8.8 0 0 1-.8-.8"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m206.2 191.8 1.2.2a4.6 4.6 0 0 0 4.5 6 4.7 4.7 0 0 0 4.4-3c.1 0 .5-1.7.7-1.7.2 0 .1 1.8.2 1.7.3 2.3 2.4 3.8 4.7 3.8a4.6 4.6 0 0 0 4.7-5l1.5-1.5.7 2a4 4 0 0 0-.4 1.9 4.4 4.4 0 0 0 4.5 4.2c1.6 0 3-.7 3.8-1.9l.9-1.2v1.5c0 1.5.6 2.8 2 3 0 0 1.7.1 4-1.6 2.1-1.7 3.3-3.1 3.3-3.1l.2 1.7s-1.8 2.8-3.8 4c-1 .6-2.7 1.3-4 1-1.4-.2-2.4-1.3-3-2.6a6.7 6.7 0 0 1-3.3 1 6.5 6.5 0 0 1-6.1-3.7 7 7 0 0 1-10.4-.3 7 7 0 0 1-4.6 1.8 6.9 6.9 0 0 1-5.7-3 6.9 6.9 0 0 1-5.7 3 7 7 0 0 1-4.7-1.8 7 7 0 0 1-10.4.3 6.5 6.5 0 0 1-6 3.7 6.7 6.7 0 0 1-3.4-1c-.6 1.3-1.5 2.4-3 2.7-1.2.2-2.9-.5-4-1.1-2-1.2-3.8-4-3.8-4l.2-1.7s1.2 1.4 3.4 3.1c2.2 1.8 3.9 1.6 3.9 1.6 1.4-.2 2-1.5 2-3v-1.5l1 1.2a4.6 4.6 0 0 0 3.7 2c2.5 0 4.5-2 4.5-4.3a4 4 0 0 0-.4-2l.8-1.9 1.5 1.5a4.4 4.4 0 0 0 0 .6c0 2.4 2 4.4 4.6 4.4 2.4 0 4.4-1.5 4.7-3.8 0 0 0-1.6.2-1.7.2 0 .6 1.7.7 1.6a4.7 4.7 0 0 0 4.5 3.1 4.6 4.6 0 0 0 4.5-6l1.2-.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M238.6 197.7c.3-.8 0-1.6-.6-1.8-.5-.2-1.2.3-1.5 1.1-.3.8 0 1.6.6 1.8.5.2 1.2-.3 1.5-1.1m-20.5-4c0-.8-.3-1.6-1-1.6-.5-.1-1 .5-1.2 1.4-.1.8.3 1.5.9 1.6.6 0 1.2-.6 1.3-1.4m-23.9 0c0-.8.4-1.6 1-1.6.6-.1 1.1.5 1.2 1.4.1.8-.3 1.5-.9 1.6-.6 0-1.1-.6-1.2-1.4m-20.6 4c-.2-.8 0-1.6.6-1.8.6-.2 1.2.3 1.5 1.1.3.8 0 1.6-.5 1.8-.6.2-1.3-.3-1.6-1.1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M182.7 184a5.1 5.1 0 0 1 2.2 2.9s0-.3.6-.6 1-.3 1-.3l-.1 1.3-.3 2.2a7.4 7.4 0 0 1-.7 1.6 1.9 1.9 0 0 0-1.5-.4 1.8 1.8 0 0 0-1.2.9s-.7-.6-1.2-1.3l-1.1-2-.7-1.1s.5-.2 1.1 0c.6 0 .8.2.8.2a4.9 4.9 0 0 1 1-3.4m.4 9.8a1.8 1.8 0 0 1-.6-1c0-.5 0-.9.3-1.2 0 0-.9-.5-1.8-.7-.7-.2-2-.2-2.3-.2h-1l.2.5c.2.5.5.7.5.7a5 5 0 0 0-3 2 5.3 5.3 0 0 0 3.5 1l-.2.8v.6l1-.4c.3-.1 1.5-.5 2-1 .8-.4 1.5-1.1 1.5-1.1m2.7-.5a1.6 1.6 0 0 0 .2-1.1 1.7 1.7 0 0 0-.6-1l1.4-1.3a10 10 0 0 1 2-.9l1.1-.4v.6a5.7 5.7 0 0 1-.2.8 5 5 0 0 1 3.4 1 5 5 0 0 1-2.9 2 6.4 6.4 0 0 0 .7 1.2h-1c-.4 0-1.6 0-2.3-.2a11 11 0 0 1-1.8-.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M182.2 192.4c0-1 1-2 2-2s2.2 1 2.2 2c0 1.1-1 2-2.1 2a2 2 0 0 1-2.1-2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M206.1 180.8a5.7 5.7 0 0 1 1.9 3.7s.2-.3.9-.5c.7-.3 1.2-.2 1.2-.2l-.5 1.4-.8 2.4a8.2 8.2 0 0 1-1 1.7 2.1 2.1 0 0 0-1.7-.7c-.6 0-1.2.3-1.6.7 0 0-.6-.7-1-1.7l-.8-2.4-.5-1.4 1.2.2c.7.2.9.5.9.5 0-1.4.8-2.8 1.8-3.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M204.6 191.8a2 2 0 0 1-.5-1.2c0-.5.1-1 .4-1.3 0 0-.8-.7-1.8-1-.7-.4-2-.7-2.5-.7l-1.2-.2.2.6.4.9a5.9 5.9 0 0 0-3.7 1.7c1 .9 2.3 1.6 3.7 1.6l-.4 1-.2.6 1.2-.2c.4-.1 1.8-.4 2.5-.7 1-.4 1.9-1 1.9-1m3 0a1.9 1.9 0 0 0 .1-2.6s.9-.7 1.8-1a8 8 0 0 1 2.5-.7l1.2-.3-.1.7-.4.9c1.4 0 2.7.8 3.6 1.7a5.9 5.9 0 0 1-3.6 1.6 6.9 6.9 0 0 0 .5 1.6l-1.2-.2-2.5-.7c-1-.4-1.8-1-1.8-1m22-8a5.2 5.2 0 0 0-2.2 3l-.7-.6c-.6-.3-1-.3-1-.3l.2 1.3c0 .3 0 1.3.3 2.2.2 1 .6 1.6.6 1.6a2 2 0 0 1 1.5-.4c.6.1 1 .5 1.3.9l1.1-1.3c.6-.8 1-1.7 1.1-2l.7-1.1s-.4-.2-1 0c-.7 0-1 .2-1 .2a4.9 4.9 0 0 0-1-3.4m-.3 9.8c.3-.3.5-.6.6-1a1.6 1.6 0 0 0-.2-1.2s.8-.5 1.7-.7c.7-.2 2-.2 2.3-.2h1.1l-.3.5a6.2 6.2 0 0 1-.4.7 5 5 0 0 1 2.9 2 5.3 5.3 0 0 1-3.5 1l.2.8v.6l-1-.4c-.3-.1-1.4-.5-2-1-.8-.4-1.4-1.1-1.4-1.1m-2.8-.5a1.7 1.7 0 0 1-.2-1.1c0-.5.3-.8.6-1 0 0-.6-.8-1.4-1.3-.6-.4-1.7-.8-2-.9a171.4 171.4 0 0 1-1-.4v.6c0 .5.2.8.2.8a5.2 5.2 0 0 0-3.5 1c.7.9 1.7 1.7 3 2 0 0-.3.2-.5.7l-.3.5h1c.4 0 1.7 0 2.3-.2a11.1 11.1 0 0 0 1.8-.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M226 192.4c0-1 1-2 2-2s2.1 1 2.1 2a2 2 0 0 1-2 2 2 2 0 0 1-2.1-2m23.2 4.4c-.4-.5-1.4-.4-2.2.2-.8.7-1 1.6-.5 2.2.5.5 1.5.4 2.3-.3.7-.6 1-1.6.5-2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m246.3 198 .7-1c.7-.6 1.8-.7 2.3-.2l.1.2s1-2 2.3-2.6c1.3-.7 3.4-.5 3.4-.5a2.8 2.8 0 0 0-2.9-2.8 3 3 0 0 0-2.4 1l-.2-1s-1.3.3-1.9 1.8c-.6 1.5 0 3.6 0 3.6s-.3-.9-.7-1.5a8 8 0 0 0-2.4-1.6l-1.3-.7-.1.5a5 5 0 0 0 0 .8 7.9 7.9 0 0 0-3.7.5 4.7 4.7 0 0 0 2.5 2.2l-.8.7a4 4 0 0 0-.4.5l1.3.2 2.5.2a14.5 14.5 0 0 0 1.7-.2m-80.3 0c0-.4-.3-.7-.7-1-.7-.7-1.7-.8-2.2-.3l-.2.3s-1-2-2.3-2.7c-1.2-.7-3.3-.5-3.3-.5a2.8 2.8 0 0 1 2.8-2.8c1 0 1.9.4 2.4 1l.2-1s1.3.3 2 1.8c.5 1.5-.1 3.6-.1 3.6s.3-.9.8-1.5a8 8 0 0 1 2.4-1.6l1.3-.7v1.3a7.9 7.9 0 0 1 3.7.5 4.7 4.7 0 0 1-2.5 2.2l.8.7.4.5-1.2.2-2.6.2a14.7 14.7 0 0 1-1.7-.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M163 196.8c.6-.5 1.6-.4 2.4.3.7.6 1 1.5.4 2-.5.6-1.5.5-2.2-.2-.8-.6-1-1.6-.5-2m41-6.3c0-1.1.9-2 2-2s2.1.9 2.1 2c0 1-1 2-2 2a2 2 0 0 1-2.1-2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#005bbf",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M201.8 160.6c0-2.2 1.9-4 4.3-4s4.2 1.8 4.2 4-1.9 4-4.3 4a4.1 4.1 0 0 1-4.2-4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M205 149.3v2.2h-2.4v2.2h2.3v6.3H202l-.2.6c0 .6.1 1.1.3 1.6h7.9c.2-.5.3-1 .3-1.6l-.2-.6h-2.8v-6.3h2.3v-2.2h-2.3v-2.2h-2.4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ccc",
      d: "M206.5 330.6a82 82 0 0 1-35.5-8.2 22.7 22.7 0 0 1-12.8-20.4v-32h96.4v32a22.7 22.7 0 0 1-12.8 20.4 81 81 0 0 1-35.3 8.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M206.5 330.6a82 82 0 0 1-35.5-8.2 22.7 22.7 0 0 1-12.8-20.4v-32h96.4v32a22.7 22.7 0 0 1-12.8 20.4 81 81 0 0 1-35.3 8.2z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ccc", d: "M206.3 270h48.3v-53.5h-48.3V270z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.5, d: "M206.3 270h48.3v-53.5h-48.3V270z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M206.3 302c0 12.6-10.7 22.9-24 22.9s-24.2-10.3-24.2-23v-32h48.2v32" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", stroke: "#000", strokeWidth: 0.5, d: "M168.6 320.9c1.5.8 3.6 2 5.8 2.6l-.1-54.7h-5.7v52z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.5,
      d: "M158 301.6a24.4 24.4 0 0 0 5.5 15v-47.5h-5.4v32.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c7b500", stroke: "#000", strokeWidth: 0.5, d: "M179.4 324.7a26.6 26.6 0 0 0 5.6 0v-55.9h-5.6v56z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", stroke: "#000", strokeWidth: 0.5, d: "M190 323.5a19 19 0 0 0 5.8-2.5v-52.2H190l-.1 54.7z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M158.1 270h48.2v-53.5H158V270z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.5, d: "M158.1 270h48.2v-53.5H158V270z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", stroke: "#000", strokeWidth: 0.5, d: "M201 316c2.4-2 4.6-6.8 5.4-12.2l.1-35H201l.1 47.3z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M206.3 302c0 12.6-10.7 22.9-24 22.9s-24.2-10.3-24.2-23v-32h48.2v32"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#ad1519", d: "M254.6 270v32c0 12.6-10.8 22.9-24.1 22.9s-24.2-10.3-24.2-23v-32h48.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M254.6 270v32c0 12.6-10.8 22.9-24.1 22.9s-24.2-10.3-24.2-23v-32h48.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "m215.1 294.1.1.5c0 .6-.5 1-1.1 1a1 1 0 0 1-1.1-1v-.5h-1.5a2.5 2.5 0 0 0 1.8 2.9v3.9h1.6V297a2.6 2.6 0 0 0 1.7-1.6h4.4v-1.2h-6m21.8 0v1.2h-4a2.5 2.5 0 0 1-.3.6l4.6 5.2-1.2 1-4.6-5.3-.2.1v8.7h-1.6V297h-.2l-4.8 5.2-1.2-1 4.7-5.3a2.1 2.1 0 0 1-.2-.4h-4V294h13zm2.6 0v1.2h4.4c.3.8.9 1.4 1.7 1.6v3.9h1.6V297a2.5 2.5 0 0 0 1.8-2.4 2 2 0 0 0 0-.5h-1.6l.1.5c0 .6-.5 1-1 1-.7 0-1.2-.4-1.2-1a1 1 0 0 1 .1-.5h-5.9m-6.7 22.1a15.6 15.6 0 0 0 3.7-1l.8 1.4a17.6 17.6 0 0 1-4.3 1.2 2.6 2.6 0 0 1-2.6 2 2.6 2.6 0 0 1-2.5-2 17.5 17.5 0 0 1-4.6-1.2l.8-1.4c1.3.5 2.6.9 4 1a2.5 2.5 0 0 1 1.5-1.3v-6.7h1.6v6.7c.7.2 1.3.7 1.6 1.4zm-11-2.2-.8 1.4a16.6 16.6 0 0 1-3.6-3.1c-.9.2-1.8 0-2.5-.5a2.4 2.4 0 0 1-.3-3.5l.1-.1a15.3 15.3 0 0 1-1.3-4.8h1.7a13.1 13.1 0 0 0 1 4c.5 0 1 0 1.4.2l4.1-4.5 1.3 1-4.1 4.5c.5.9.5 2-.1 2.8a15.2 15.2 0 0 0 3.1 2.6zm-6-4.8c.3-.4 1-.5 1.5 0s.5 1 .1 1.4a1.2 1.2 0 0 1-1.6.1 1 1 0 0 1 0-1.5zm-2.2-4.5-1.6-.3-.3-4.3 1.7-.6v2.5c0 1 0 1.8.2 2.7zm1.4-5.3 1.7.4v2.2c0-.8.3 2.1.3 2.1l-1.7.6a14 14 0 0 1-.3-2.7v-2.6zm5.6 13.7a15.7 15.7 0 0 0 4.8 2.6l.4-1.6a13.7 13.7 0 0 1-4-2l-1.2 1m-.8 1.4a17.4 17.4 0 0 0 4.8 2.6l-1.2 1.1a18.7 18.7 0 0 1-4-2l.4-1.7m2.2-9.4 1.6.7 3-3.3-1-1.4-3.6 4m-1.3-1-1-1.4 3-3.3 1.6.7-3.6 4m18.1 9.9.8 1.4a16.7 16.7 0 0 0 3.6-3.1c.9.2 1.8 0 2.5-.5a2.4 2.4 0 0 0 .3-3.5l-.1-.1a15 15 0 0 0 1.3-4.8h-1.7a13.3 13.3 0 0 1-1 4 3 3 0 0 0-1.4.2l-4.1-4.5-1.3 1 4.1 4.5a2.4 2.4 0 0 0 .1 2.8 15 15 0 0 1-3.1 2.6zm6-4.8a1.2 1.2 0 0 0-1.5 0 1 1 0 0 0-.1 1.4 1.2 1.2 0 0 0 1.6.1 1 1 0 0 0 0-1.5zm2.2-4.5 1.6-.3.3-4.3-1.7-.6v2.5c0 1 0 1.9-.2 2.8zm-1.4-5.3-1.7.4v2.2c0-.8-.3 2.1-.3 2.1l1.7.6.3-2.7v-2.6m-5.6 13.7a15.7 15.7 0 0 1-4.8 2.6l-.4-1.6a13.7 13.7 0 0 0 4-2l1.2 1m.8 1.4a17.4 17.4 0 0 1-4.8 2.6l1.2 1.1a18.6 18.6 0 0 0 4-2l-.4-1.7m-2.2-9.4-1.6.7-2.9-3.3 1-1.4 3.5 4m1.3-1 1-1.4-3-3.3-1.6.7 3.6 4m-20.1-8.7.5 1.6h4.5l.5-1.6h-5.5m21.1 0-.5 1.6h-4.5l-.5-1.6h5.5m-11.6 21.9c0-.6.5-1 1.1-1a1 1 0 0 1 1.1 1c0 .6-.5 1-1 1a1.1 1.1 0 0 1-1.2-1zm1.9-7.8 1.7-.4v-4.3l-1.7-.5v5.2m-1.6 0-1.7-.4v-4.3l1.7-.5v5.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M211.5 294.2c.2-1 1-1.6 1.8-2V287h1.6v5.3c.8.3 1.5.9 1.7 1.6h4.4v.3h-6a1.2 1.2 0 0 0-1-.6c-.4 0-.7.3-1 .6h-1.5m12.2 0v-.3h4.1a2.4 2.4 0 0 1 .2-.3l-5-5.7 1.2-1 5 5.6.2-.1V285h1.6v7.3h.3l4.9-5.5 1.2 1-4.9 5.5.3.6h4v.3h-13zm21.6 0a1.1 1.1 0 0 1 1-.6c.5 0 .8.3 1 .6h1.6c-.2-1-.9-1.6-1.8-2V287h-1.6v5.3c-.8.3-1.4.8-1.7 1.6h-4.4v.3h6m-30.2-15 6 6.8 1.3-1-6.1-6.7.3-.6h4.4V276h-4.4a2.6 2.6 0 0 0-2.5-1.7 2.6 2.6 0 0 0-2.7 2.5 2.5 2.5 0 0 0 1.8 2.4v5.2h1.6v-5.2h.3zm32 0v5.3h-1.7v-5.2a2.5 2.5 0 0 1-.4-.2l-6 6.8-1.3-1 6.2-6.9-.1-.3h-4.5V276h4.5a2.6 2.6 0 0 1 2.4-1.7 2.6 2.6 0 0 1 2.7 2.5 2.5 2.5 0 0 1-1.9 2.4zm-16.1 0v3.3h-1.7v-3.2a2.6 2.6 0 0 1-1.7-1.6h-4V276h4a2.6 2.6 0 0 1 2.5-1.7c1.2 0 2.2.7 2.5 1.7h4v1.6h-4a2.5 2.5 0 0 1-1.6 1.6zm-17.8 4-1.7.4v4.3l1.7.5v-5.2m1.6 0 1.7.4v4.3l-1.7.5v-5.2m30.6 0-1.7.4v4.3l1.7.5v-5.2m1.6 0 1.7.4v4.3l-1.7.5v-5.2m-25.5.8 1.6-.7 2.9 3.3-1 1.4-3.5-4m-1.3 1-1 1.4 3 3.3 1.6-.7-3.6-4m18.5-1.1-1.6-.7-3 3.3 1 1.4 3.6-4m1.2 1 1 1.4-3 3.3-1.5-.7 3.5-4m-20.3 9 .5-1.6h4.5l.5 1.6h-5.5m-6.7-17c0-.6.5-1 1.2-1a1 1 0 0 1 1 1c0 .6-.4 1-1 1a1.1 1.1 0 0 1-1.2-1zm12.1.8-.5 1.6h-4.5l-.5-1.6h5.5m0-1.6-.5-1.6h-4.5l-.5 1.6h5.5m15.7 17.8-.5-1.6h-4.5l-.5 1.6h5.5m4.4-17c0-.6.5-1 1.1-1a1 1 0 0 1 1.1 1c0 .6-.5 1-1 1a1.1 1.1 0 0 1-1.2-1zm-16.1 0c0-.6.5-1 1.1-1a1 1 0 0 1 1.1 1c0 .6-.5 1-1.1 1a1.1 1.1 0 0 1-1.1-1zm6.2.8.5 1.6h4.6l.5-1.6h-5.6m0-1.6.5-1.6h4.6l.5 1.6h-5.6m-5.9 5-1.7.5v4.3l1.7.5V281m1.7 0 1.6.5v4.3l-1.6.5V281"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#c8b100",
      strokeWidth: 0.3,
      d: "M232.7 316.3a15.6 15.6 0 0 0 3.7-1.1l.8 1.4a17.6 17.6 0 0 1-4.3 1.2 2.6 2.6 0 0 1-2.6 2 2.6 2.6 0 0 1-2.5-2 17.5 17.5 0 0 1-4.6-1.2l.8-1.4c1.3.5 2.6.9 4 1a2.5 2.5 0 0 1 1.5-1.3v-6.7h1.6v6.7c.7.2 1.3.7 1.6 1.4zm-4.7-20.4a2.3 2.3 0 0 1-.2-.5h-4V294h4a2.6 2.6 0 0 1 .2-.4l-5-5.6 1.2-1 5 5.5a2.2 2.2 0 0 1 .2 0V285h1.7v7.3h.2l4.9-5.5 1.2 1-4.9 5.5.3.6h4v1.5h-4c0 .2-.2.4-.3.5l4.7 5.3-1.3 1-4.6-5.3-.2.1v8.7h-1.6V297l-.2-.1-4.8 5.3-1.2-1 4.7-5.3m-12.8-16.7 6 6.8 1.3-1-6.1-6.7.3-.6h4.4V276h-4.4a2.6 2.6 0 0 0-2.5-1.7 2.6 2.6 0 0 0-2.6 2.5 2.5 2.5 0 0 0 1.7 2.4v5.2h1.6v-5.2h.3zm6.5 34.8-.8 1.4a16.6 16.6 0 0 1-3.6-3.1c-.9.2-1.8 0-2.5-.5a2.4 2.4 0 0 1-.3-3.5l.1-.1a15.3 15.3 0 0 1-1.2-4.8h1.6a13.1 13.1 0 0 0 1 4c.5 0 1 0 1.4.2l4.1-4.5 1.3 1-4.1 4.5c.6.9.5 2-.1 2.8a15.2 15.2 0 0 0 3.1 2.6zm-8.4-13.1V297a2.5 2.5 0 0 1-1.8-2.4c0-1 .8-2 1.8-2.4V287h1.6v5.3c.8.2 1.5.8 1.7 1.6h4.4v1.5h-4.4a2.6 2.6 0 0 1-1.6 1.6v3.9h-1.7m2.3 8.3c.4-.4 1.1-.5 1.6 0s.5 1 .1 1.4a1.2 1.2 0 0 1-1.6.1 1 1 0 0 1 0-1.5zm-2-4.5-1.7-.3-.3-4.3 1.7-.6v2.5c0 1 0 1.8.3 2.7zm1.4-5.3 1.6.4v2.2c0-.8.3 2.1.3 2.1l-1.7.6-.3-2.7v-2.6zm5.5 13.7a15.7 15.7 0 0 0 4.8 2.6l.4-1.6a13.7 13.7 0 0 1-4-2l-1.2 1m-.8 1.4a17.4 17.4 0 0 0 4.8 2.6l-1.2 1.1a18.7 18.7 0 0 1-4-2l.4-1.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#c8b100",
      strokeWidth: 0.3,
      d: "m221.9 305.1 1.6.7 3-3.3-1-1.4-3.6 4m-1.3-1-1-1.4 3-3.3 1.6.7-3.6 4m-7.6-9.5c0-.6.5-1 1-1 .7 0 1.2.5 1.2 1 0 .6-.5 1.1-1.1 1.1a1 1 0 0 1-1.1-1zm25.7 19.4.8 1.4a16.7 16.7 0 0 0 3.6-3.1c.9.2 1.8 0 2.6-.5a2.4 2.4 0 0 0 .2-3.5l-.1-.1a15 15 0 0 0 1.3-4.8h-1.7a13.3 13.3 0 0 1-1 4 3 3 0 0 0-1.4.2l-4.1-4.5-1.3 1 4.1 4.5a2.4 2.4 0 0 0 .1 2.8 15 15 0 0 1-3 2.6zm8.4-13.1V297a2.5 2.5 0 0 0 1.8-2.4c0-1-.7-2-1.8-2.4V287h-1.6v5.3c-.8.2-1.4.8-1.7 1.6h-4.4v1.5h4.4c.3.8.9 1.3 1.7 1.6v3.9h1.6zm-2.3 8.3a1.2 1.2 0 0 0-1.6 0 1 1 0 0 0-.1 1.4 1.2 1.2 0 0 0 1.6.1 1 1 0 0 0 0-1.5zm2-4.5 1.7-.3.3-4.3-1.7-.6v2.5c0 1 0 1.8-.2 2.7zm-1.3-5.3-1.7.4v2.2c0-.8-.3 2.1-.3 2.1l1.7.6.3-2.7v-2.6m1.6-20.1v5.2h-1.6v-5.2a2.3 2.3 0 0 1-.4-.2l-6 6.8-1.2-1 6-7v-.2h-4.5V276h4.4a2.6 2.6 0 0 1 2.5-1.7 2.6 2.6 0 0 1 2.6 2.5 2.5 2.5 0 0 1-1.8 2.4zm-16 0v3.2h-1.7v-3.2a2.6 2.6 0 0 1-1.7-1.6h-4V276h4c.4-1 1.3-1.7 2.5-1.7s2.2.7 2.5 1.7h4v1.6h-4a2.5 2.5 0 0 1-1.6 1.6zm8.8 33.8a15.7 15.7 0 0 1-4.8 2.6l-.4-1.6a13.7 13.7 0 0 0 4-2l1.2 1m.8 1.4a17.4 17.4 0 0 1-4.8 2.6l1.2 1.1a18.7 18.7 0 0 0 4-2l-.4-1.7m-27.4-31.4-1.7.5v4.3l1.7.5v-5.2m1.7 0 1.6.4v4.3l-1.6.5V283m30.5 0-1.7.5v4.3l1.7.5V283"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#c8b100",
      strokeWidth: 0.3,
      d: "m247.1 283.1 1.7.5v4.3l-1.7.5V283m-8.6 22-1.6.7-2.9-3.3 1-1.4 3.5 4m1.3-1 1-1.4-3-3.3-1.6.7 3.6 4m-18.2-20 1.6-.7 3 3.3-1 1.4-3.6-4m-1.3 1-1 1.4 3 3.3 1.6-.7-3.6-4m18.5-1.1-1.6-.7-3 3.3 1 1.4 3.6-4m1.2 1 1 1.4-3 3.2-1.5-.6 3.5-4m-20.3 9 .5-1.6h4.5l.5 1.6h-5.5m0 1.5.5 1.6h4.5l.5-1.6h-5.5M213 277c0-.6.5-1 1.2-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1.2-1zm12.1.8-.5 1.6h-4.5l-.5-1.6h5.5m0-1.6-.5-1.6h-4.5l-.5 1.6h5.5m20.1 18.5c0-.5.5-1 1.1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1.1-1 1.1a1 1 0 0 1-1.2-1zm-4.4-.7-.5-1.6h-4.5l-.5 1.6h5.5m0 1.5-.5 1.6h-4.5l-.5-1.6h5.5m-11.6 21.9c0-.6.5-1 1.1-1 .6 0 1.1.4 1.1 1s-.5 1-1 1a1.1 1.1 0 0 1-1.2-1zm1.9-7.8 1.7-.4v-4.3l-1.7-.5v5.2m-1.6 0-1.7-.4v-4.3l1.7-.5v5.2m15.7-32.6c0-.6.5-1 1.1-1a1 1 0 0 1 1.1 1c0 .6-.5 1-1 1a1.1 1.1 0 0 1-1.2-1zm-16.1 0c0-.6.5-1 1.1-1a1 1 0 0 1 1.1 1c0 .6-.5 1-1 1a1.1 1.1 0 0 1-1.2-1zm6.2.8.5 1.6h4.6l.5-1.6h-5.5m0-1.6.4-1.6h4.6l.5 1.6h-5.5m-6 5-1.6.5v4.3l1.6.5V281m1.7 0 1.6.5v4.3l-1.6.5V281"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      d: "M227.7 294.7a2.6 2.6 0 0 1 2.6-2.5 2.6 2.6 0 0 1 2.6 2.5 2.6 2.6 0 0 1-2.6 2.4c-1.4 0-2.6-1-2.6-2.4"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#db4446",
      d: "M230.9 229.7v-.6l.1-.3-2.3-.1a5.9 5.9 0 0 1-2.3-1.2c-.8-.7-1.1-1-1.6-1.2-1.3-.2-2.3.4-2.3.4s1 .4 1.7 1.3 1.5 1.3 1.8 1.4c.6.2 2.6 0 3.1.1l1.8.2"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M230.9 229.7v-.6l.1-.3-2.3-.1a5.9 5.9 0 0 1-2.3-1.2c-.8-.7-1.1-1-1.6-1.2-1.3-.2-2.3.4-2.3.4s1 .4 1.7 1.3 1.5 1.3 1.8 1.4c.6.2 2.6 0 3.1.1l1.8.2z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ed72aa",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M238.1 227.5v1.4c.2.6-.1 1.2 0 1.5 0 .4.1.6.3.9l.2.9-.7-.5-.6-.4v1c.1.2.3.8.6 1.1l1 1.3c.2.5.1 1.4.1 1.4s-.4-.7-.8-.8l-1.2-.7s.7.8.7 1.5c0 .8-.3 1.6-.3 1.6s-.3-.7-.8-1.1l-1-.9s.4 1.2.4 2v2.3l-.9-1-1-.7c0-.2.5.6.6 1.1 0 .5.3 2.3 1.8 4.5 1 1.3 2.3 3.6 5.3 2.9 3-.8 1.9-4.8 1.3-6.7a16.8 16.8 0 0 1-1-4.6c0-.8.6-2.9.5-3.3a8 8 0 0 1 .2-3.1c.4-1.3.7-1.8.9-2.3.2-.6.4-.9.4-1.3l.1-1.3.7 1.3.1 1.5s.1-1 1-1.6c.8-.6 1.8-1.1 2-1.4.3-.3.3-.5.3-.5s0 1.8-.6 2.6l-1.7 2s.7-.3 1.2-.3h.9s-.6.4-1.4 1.6c-.8 1-.5 1.2-1 2.1-.6 1-1 1-1.7 1.5-1 .8-.5 4.2-.4 4.7.2.5 2 4.5 2 5.5s.2 3.2-1.5 4.6c-1.1 1-3 1-3.4 1.2-.4.3-1.2 1.1-1.2 2.8 0 1.7.6 2 1 2.4.6.5 1.2.2 1.3.6.2.3.2.5.5.7.2.2.3.4.2.8 0 .3-.8 1.1-1.1 1.7l-.8 2.4c0 .2-.1 1 .1 1.3 0 0 .9 1 .3 1.2-.4.2-.8-.2-1-.2l-.9.5c-.3-.1-.3-.3-.4-.8l-.1-.7c-.2 0-.3.2-.4.5 0 .2 0 .8-.3.8-.2 0-.5-.4-.8-.5-.2 0-.8-.2-.8-.4 0-.3.4-.9.7-1 .4 0 .8-.3.5-.5s-.5-.2-.7 0-.8 0-.7-.2v-.8c0-.2-.4-.5.1-.8.6-.3.8.2 1.4.1.6 0 .8-.3 1-.6.2-.3.2-1-.2-1.4-.4-.5-.7-.5-.9-.8l-.3-.9v2.2l-.7-.8c-.3-.3-.6-1.3-.6-1.3v1.3c0 .4.3.7.2.8-.1.1-.8-.7-1-.8a3.7 3.7 0 0 1-1-1l-.4-1.4a4.2 4.2 0 0 1 0-1.5l.4-1h-1.4c-.7 0-1.2-.3-1.5.2-.3.5-.2 1.5.2 2.8.3 1.2.5 1.9.4 2.1a3 3 0 0 1-.7.8h-.9a2.5 2.5 0 0 0-1.2-.3h-1.3l-1.1-.3c-.3.1-.8.3-.6.7.2.6-.2.7-.5.7l-.9-.2c-.4-.1-.9 0-.8-.4 0-.4.2-.4.4-.7.2-.3.2-.5 0-.5h-.6c-.2.2-.5.5-.8.4-.2-.1-.4-.4-.4-1s-.7-1.2 0-1.1c.5 0 1.3.4 1.4 0 .2-.3 0-.4-.2-.7s-.8-.4-.3-.7l.7-.5c.1-.2.4-.8.7-.6.6.2 0 .7.6 1.3.6.7 1 1 2 .8 1 0 1.3-.2 1.3-.5l-.1-1v-1s-.4.3-.5.6l-.4.8v-2a8 8 0 0 0-.2-.8l-.3.9-.1 1s-.7-.5-.5-1.5c.1-.7-.1-1.6.1-2 .2-.3.7-1.5 2-1.6h2.6l2-.3s-2.8-1.4-3.5-1.9a9.5 9.5 0 0 1-2-2l-.6-1.6s-.5 0-1 .3a5 5 0 0 0-1.2 1l-.7 1 .1-1.2v-.8s-.4 1.2-1 1.7l-1.4 1v-.8l.2-1s-.4.8-1.1 1c-.7 0-1.8 0-1.9.4 0 .5.2 1 0 1.4 0 .3-.4.5-.4.5l-.8-.4c-.4 0-.7.2-.7.2s-.3-.4-.2-.7c.1-.2.7-.6.5-.8l-.8.2c-.3.1-.8.3-.8-.2 0-.4.2-.7 0-1 0-.3 0-.5.2-.6l1.2-.1c0-.2-.2-.5-.8-.6-.6-.1-.8-.5-.5-.8.3-.2.3-.3.5-.6.1-.2.2-.7.7-.5.5.3.4.8 1 1a4 4 0 0 0 2-.2l1.5-1 1.5-1-1-.8c-.3-.3-.7-.9-1-1a8.3 8.3 0 0 0-1.8-.6 9 9 0 0 1-1.7-.5l.8-.3c.2-.2.6-.6.8-.6h.3-1.4c-.3-.1-1-.6-1.3-.6l-.8.1s.8-.4 1.4-.5l1-.1s-.9-.3-1.1-.6l-.6-1c-.2-.1-.3-.5-.6-.5l-1 .3c-.4 0-.6-.2-.6-.6l-.1-.5c-.2-.3-.6-.8-.2-1h1.4c0-.2-.5-.6-.8-.8-.4-.2-1-.5-.7-.8l.8-.5c.2-.3.3-1 .7-.7.4.2.8 1.2 1.1 1.1.3 0 .3-.8.3-1 0-.4 0-1 .2-.9.3 0 .5.4 1 .5.4 0 1-.1 1 .2 0 .3-.3.7-.6 1-.3.3-.4 1-.3 1.4.2.5.7 1.2 1.2 1.4.4.3 1.2.5 1.7.9.5.3 1.7 1.2 2.1 1.3l.8.4s.5-.2 1.1-.2c.7 0 2.1 0 2.6-.2.6-.2 1.3-.6 1-1-.1-.6-1.3-1-1.2-1.4 0-.4.5-.4 1.2-.4.8 0 1.8.1 2-1 .2-1 .2-1.5-.8-1.8-1-.2-1.8-.2-2-1-.2-.7-.4-.9-.2-1.1.3-.2.6-.3 1.4-.4.8 0 1.6 0 1.9-.2.2-.2.3-.7.6-.9.3-.2 1.4-.4 1.4-.4s1.4.7 2.7 1.7a15 15 0 0 1 2.2 2.1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { d: "m228.1 226.8-.2-.6v-.3s.8 0 .7.3c0 .2-.2.2-.3.3l-.2.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m228.1 226.8-.2-.6v-.3s.8 0 .7.3c0 .2-.2.2-.3.3l-.2.3z" }),
  /* @__PURE__ */ jsx21("path", { d: "M232 225.4v-.4s.7 0 1 .3c.5.4.9 1 .9 1l-.8-.4h-.5l-.3-.1v-.3h-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.1,
      d: "M232 225.4v-.4s.7 0 1 .3c.5.4.9 1 .9 1l-.8-.4h-.5l-.3-.1v-.3h-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m237.3 231.3-.4-.7a8 8 0 0 1-.3-.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M217.4 226.6s.5.4.8.4h.8s.2-.5.1-.8c-.2-1.2-1.2-1.4-1.2-1.4s.3.7.1 1a2 2 0 0 1-.6.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M217.4 226.6s.5.4.8.4h.8s.2-.5.1-.8c-.2-1.2-1.2-1.4-1.2-1.4s.3.7.1 1a2 2 0 0 1-.6.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M215.2 227.6s-.4-.7-1.3-.6c-.8 0-1.4.8-1.4.8h1.2c.3.3.4 1 .4 1l.7-.6a7.2 7.2 0 0 0 .4-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M215.2 227.6s-.4-.7-1.3-.6c-.8 0-1.4.8-1.4.8h1.2c.3.3.4 1 .4 1l.7-.6a7.2 7.2 0 0 0 .4-.6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M214.2 230.6s-.8.1-1.2.6c-.4.5-.3 1.3-.3 1.3s.4-.5.9-.5l1 .2-.1-.8-.3-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M214.2 230.6s-.8.1-1.2.6c-.4.5-.3 1.3-.3 1.3s.4-.5.9-.5l1 .2-.1-.8-.3-.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { d: "m228.2 230.5.3-.5.3.5h-.7" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m228.2 230.5.3-.5.3.5h-.7" }),
  /* @__PURE__ */ jsx21("path", { d: "m229 230.5.3-.5.4.5h-.8" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m229 230.5.3-.5.4.5h-.8" }),
  /* @__PURE__ */ jsx21("path", { d: "m228.6 227.3.8.3-.7.4-.1-.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m228.6 227.3.8.3-.7.4-.1-.6" }),
  /* @__PURE__ */ jsx21("path", { d: "m229.5 227.6.7.2-.5.4-.2-.6" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m229.5 227.6.7.2-.5.4-.2-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M224.2 233.7s-.7.2-1 .6c-.4.5-.3 1-.3 1s.6-.5 1.5-.3l1.2.3 1.3-.3s-.7.8-.7 1.3l.2 1.1c0 .7-.6 1.6-.6 1.6l1-.3a4.6 4.6 0 0 0 1.7-.8l.9-1s-.2 1 0 1.4l.2 1.6.8-.6c.2-.1.7-.4.9-.7l.3-1s0 .8.4 1.3l.6 1.6s.3-.8.6-1.1c.3-.4.7-.8.7-1a4.3 4.3 0 0 0-.1-.9l.4.8m-11 .6s.5-.8 1-1l1.1-.8.9-.4m1 5 1.3-.8a4 4 0 0 0 1-1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M216.6 240.4s-.4-.5-1.1-.3c-.7 0-1.2.9-1.2.9s.6-.2 1-.1.6.4.6.4l.4-.4.3-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M216.6 240.4s-.4-.5-1.1-.3c-.7 0-1.2.9-1.2.9s.6-.2 1-.1.6.4.6.4l.4-.4.3-.6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M215.8 243.2s-.6 0-1.1.3c-.5.4-.5 1.2-.5 1.2s.4-.4.8-.3l.9.2v-.6c.2-.4-.1-.8-.1-.8" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M215.8 243.2s-.6 0-1.1.3c-.5.4-.5 1.2-.5 1.2s.4-.4.8-.3l.9.2v-.6c.2-.4-.1-.8-.1-.8z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M217.2 245.8s0 .8.3 1.3c.4.5 1.1.5 1.1.5l-.3-.7c0-.4.3-.8.3-.8s-.3-.3-.7-.3h-.7" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M217.2 245.8s0 .8.3 1.3c.4.5 1.1.5 1.1.5l-.3-.7c0-.4.3-.8.3-.8s-.3-.3-.7-.3h-.7zm16 1.3s2 1.2 1.9 2.2c0 1-1 2.3-1 2.3"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M224.2 252.6s-.4-.6-1.1-.6c-.7 0-1.4.7-1.4.7s.8-.1 1 .2l.5.6.5-.3.5-.6" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M224.2 252.6s-.4-.6-1.1-.6c-.7 0-1.4.7-1.4.7s.8-.1 1 .2l.5.6.5-.3.5-.6z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M222.2 255.3s-1-.1-1.4.3c-.4.5-.4 1.3-.4 1.3s.6-.6 1-.5c.5 0 1 .3 1 .3v-.7l-.3-.7" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M222.2 255.3s-1-.1-1.4.3c-.4.5-.4 1.3-.4 1.3s.6-.6 1-.5c.5 0 1 .3 1 .3v-.7l-.3-.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M224 258.1s-.3.7 0 1.1c.3.5 1 .8 1 .8s-.3-.4-.2-.8c.1-.3.7-.8.7-.8l-1.4-.2" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M224 258.1s-.3.7 0 1.1c.3.5 1 .8 1 .8s-.3-.4-.2-.8c.1-.3.7-.8.7-.8l-1.4-.2z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M236 259.3s-.8-.2-1.2 0c-.5.3-.8 1.4-.8 1.4s.7-.6 1.2-.5c.5 0 1 .3 1 .3v-.8l-.2-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M236 259.3s-.8-.2-1.2 0c-.5.3-.8 1.4-.8 1.4s.7-.6 1.2-.5c.5 0 1 .3 1 .3v-.8l-.2-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M236.4 262.2s-.6.6-.4 1.1l.6 1s0-.7.2-1l1-.3-.7-.5a15.8 15.8 0 0 1-.7-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M236.4 262.2s-.6.6-.4 1.1l.6 1s0-.7.2-1l1-.3-.7-.5a15.8 15.8 0 0 1-.7-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#db4446", d: "M239.4 263s-.3.8.2 1.3c.6.5 1 .5 1 .5s-.3-.7-.2-1.1c.1-.5.5-.7.5-.7l-.8-.2-.7.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M239.4 263s-.3.8.2 1.3c.6.5 1 .5 1 .5s-.3-.7-.2-1.1c.1-.5.5-.7.5-.7l-.8-.2-.7.3z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ffd691",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M208.8 316.4c2 .6 3 2 3 3.8 0 2.3-2.2 4-5 4-3 0-5.3-1.7-5.3-4 0-1.7 1-3.6 3-3.8l-.2-.4-.7-.7h1.2l.8.5.5-.7c.3-.4.6-.5.6-.5l.6.6.3.5.7-.4.8-.3s0 .4-.2.7l-.1.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M206.3 326.7s-3.8-2.6-5.5-3c-2-.4-4.5 0-5.5 0 0 0 1.2.8 1.8 1.4.5.5 2.3 1.5 3.3 1.8 3 .8 6-.2 6-.2m1 .2s2.4-2.5 5-2.9c3-.4 5 .3 6.2.6l-1.5.8c-.5.3-2 1.5-4 1.6-2 0-4.4-.3-4.8-.2l-.9.1"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M206.7 323.8a4.8 4.8 0 0 1 0-7.1 4.8 4.8 0 0 1 1.5 3.5 4.9 4.9 0 0 1-1.5 3.6"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#058e6e",
      stroke: "#000",
      strokeWidth: 0.5,
      d: "M205.7 329s.6-1.5.6-2.7l-.1-2.1h.8s.3 1.1.3 2l-.1 2.4-.7.1-.8.3"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M254 190.7c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M254 190.7c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M255.4 188.2c0-.6.5-1 1.1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M255.4 188.2c0-.6.5-1 1.1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M256.4 185.2c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M256.4 185.2c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M256.5 182c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M256.5 182c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M255.7 179c0-.6.5-1 1-1 .7 0 1.2.4 1.2 1s-.5 1-1.1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M255.7 179c0-.6.5-1 1-1 .7 0 1.2.4 1.2 1s-.5 1-1.1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M254.1 176.1c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M254.1 176.1c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M252 173.8c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M252 173.8c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M249.4 171.8c0-.5.5-1 1.1-1a1 1 0 0 1 0 2c-.6 0-1-.4-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M249.4 171.8c0-.5.5-1 1.1-1a1 1 0 0 1 0 2c-.6 0-1-.4-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M246.5 170.3c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M246.5 170.3c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M243.3 169.1c0-.5.5-1 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M243.3 169.1c0-.5.5-1 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M239.9 168.5c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M239.9 168.5c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M236.6 168.3c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M236.6 168.3c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M233.3 168.5c0-.6.5-1 1-1 .7 0 1.1.4 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M233.3 168.5c0-.6.5-1 1-1 .7 0 1.1.4 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M230.1 168.5c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M230.1 168.5c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M231.7 171.2c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1m.6 3.1c0-.6.4-1 1-1s1 .4 1 1c0 .5-.4 1-1 1a1 1 0 0 1-1-1m0 3c0-.5.6-1 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1m-1 2.8c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1 0 .6-.4 1-1 1a1 1 0 0 1-1-1m-1.9 2.6c0-.5.5-1 1-1 .7 0 1.2.5 1.2 1s-.5 1-1.1 1c-.6 0-1-.4-1-1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M227.6 166.5c0-.5.5-1 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M227.6 166.5c0-.5.5-1 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M224.8 165c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M224.8 165c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M221.6 164c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1 0 .5-.5 1-1 1-.6 0-1.1-.5-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M221.6 164c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1 0 .5-.5 1-1 1-.6 0-1.1-.5-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M218.3 163.4c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M218.3 163.4c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M215 163.5c0-.6.5-1 1.1-1 .6 0 1 .4 1 1 0 .5-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M215 163.5c0-.6.5-1 1.1-1 .6 0 1 .4 1 1 0 .5-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M211.7 164c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M211.7 164c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M208.6 165.1c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M208.6 165.1c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M156 190.7c0-.5.4-1 1-1s1 .5 1 1c0 .6-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M156 190.7c0-.5.4-1 1-1s1 .5 1 1c0 .6-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M154.5 188.2c0-.6.5-1 1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M154.5 188.2c0-.6.5-1 1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M153.5 185.2c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M153.5 185.2c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M153.4 182c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M153.4 182c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M154.2 179c0-.6.5-1 1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M154.2 179c0-.6.5-1 1-1 .6 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M155.8 176.1c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M155.8 176.1c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1s-.5 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M158 173.8c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M158 173.8c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M160.5 171.8c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M160.5 171.8c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M163.5 170.3c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M163.5 170.3c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M166.6 169.1c0-.5.5-1 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M166.6 169.1c0-.5.5-1 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M170 168.5c0-.5.5-1 1.1-1a1 1 0 0 1 0 2c-.6 0-1-.4-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M170 168.5c0-.5.5-1 1.1-1a1 1 0 0 1 0 2c-.6 0-1-.4-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M173.4 168.3c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M173.4 168.3c0-.5.4-1 1-1s1 .5 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M176.6 168.5c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M176.6 168.5c0-.6.5-1 1-1 .6 0 1.1.4 1.1 1s-.5 1-1 1a1 1 0 0 1-1.1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M179.8 168.5c0-.6.5-1 1-1 .7 0 1.2.4 1.2 1s-.5 1-1.1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M179.8 168.5c0-.6.5-1 1-1 .7 0 1.2.4 1.2 1s-.5 1-1.1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#fff",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M178.2 171.2c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1m-.7 3.1c0-.6.4-1 1-1s1 .4 1 1c0 .5-.4 1-1 1a1 1 0 0 1-1-1m-.2 3c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1m.9 2.8c0-.5.5-1 1-1 .6 0 1.1.5 1.1 1 0 .6-.5 1-1 1a1 1 0 0 1-1.1-1m1.8 2.6c0-.5.5-1 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M182.3 166.5c0-.5.5-1 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M182.3 166.5c0-.5.5-1 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M185.2 165c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.4, d: "M185.2 165c0-.6.4-1 1-1s1 .4 1 1-.4 1-1 1a1 1 0 0 1-1-1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M188.3 164c0-.6.5-1 1-1 .7 0 1.1.4 1.1 1 0 .5-.4 1-1 1s-1-.5-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M188.3 164c0-.6.5-1 1-1 .7 0 1.1.4 1.1 1 0 .5-.4 1-1 1s-1-.5-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M191.6 163.4c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M191.6 163.4c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M194.9 163.5c0-.6.4-1 1-1s1 .4 1 1c0 .5-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M194.9 163.5c0-.6.4-1 1-1s1 .4 1 1c0 .5-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M198.2 164c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M198.2 164c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#fff", d: "M201.3 165.1c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M201.3 165.1c0-.5.5-1 1-1 .7 0 1.1.5 1.1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M174.7 228.9h-1v-1h-1.5v3.6h1.6v2.5h-3.4v7h1.8v14.3h-3.5v7.3h27.2v-7.3h-3.5V241h1.8v-7h-3.4v-2.5h1.6V228h-1.6v.9h-.8v-1h-1.6v1h-1.1v-1h-1.6v3.6h1.6v2.5H184v-7.8h1.7v-3.5H184v.9h-1v-1h-1.5v1h-.9v-1H179v3.6h1.7v7.8h-3.3v-2.5h1.6V228h-1.6v.9h-.9v-1h-1.8v1zm-6 33.7H196m-27.3-1.8H196m-27.3-1.8H196m-27.3-1.7H196m-27.3-2H196m-23.8-1.6h20.2m-20.2-1.8h20.2m-20.2-2h20.2m-20.2-1.7h20.2m-20.2-1.8h20.2m-20.2-1.8h20.2m-20.2-1.7h20.2m-22-1.8h23.8m-23.8-1.8h23.8m-23.8-1.8h23.8m-23.8-1.8h23.8m-20.4-1.7h17m-10.2-1.8h3.4m-3.4-1.8h3.4m-3.4-1.8h3.4m-3.4-1.7h3.4m-5.1-2.2h6.8m-12 7.5h3.6m-5-2.2h6.6m-6.7 32.6v-1.8m0-1.8v-1.7m-1.8 1.7v1.8m3.4 0V259m1.7 3.6v-1.8m0-1.8v-1.7m0-2v-1.6m0-1.8v-2m-1.7 7.4v-2m-3.4 2v-2m7 0v2m1.5-2v-1.6m-5.1-1.8v1.8m3.5-1.8v1.8m3.3-1.8v1.8M179 252v-2m1.7-1.7v1.7m0-5.3v1.8m-1.7-3.6v1.8m1.7-3.5v1.7m-3.3-1.7v1.7m-3.5-1.7v1.7m-1.6-3.5v1.8m3.3-1.8v1.8m3.4-1.8v1.8m1.7-3.6v1.8m-3.3-1.8v1.8m-3.5-1.8v1.8m-1.6-3.6v1.8m6.7-1.8v1.8m-3.4-5.3v1.8m15.3-1.8h-3.5m5-2.2h-6.6m6.7 32.6v-1.8m0-1.8v-1.7m1.8 1.7v1.8m-3.4 0V259m-1.7 3.6v-1.8m0-1.8v-1.7m0-2v-1.6m0-1.8v-2m1.7 7.4v-2m3.4 2v-2m-7 0v2m-1.5-2v-1.6m5.1-1.8v1.8m-3.5-1.8v1.8m-3.3-1.8v1.8m1.7-1.8v-2m-1.7-1.7v1.7m0-5.3v1.8m1.7-3.6v1.8m-1.7-3.5v1.7m3.3-1.7v1.7m3.5-1.7v1.7m1.6-3.5v1.8m-3.3-1.8v1.8m-3.4-1.8v1.8m-1.7-3.6v1.8m3.3-1.8v1.8m3.5-1.8v1.8m1.6-3.6v1.8m-6.7-1.8v1.8m3.4-5.3v1.8m-7 18v-2m0-5.4v-1.8m0 5.4v-1.8m0-5.3v-1.8m0-1.8v-1.7m0-3.6v-1.8m0-1.7v-1.8m-8.3 4.6h3.5m3.3-5.3h3.4m3.3 5.3h3.5"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "M186.8 262.6v-4.7c0-.8-.4-3.5-4.6-3.5-4 0-4.4 2.7-4.4 3.5v4.7h9z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      stroke: "#000",
      strokeWidth: 0.4,
      d: "m179.3 258.2-2.2-.3c0-.9.2-2.2.9-2.6l2 1.5c-.3.2-.7 1-.7 1.4zm6 0 2.2-.3c0-.9-.2-2.2-.9-2.6l-2 1.5c.3.2.7 1 .7 1.4zm-2.2-2.3 1-2a5.3 5.3 0 0 0-2-.4l-1.7.4 1.1 2h1.6zm-4.2-5.5v-4.9c0-1.3-1-2.4-2.5-2.4s-2.4 1-2.4 2.4v4.9h4.9zm6.8 0v-4.9c0-1.3 1-2.4 2.5-2.4s2.4 1 2.4 2.4v4.9h-4.9zm-1.7-12 .4-4.4h-4.2l.2 4.4h3.6zm3.3 0-.4-4.4h4.4l-.5 4.4h-3.5zm-10 0 .2-4.4h-4.2l.5 4.4h3.5z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#0039f0",
      d: "M185.3 262.6v-4c0-.7-.5-2.7-3.1-2.7-2.4 0-2.9 2-2.9 2.7v4h6zm-6.9-12.7v-4.2c0-1-.6-2.2-2-2.2s-2 1.1-2 2.2v4.3h4zm7.8 0v-4.2c0-1 .7-2.2 2-2.2s2 1.1 2 2.2v4.3h-4z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#ad1519",
      d: "M190.8 269.8c0-9.7 7-17.6 15.6-17.6s15.6 7.9 15.6 17.6-7 17.5-15.6 17.5-15.6-7.8-15.6-17.5"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.6,
      d: "M190.8 269.8c0-9.7 7-17.6 15.6-17.6s15.6 7.9 15.6 17.6-7 17.5-15.6 17.5-15.6-7.8-15.6-17.5z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#005bbf", d: "M195.4 269.7c0-7 5-12.8 11-12.8s11 5.7 11 12.8c0 7.2-5 13-11 13s-11-5.8-11-13" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.6,
      d: "M195.4 269.7c0-7 5-12.8 11-12.8s11 5.7 11 12.8c0 7.2-5 13-11 13s-11-5.8-11-13z"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M201.2 260.9s-1.3 1.4-1.3 2.7a6 6 0 0 0 .6 2.4c-.2-.5-.8-.8-1.4-.8-.8 0-1.4.6-1.4 1.3l.2.8.5.9c.1-.3.5-.5 1-.5s1 .4 1 1a.9.9 0 0 1 0 .2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1a.9.9 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .7.3 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3c-.7 0-1.2.3-1.4.9 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M201.2 260.9s-1.3 1.4-1.3 2.7a6 6 0 0 0 .6 2.4c-.2-.5-.8-.8-1.4-.8-.8 0-1.4.6-1.4 1.3l.2.8.5.9c.1-.3.5-.5 1-.5s1 .4 1 1a.9.9 0 0 1 0 .2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1a.9.9 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .7.3 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3c-.7 0-1.2.3-1.4.9 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M199.2 269.9h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M199.2 269.9h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M211.4 260.9s-1.3 1.4-1.3 2.7c0 1.3.6 2.4.6 2.4-.2-.5-.7-.8-1.4-.8-.8 0-1.4.6-1.4 1.3l.2.8.5.9c.2-.3.5-.5 1-.5a1 1 0 0 1 1 1 .9.9 0 0 1 0 .2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1a.8.8 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .8.3 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3c-.6 0-1.2.3-1.4.9 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M211.4 260.9s-1.3 1.4-1.3 2.7c0 1.3.6 2.4.6 2.4-.2-.5-.7-.8-1.4-.8-.8 0-1.4.6-1.4 1.3l.2.8.5.9c.2-.3.5-.5 1-.5a1 1 0 0 1 1 1 .9.9 0 0 1 0 .2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1a.8.8 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .8.3 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3c-.6 0-1.2.3-1.4.9 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M209.4 269.9h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M209.4 269.9h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M206.3 269.6s-1.3 1.5-1.3 2.8.6 2.4.6 2.4c-.2-.5-.7-.9-1.4-.9-.8 0-1.4.6-1.4 1.4l.2.7.5 1c.1-.4.5-.6 1-.6a1 1 0 0 1 1 1 .9.9 0 0 1 0 .3h-1.2v1h1l-.8 1.5 1-.4.8.9.8-1 1 .5-.7-1.5h1v-1h-1.1a.9.9 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .7.2.9.6l.5-1 .2-.7a1.4 1.4 0 0 0-1.4-1.4c-.7 0-1.2.4-1.4 1 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeLinejoin: "round",
      strokeWidth: 0.3,
      d: "M206.3 269.6s-1.3 1.5-1.3 2.8.6 2.4.6 2.4c-.2-.5-.7-.9-1.4-.9-.8 0-1.4.6-1.4 1.4l.2.7.5 1c.1-.4.5-.6 1-.6a1 1 0 0 1 1 1 .9.9 0 0 1 0 .3h-1.2v1h1l-.8 1.5 1-.4.8.9.8-1 1 .5-.7-1.5h1v-1h-1.1a.9.9 0 0 1 0-.3 1 1 0 0 1 1-1c.4 0 .7.2.9.6l.5-1 .2-.7a1.4 1.4 0 0 0-1.4-1.4c-.7 0-1.2.4-1.4 1 0 0 .6-1.2.6-2.5s-1.4-2.7-1.4-2.7z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M204.3 278.6h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M204.3 278.6h4.1v-1h-4.1v1z" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "#c8b100",
      d: "M237.6 223.4h-.3a1.5 1.5 0 0 1-.3.4c-.2.2-.6.2-.8 0a.5.5 0 0 1-.1-.4.5.5 0 0 1-.5 0c-.3-.1-.3-.5-.1-.7v-.5h-.3l-.1.2c-.2.3-.5.3-.7.2a.6.6 0 0 1 0-.2h-.3c-.5.2-.7-1-.7-1.2l-.2.2s.2.7.1 1.2c0 .6-.3 1.2-.3 1.2a9 9 0 0 1 2.9 1.6 9 9 0 0 1 2.2 2.3l1.2-.5c.6-.2 1.3-.2 1.3-.2l.2-.2c-.3 0-1.5.1-1.5-.4v-.2a.7.7 0 0 1-.2 0c-.2-.2-.2-.4 0-.7l.2-.1v-.3h-.3l-.2.1c-.2.3-.6.3-.8 0a.4.4 0 0 1-.1-.4.6.6 0 0 1-.5 0c-.2-.2-.3-.5 0-.8l.2-.3v-.3"
    }
  ),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M237.6 223.4h-.3a1.5 1.5 0 0 1-.3.4c-.2.2-.6.2-.8 0a.5.5 0 0 1-.1-.4.5.5 0 0 1-.5 0c-.3-.1-.3-.5-.1-.7v-.5h-.3l-.1.2c-.2.3-.5.3-.7.2a.6.6 0 0 1 0-.2h-.3c-.5.2-.7-1-.7-1.2l-.2.2s.2.7.1 1.2c0 .6-.3 1.2-.3 1.2a9 9 0 0 1 2.9 1.6 9 9 0 0 1 2.2 2.3l1.2-.5c.6-.2 1.3-.2 1.3-.2l.2-.2c-.3 0-1.5.1-1.5-.4v-.2a.7.7 0 0 1-.2 0c-.2-.2-.2-.4 0-.7l.2-.1v-.3h-.3l-.2.1c-.2.3-.6.3-.8 0a.4.4 0 0 1-.1-.4.6.6 0 0 1-.5 0c-.2-.2-.3-.5 0-.8l.2-.3v-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { d: "M235.4 224h.2v.3h-.1c-.1 0-.1-.2 0-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M235.4 224h.2v.3h-.1c-.1 0-.1-.2 0-.2z" }),
  /* @__PURE__ */ jsx21("path", { d: "m236.3 224.8-.3-.2v-.2h.1l.4.3.3.2v.2h-.2l-.3-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "m236.3 224.8-.3-.2v-.2h.1l.4.3.3.2v.2h-.2l-.3-.3" }),
  /* @__PURE__ */ jsx21("path", { d: "m234.6 223.7-.2-.2s-.1 0 0-.1l.3.1.3.1v.2h-.1l-.3-.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "m234.6 223.7-.2-.2s-.1 0 0-.1l.3.1.3.1v.2h-.1l-.3-.1" }),
  /* @__PURE__ */ jsx21("path", { d: "M233.7 223h.2v.2h-.2s-.1-.1 0-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M233.7 223h.2v.2h-.2s-.1-.1 0-.2z" }),
  /* @__PURE__ */ jsx21("path", { d: "M237.3 225.5v-.2h-.3l.1.2h.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M237.3 225.5v-.2h-.3l.1.2h.2z" }),
  /* @__PURE__ */ jsx21("path", { d: "m237.9 226.2.2.2h.1c.1 0 0-.1 0-.2l-.2-.2-.2-.2h-.1v.2l.2.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "m237.9 226.2.2.2h.1c.1 0 0-.1 0-.2l-.2-.2-.2-.2h-.1v.2l.2.2" }),
  /* @__PURE__ */ jsx21("path", { d: "M238.8 227v-.3h-.3v.2h.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.1, d: "M238.8 227v-.3h-.3v.2h.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M236.2 221.1h-.6l-.1.9v.1h.2l.7-.5-.3-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M236.2 221.1h-.6l-.1.9v.1h.2l.7-.5-.3-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M234.6 221.6v.5l.9.1h.1v-.2l-.5-.7-.5.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M234.6 221.6v.5l.9.1h.1v-.2l-.5-.7-.5.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m236.4 222.6-.4.3-.6-.7v-.1h1.1v.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m236.4 222.6-.4.3-.6-.7v-.1h1.1v.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M235.3 222a.3.3 0 0 1 .4 0 .3.3 0 0 1 0 .3.3.3 0 0 1-.3 0 .3.3 0 0 1-.1-.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M235.3 222a.3.3 0 0 1 .4 0 .3.3 0 0 1 0 .3.3.3 0 0 1-.3 0 .3.3 0 0 1-.1-.3z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m233.2 221.1-.2-.7-.4-.4s.4-.2.8.1c.4.3 0 .9 0 .9l-.2.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m233.2 221.1-.2-.7-.4-.4s.4-.2.8.1c.4.3 0 .9 0 .9l-.2.1z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m234.2 221.4-.4.4-.6-.6v-.2h1v.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m234.2 221.4-.4.4-.6-.6v-.2h1v.4" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m233.1 221 .3-.1v.3c0 .2-.1.2-.2.2l-.1-.3" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m233.1 221 .3-.1v.3c0 .2-.1.2-.2.2l-.1-.3z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M238.3 222.5h-.5l-.3.7v.2h.2l.8-.4-.2-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M238.3 222.5h-.5l-.3.7v.2h.2l.8-.4-.2-.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M236.7 222.8v.5l.8.2h.1v-.2l-.4-.7-.5.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M236.7 222.8v.5l.8.2h.1v-.2l-.4-.7-.5.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m238.4 224-.5.2-.4-.7v-.2h.1l.9.2-.1.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m238.4 224-.5.2-.4-.7v-.2h.1l.9.2-.1.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M237.3 223.2h.4a.3.3 0 0 1 0 .4.3.3 0 0 1-.3 0 .3.3 0 0 1 0-.4" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M237.3 223.2h.4a.3.3 0 0 1 0 .4.3.3 0 0 1-.3 0 .3.3 0 0 1 0-.4z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m240.2 224.3.1.5-.8.3h-.2v-.2l.4-.8.5.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m240.2 224.3.1.5-.8.3h-.2v-.2l.4-.8.5.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m240 225.8-.5.1-.3-.8v-.1h.2l.8.3-.1.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m240 225.8-.5.1-.3-.8v-.1h.2l.8.3-.1.5" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m238.6 224.3-.2.5.9.3h.1v-.1l-.3-.8-.5.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m238.6 224.3-.2.5.9.3h.1v-.1l-.3-.8-.5.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M239.5 225.2a.3.3 0 0 0 0-.3.3.3 0 0 0-.4 0 .3.3 0 0 0 0 .3.3.3 0 0 0 .4 0" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M239.5 225.2a.3.3 0 0 0 0-.3.3.3 0 0 0-.4 0 .3.3 0 0 0 0 .3.3.3 0 0 0 .4 0z"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M240.8 227h.8l.5.3s.1-.4-.3-.7c-.3-.3-.8.2-.8.2l-.2.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "M240.8 227h.8l.5.3s.1-.4-.3-.7c-.3-.3-.8.2-.8.2l-.2.2z" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "m240.3 226.1-.3.5.8.5v-.1h.2l-.1-1-.6.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.3, d: "m240.3 226.1-.3.5.8.5v-.1h.2l-.1-1-.6.1" }),
  /* @__PURE__ */ jsx21("path", { fill: "#c8b100", d: "M241 227s.1-.1 0-.2h-.3c-.2 0-.2.1-.1.2h.3" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.3,
      d: "M241 227s.1-.1 0-.2h-.3c-.2 0-.2.1-.1.2h.3zm38-21.9v.6h-2.4v-.6h1v-1.3h-.7v-.5h.6v-.6h.6v.6h.6v.6h-.6v1.2h1"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", d: "M134.4 217.1v-1.2m-.4 1.2v-1.2m-.2 1.2v-1.2m-.3 1.2v-1.2" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.1,
      d: "M133.2 217.1v-1.2m-.5 1.1v-1m.2 1v-1m-.7 1v-1m.2 1v-1m-.9 1v-1m.2 1v-1m.3 1v-1m-.7 1v-1m-.3.9v-.8m-.1.8v-.8m-.5.7v-.6m.2.6v-.6m-.4.5v-.5m-.2.5v-.4m-.3.3v-.3m-.3.3v-.2"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.2, d: "M129.2 216.6v-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", d: "M135.7 217v-1m-.5 1v-1m-.4 1.2V216m143 1.1V216m-.4 1.1V216m-.3 1.1V216m-.3 1.2V216" }),
  /* @__PURE__ */ jsx21(
    "path",
    {
      fill: "none",
      stroke: "#000",
      strokeWidth: 0.1,
      d: "M276.6 217.1V216m-.6 1v-1m.3 1v-1m-.8 1v-1m.3 1v-1m-.9 1v-1m.2 1v-1m.2 1v-1m-.6 1v-1m-.3.9v-.8m-.2.8v-.8m-.4.7v-.6m.2.6v-.6m-.5.6v-.6m-.2.5v-.4m-.3.4v-.4m-.2.3v-.2"
    }
  ),
  /* @__PURE__ */ jsx21("path", { fill: "none", stroke: "#000", strokeWidth: 0.2, d: "M272.6 216.6v-.2" }),
  /* @__PURE__ */ jsx21("path", { fill: "none", d: "M279.1 217v-1m-.6 1v-1m-.4 1.1V216" })
] })), "SvgEs");
var es_default = SvgEs;

// src/icons/locales/fi.tsx
import { jsx as jsx22, jsxs as jsxs15 } from "react/jsx-runtime";
var SvgFi = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs15("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx22("path", { fill: "#fff", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx22("path", { fill: "#002f6c", d: "M0 174.5h640v131H0z" }),
  /* @__PURE__ */ jsx22("path", { fill: "#002f6c", d: "M175.5 0h130.9v480h-131z" })
] })), "SvgFi");
var fi_default = SvgFi;

// src/icons/locales/fr.tsx
import { jsx as jsx23, jsxs as jsxs16 } from "react/jsx-runtime";
var SvgFr = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx23("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs16("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx23("path", { fill: "#fff", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx23("path", { fill: "#002654", d: "M0 0h213.3v480H0z" }),
  /* @__PURE__ */ jsx23("path", { fill: "#ce1126", d: "M426.7 0H640v480H426.7z" })
] }) })), "SvgFr");
var fr_default = SvgFr;

// src/icons/locales/gb.tsx
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
var SvgGb = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs17("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx24("path", { fill: "#012169", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx24("path", { fill: "#FFF", d: "m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z" }),
  /* @__PURE__ */ jsx24(
    "path",
    {
      fill: "#C8102E",
      d: "m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"
    }
  ),
  /* @__PURE__ */ jsx24("path", { fill: "#FFF", d: "M241 0v480h160V0H241zM0 160v160h640V160H0z" }),
  /* @__PURE__ */ jsx24("path", { fill: "#C8102E", d: "M0 193v96h640v-96H0zM273 0v480h96V0h-96z" })
] })), "SvgGb");
var gb_default = SvgGb;

// src/icons/locales/ie.tsx
import { jsx as jsx25, jsxs as jsxs18 } from "react/jsx-runtime";
var SvgIe = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx25("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs18("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx25("path", { fill: "#fff", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx25("path", { fill: "#009A49", d: "M0 0h213.3v480H0z" }),
  /* @__PURE__ */ jsx25("path", { fill: "#FF7900", d: "M426.7 0H640v480H426.7z" })
] }) })), "SvgIe");
var ie_default = SvgIe;

// src/icons/locales/it.tsx
import { jsx as jsx26, jsxs as jsxs19 } from "react/jsx-runtime";
var SvgIt = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx26("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs19("g", { fillRule: "evenodd", strokeWidth: "1pt", children: [
  /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx26("path", { fill: "#009246", d: "M0 0h213.3v480H0z" }),
  /* @__PURE__ */ jsx26("path", { fill: "#ce2b37", d: "M426.7 0H640v480H426.7z" })
] }) })), "SvgIt");
var it_default = SvgIt;

// src/icons/locales/nl.tsx
import { jsx as jsx27, jsxs as jsxs20 } from "react/jsx-runtime";
var SvgNl = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs20("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx27("path", { fill: "#21468b", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx27("path", { fill: "#fff", d: "M0 0h640v320H0z" }),
  /* @__PURE__ */ jsx27("path", { fill: "#ae1c28", d: "M0 0h640v160H0z" })
] })), "SvgNl");
var nl_default = SvgNl;

// src/icons/locales/no.tsx
import { jsx as jsx28, jsxs as jsxs21 } from "react/jsx-runtime";
var SvgNo = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs21("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx28("path", { fill: "#ed2939", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx28("path", { fill: "#fff", d: "M180 0h120v480H180z" }),
  /* @__PURE__ */ jsx28("path", { fill: "#fff", d: "M0 180h640v120H0z" }),
  /* @__PURE__ */ jsx28("path", { fill: "#002664", d: "M210 0h60v480h-60z" }),
  /* @__PURE__ */ jsx28("path", { fill: "#002664", d: "M0 210h640v60H0z" })
] })), "SvgNo");
var no_default = SvgNo;

// src/icons/locales/nz.tsx
import { jsx as jsx29, jsxs as jsxs22 } from "react/jsx-runtime";
var SvgNz = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs22("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx29("defs", { children: /* @__PURE__ */ jsxs22("g", { id: "nz_svg__b", children: [
    /* @__PURE__ */ jsxs22("g", { id: "nz_svg__a", children: [
      /* @__PURE__ */ jsx29("path", { d: "M0-.3v.5l1-.5z" }),
      /* @__PURE__ */ jsx29("path", { d: "M.213.256-.081-.15l1.103-.183z" })
    ] }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__a", transform: "scale(-1 1)" }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__a", transform: "rotate(72 0 0)" }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__a", transform: "rotate(-72 0 0)" }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__a", transform: "scale(-1 1) rotate(72)" })
  ] }) }),
  /* @__PURE__ */ jsx29("path", { fill: "#00247d", fillRule: "evenodd", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsxs22("g", { transform: "translate(-111 36.1) scale(.66825)", children: [
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__b", width: "100%", height: "100%", fill: "#fff", transform: "matrix(45.4 0 0 45.4 900 120)" }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__b", width: "100%", height: "100%", fill: "#cc142b", transform: "matrix(30 0 0 30 900 120)" })
  ] }),
  /* @__PURE__ */ jsxs22("g", { transform: "rotate(82 525.2 114.6) scale(.66825)", children: [
    /* @__PURE__ */ jsx29(
      "use",
      {
        xlinkHref: "#nz_svg__b",
        width: "100%",
        height: "100%",
        fill: "#fff",
        transform: "rotate(-82 519 -457.7) scale(40.4)"
      }
    ),
    /* @__PURE__ */ jsx29(
      "use",
      {
        xlinkHref: "#nz_svg__b",
        width: "100%",
        height: "100%",
        fill: "#cc142b",
        transform: "rotate(-82 519 -457.7) scale(25)"
      }
    )
  ] }),
  /* @__PURE__ */ jsxs22("g", { transform: "rotate(82 525.2 114.6) scale(.66825)", children: [
    /* @__PURE__ */ jsx29(
      "use",
      {
        xlinkHref: "#nz_svg__b",
        width: "100%",
        height: "100%",
        fill: "#fff",
        transform: "rotate(-82 668.6 -327.7) scale(45.4)"
      }
    ),
    /* @__PURE__ */ jsx29(
      "use",
      {
        xlinkHref: "#nz_svg__b",
        width: "100%",
        height: "100%",
        fill: "#cc142b",
        transform: "rotate(-82 668.6 -327.7) scale(30)"
      }
    )
  ] }),
  /* @__PURE__ */ jsxs22("g", { transform: "translate(-111 36.1) scale(.66825)", children: [
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__b", width: "100%", height: "100%", fill: "#fff", transform: "matrix(50.4 0 0 50.4 900 480)" }),
    /* @__PURE__ */ jsx29("use", { xlinkHref: "#nz_svg__b", width: "100%", height: "100%", fill: "#cc142b", transform: "matrix(35 0 0 35 900 480)" })
  ] }),
  /* @__PURE__ */ jsx29("path", { fill: "#012169", d: "M0 0h320v240H0z" }),
  /* @__PURE__ */ jsx29(
    "path",
    {
      fill: "#fff",
      d: "m37.5 0 122 90.5L281 0h39v31l-120 89.5 120 89V240h-40l-120-89.5L40.5 240H0v-30l119.5-89L0 32V0z"
    }
  ),
  /* @__PURE__ */ jsx29(
    "path",
    {
      fill: "#c8102e",
      d: "M212 140.5 320 220v20l-135.5-99.5zm-92 10 3 17.5-96 72H0zM320 0v1.5l-124.5 94 1-22L295 0zM0 0l119.5 88h-30L0 21z"
    }
  ),
  /* @__PURE__ */ jsx29("path", { fill: "#fff", d: "M120.5 0v240h80V0zM0 80v80h320V80z" }),
  /* @__PURE__ */ jsx29("path", { fill: "#c8102e", d: "M0 96.5v48h320v-48zM136.5 0v240h48V0z" })
] })), "SvgNz");
var nz_default = SvgNz;

// src/icons/locales/pl.tsx
import { jsx as jsx30, jsxs as jsxs23 } from "react/jsx-runtime";
var SvgPl = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx30("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs23("g", { fillRule: "evenodd", children: [
  /* @__PURE__ */ jsx30("path", { fill: "#fff", d: "M640 480H0V0h640z" }),
  /* @__PURE__ */ jsx30("path", { fill: "#dc143c", d: "M640 480H0V240h640z" })
] }) })), "SvgPl");
var pl_default = SvgPl;

// src/icons/locales/se.tsx
import { jsx as jsx31, jsxs as jsxs24 } from "react/jsx-runtime";
var SvgSe = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs24("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: [
  /* @__PURE__ */ jsx31("path", { fill: "#005293", d: "M0 0h640v480H0z" }),
  /* @__PURE__ */ jsx31("path", { fill: "#fecb00", d: "M176 0v192H0v96h176v192h96V288h368v-96H272V0h-96z" })
] })), "SvgSe");
var se_default = SvgSe;

// src/icons/locales/us.tsx
import { jsx as jsx32, jsxs as jsxs25 } from "react/jsx-runtime";
var SvgUs = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx32("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 640 480" }, props), { children: /* @__PURE__ */ jsxs25("g", { fillRule: "evenodd", children: [
  /* @__PURE__ */ jsxs25("g", { strokeWidth: "1pt", children: [
    /* @__PURE__ */ jsx32(
      "path",
      {
        fill: "#bd3d44",
        d: "M0 0h912v37H0zm0 73.9h912v37H0zm0 73.8h912v37H0zm0 73.8h912v37H0zm0 74h912v36.8H0zm0 73.7h912v37H0zM0 443h912v37H0z"
      }
    ),
    /* @__PURE__ */ jsx32(
      "path",
      {
        fill: "#fff",
        d: "M0 37h912v36.9H0zm0 73.8h912v36.9H0zm0 73.8h912v37H0zm0 73.9h912v37H0zm0 73.8h912v37H0zm0 73.8h912v37H0z"
      }
    )
  ] }),
  /* @__PURE__ */ jsx32("path", { fill: "#192f5d", d: "M0 0h364.8v258.5H0z" }),
  /* @__PURE__ */ jsx32(
    "path",
    {
      fill: "#fff",
      d: "m30.4 11 3.4 10.3h10.6l-8.6 6.3 3.3 10.3-8.7-6.4-8.6 6.3L25 27.6l-8.7-6.3h10.9zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.2 10.3-8.6-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.6zm60.8 0 3.3 10.3H166l-8.6 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.7-6.3h10.8zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.4-10.2-8.8-6.3h10.7zm60.8 0 3.3 10.3h10.7l-8.6 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.3 10.3h10.8l-8.8 6.3 3.4 10.3-8.7-6.4-8.7 6.3 3.4-10.2-8.8-6.3h10.8zM60.8 37l3.3 10.2H75l-8.7 6.2 3.2 10.3-8.5-6.3-8.7 6.3 3.1-10.3-8.4-6.2h10.7zm60.8 0 3.4 10.2h10.7l-8.8 6.2 3.4 10.3-8.7-6.3-8.7 6.3 3.3-10.3-8.7-6.2h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.2 3.3 10.3-8.7-6.3-8.7 6.3 3.3-10.3-8.6-6.2H179zm60.8 0 3.4 10.2h10.7l-8.8 6.2 3.4 10.3-8.7-6.3-8.6 6.3 3.2-10.3-8.7-6.2H240zm60.8 0 3.3 10.2h10.8l-8.7 6.2 3.3 10.3-8.7-6.3-8.7 6.3 3.3-10.3-8.6-6.2h10.7zM30.4 62.6 33.8 73h10.6l-8.6 6.3 3.3 10.2-8.7-6.3-8.6 6.3L25 79.3 16.3 73h10.9zm60.8 0L94.5 73h10.8l-8.7 6.3 3.2 10.2-8.6-6.3-8.7 6.3 3.3-10.3-8.6-6.3h10.6zm60.8 0 3.3 10.3H166l-8.6 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.3-10.3-8.7-6.3h10.8zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.4-10.3-8.8-6.3h10.7zm60.8 0 3.3 10.3h10.7l-8.6 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.3-10.3-8.6-6.3h10.7zm60.8 0 3.3 10.3h10.8l-8.8 6.3 3.4 10.2-8.7-6.3-8.7 6.3 3.4-10.3-8.8-6.3h10.8zm-273.6 26 3.3 10.2H75l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.4 10.2h10.7l-8.8 6.3 3.4 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.7-6.3h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3H179zm60.8 0 3.4 10.2h10.7l-8.7 6.3 3.3 10.3-8.7-6.4-8.6 6.3 3.2-10.2-8.7-6.3H240zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zM30.4 114.5l3.4 10.2h10.6l-8.6 6.3 3.3 10.3-8.7-6.4-8.6 6.3L25 131l-8.7-6.3h10.9zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.2 10.2-8.6-6.3-8.7 6.3 3.3-10.2-8.6-6.3h10.6zm60.8 0 3.3 10.2H166l-8.6 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.7-6.3h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.4-10.2-8.8-6.3h10.7zm60.8 0 3.3 10.2h10.7L279 131l3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.3 10.2h10.8l-8.8 6.3 3.4 10.3-8.7-6.4-8.7 6.3L329 131l-8.8-6.3H331zM60.8 140.3l3.3 10.3H75l-8.7 6.2 3.3 10.3-8.7-6.4-8.7 6.4 3.3-10.3-8.6-6.3h10.7zm60.8 0 3.4 10.3h10.7l-8.8 6.2 3.4 10.3-8.7-6.4-8.7 6.4 3.3-10.3-8.7-6.3h10.8zm60.8 0 3.3 10.3h10.8l-8.7 6.2 3.3 10.3-8.7-6.4-8.7 6.4 3.3-10.3-8.6-6.3H179zm60.8 0 3.4 10.3h10.7l-8.7 6.2 3.3 10.3-8.7-6.4-8.6 6.4 3.2-10.3-8.7-6.3H240zm60.8 0 3.3 10.3h10.8l-8.7 6.2 3.3 10.3-8.7-6.4-8.7 6.4 3.3-10.3-8.6-6.3h10.7zM30.4 166.1l3.4 10.3h10.6l-8.6 6.3 3.3 10.1-8.7-6.2-8.6 6.2 3.2-10.2-8.7-6.3h10.9zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.3 10.1-8.7-6.2-8.7 6.2 3.4-10.2-8.7-6.3h10.6zm60.8 0 3.3 10.3H166l-8.6 6.3 3.3 10.1-8.7-6.2-8.7 6.2 3.3-10.2-8.7-6.3h10.8zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.3 10.1-8.7-6.2-8.7 6.2 3.4-10.2-8.8-6.3h10.7zm60.8 0 3.3 10.3h10.7l-8.6 6.3 3.3 10.1-8.7-6.2-8.7 6.2 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.3 10.3h10.8l-8.8 6.3 3.4 10.1-8.7-6.2-8.7 6.2 3.4-10.2-8.8-6.3h10.8zM60.8 192l3.3 10.2H75l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.4 10.2h10.7l-8.8 6.3 3.4 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.7-6.3h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3H179zm60.8 0 3.4 10.2h10.7l-8.7 6.3 3.3 10.3-8.7-6.4-8.6 6.3 3.2-10.2-8.7-6.3H240zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.7zM30.4 217.9l3.4 10.2h10.6l-8.6 6.3 3.3 10.2-8.7-6.3-8.6 6.3 3.2-10.3-8.7-6.3h10.9zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.4-10.3-8.7-6.3h10.6zm60.8 0 3.3 10.2H166l-8.4 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.3-10.3-8.7-6.3h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.4-10.3-8.8-6.3h10.7zm60.8 0 3.3 10.2h10.7l-8.6 6.3 3.3 10.2-8.7-6.3-8.7 6.3 3.3-10.3-8.6-6.3h10.7zm60.8 0 3.3 10.2h10.8l-8.8 6.3 3.4 10.2-8.7-6.3-8.7 6.3 3.4-10.3-8.8-6.3h10.8z"
    }
  )
] }) })), "SvgUs");
var us_default = SvgUs;

// src/icons/locales/index.ts
var Locales = {
  at: at_default,
  au: au_default,
  be: be_default,
  ch: ch_default,
  cz: cz_default,
  de: de_default,
  dk: dk_default,
  ee: ee_default,
  es: es_default,
  fi: fi_default,
  fr: fr_default,
  gb: gb_default,
  ie: ie_default,
  it: it_default,
  nl: nl_default,
  no: no_default,
  nz: nz_default,
  pl: pl_default,
  se: se_default,
  us: us_default
};
var locales_default = Locales;

// src/components/flag.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var Flag = /* @__PURE__ */ __name(({ locale, country, className }) => {
  var _a;
  if (!locale && !country) {
    throw new Error("Flag component requires either a locale or country");
  }
  const isoCountry = (_a = country || (locale == null ? void 0 : locale.split("-")[1])) == null ? void 0 : _a.toLowerCase();
  const FlagSvg = locales_default[isoCountry || ""];
  if (!FlagSvg) {
    return null;
  }
  return /* @__PURE__ */ jsx33(FlagSvg, { className: cn10("aspect-[4/3]", className) });
}, "Flag");
var flag_default = Flag;

// src/components/flyout.tsx
import React14 from "react";
import { Dialog, Transition as Transition2 } from "@headlessui/react";
import cn11 from "classnames";
import { jsx as jsx34, jsxs as jsxs26 } from "react/jsx-runtime";
var FlyoutStickyHeader = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx34(
    "div",
    {
      className: cn11(
        "sticky -top-6 sm:-top-8 -mx-6 -mt-6 sm:-mx-8 sm:-mt-8 p-6 sm:p-8 bg-ds9-background z-10",
        className
      ),
      children
    }
  );
}, "FlyoutStickyHeader");
var FlyoutTitle = /* @__PURE__ */ __name(({ children, center }) => {
  return /* @__PURE__ */ jsx34(
    "h3",
    {
      className: cn11("text-2xl leading-6 font-semibold -mx-6 -mt-6 sm:-mx-8 sm:-mt-8 p-6 sm:p-8 bg-ds9-background", {
        "text-center": center
      }),
      children
    }
  );
}, "FlyoutTitle");
var FlyoutBody = /* @__PURE__ */ __name(({ children, inset }) => {
  return /* @__PURE__ */ jsx34(
    "div",
    {
      className: cn11(
        "overflow-y-auto text-sm p-6 sm:p-8 first-of-type:rounded-t-lg sm:first-of-type:rounded-t-none sm:flex-1",
        {
          "bg-ds9-background": !inset,
          "bg-ds9-50 dark:bg-ds9-900": inset
        }
      ),
      children
    }
  );
}, "FlyoutBody");
var FlyoutInset = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx34("main", { className: cn11("-mx-6 sm:-mx-8 sm:mb-16 p-6 border-t border-ds9-200 dark:border-ds9-700", className), children });
}, "FlyoutInset");
var FlyoutFooter = /* @__PURE__ */ __name(({ children }) => {
  return /* @__PURE__ */ jsx34("footer", { className: "sticky bottom-0 flex justify-between p-4 bg-ds9-50 dark:bg-ds9-900 after:absolute after:-z-10 after:inset-0 after:shadow-[0_0_0_1px_rgba(0,0,0,0.07)] dark:after:shadow-[0_0_0_1px_hsla(0,0%,100%,0.2)]", children });
}, "FlyoutFooter");
var Flyout = /* @__PURE__ */ __name(({
  children,
  width,
  static: staticDialog,
  animateInitial,
  open,
  setOpen,
  disableClose,
  initialFocus,
  afterClose
}) => {
  const desktop = use_media_default(`(min-width: 640px)`, true);
  return /* @__PURE__ */ jsx34(Transition2, { show: open != null ? open : staticDialog, as: React14.Fragment, appear: animateInitial, afterLeave: afterClose, children: /* @__PURE__ */ jsxs26(
    Dialog,
    {
      static: staticDialog,
      as: "div",
      className: "ds9-flyout relative z-30",
      onClose: disableClose ? () => null : setOpen,
      initialFocus,
      children: [
        /* @__PURE__ */ jsx34(
          Transition2.Child,
          {
            as: React14.Fragment,
            enter: "transition-opacity ease-linear duration-150",
            enterFrom: "opacity-0",
            enterTo: "opacity-100",
            leave: "transition-opacity ease-linear duration-150",
            leaveFrom: "opacity-100",
            leaveTo: "opacity-0",
            children: /* @__PURE__ */ jsx34("div", { className: "fixed inset-0 bg-ds9-black bg-opacity-25 dark:bg-opacity-75" })
          }
        ),
        desktop ? /* @__PURE__ */ jsx34("div", { className: "fixed inset-0 z-30 overflow-y-auto", children: /* @__PURE__ */ jsx34("div", { className: "fixed right-0 flex min-h-full items-end justify-end p-4 text-center sm:items-center sm:p-0", children: /* @__PURE__ */ jsx34(
          Transition2.Child,
          {
            as: React14.Fragment,
            enter: "transition duration-150 ease-out",
            enterFrom: "transform opacity-0 translate-x-full",
            enterTo: "transform opacity-100 translate-x-0",
            leave: "transition duration-150 ease-in",
            leaveFrom: "transform opacity-100 translate-x-0",
            leaveTo: "transform opacity-0 translate-x-full",
            children: /* @__PURE__ */ jsx34(
              Dialog.Panel,
              {
                className: "relative flex flex-col h-screen transform overflow-hidden text-left shadow-ds9-lg dark:shadow-ds9-dark-lg transition-all",
                style: { width },
                children
              }
            )
          }
        ) }) }) : /* @__PURE__ */ jsx34(
          Transition2.Child,
          {
            as: React14.Fragment,
            enter: "transition duration-150 ease-out",
            enterFrom: "transform translate-y-full",
            enterTo: "transform translate-y-0",
            leave: "transition duration-150 ease-out",
            leaveFrom: "transform translate-y-0",
            leaveTo: "transform translate-y-full",
            children: /* @__PURE__ */ jsx34("div", { className: "overflow-hidden rounded-t-lg shadow-ds9-md dark:shadow-[0_0_0_1px] dark:shadow-ds9-700 bg-ds9-background ring-0 focus:outline-none fixed z-30 bottom-0 top-auto inset-x-0", children: /* @__PURE__ */ jsx34(Dialog.Panel, { className: "flex flex-col max-h-[95vh]", children }) })
          }
        )
      ]
    }
  ) });
}, "Flyout");
Flyout.StickyHeader = FlyoutStickyHeader;
Flyout.Title = FlyoutTitle;
Flyout.Body = FlyoutBody;
Flyout.Inset = FlyoutInset;
Flyout.Footer = FlyoutFooter;
var flyout_default = Flyout;

// src/components/form.tsx
import "react";
import { FormProvider } from "react-hook-form";
import { jsx as jsx35 } from "react/jsx-runtime";
var Form = /* @__PURE__ */ __name((props) => {
  const _a = props, { formMethods } = _a, formProps = __objRest(_a, ["formMethods"]);
  return /* @__PURE__ */ jsx35("form", __spreadProps(__spreadValues({}, formProps), { children: /* @__PURE__ */ jsx35(FormProvider, __spreadProps(__spreadValues({}, formMethods), { children: props.children })) }));
}, "Form");
var form_default = Form;

// src/components/form_autocomplete.tsx
import "react";
import { useFormContext, useController } from "react-hook-form";
import { Combobox as Combobox2 } from "@headlessui/react";
import cn12 from "classnames";
import { jsx as jsx36 } from "react/jsx-runtime";
var Option2 = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  props.className = ({ active }) => cn12(
    "cursor-pointer px-2 sm:px-5 py-2 h-12 sm:h-auto flex items-center leading-5 text-sm",
    {
      "text-ds9-500 dark:text-ds9-400": !active,
      "bg-ds9-100 dark:bg-ds9-800 text-ds9-foreground rounded sm:rounded-none": active
    },
    customClassName
  );
  return /* @__PURE__ */ jsx36(Combobox2.Option, __spreadProps(__spreadValues({}, props), { children }));
}, "Option");
var FormAutocomplete = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { rules, name, onSelected } = _b, props = __objRest(_b, ["rules", "name", "onSelected"]);
  const { control, watch } = useFormContext();
  const {
    field,
    formState: { errors }
  } = useController({ name, control, rules });
  const error = dig(errors, name);
  const value = watch(name);
  return /* @__PURE__ */ jsx36(
    autocomplete_default,
    __spreadProps(__spreadValues({}, props), {
      value,
      error: !!error,
      onSelected: (value2) => {
        field.onChange(value2);
        onSelected && onSelected(value2);
      }
    })
  );
}, "FormAutocomplete");
FormAutocomplete.Option = Option2;
var form_autocomplete_default = FormAutocomplete;

// src/components/form_checkbox.tsx
import React16, { useCallback as useCallback2 } from "react";
import { useFormContext as useFormContext2 } from "react-hook-form";
import { jsx as jsx37 } from "react/jsx-runtime";
var ForwardableFormCheckbox = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { rules, name } = _b, props = __objRest(_b, ["rules", "name"]);
  const {
    register,
    formState: { errors }
  } = useFormContext2();
  const error = dig(errors, name);
  const registerFields = register(name, rules);
  const onChange = useCallback2(
    (e) => {
      registerFields.onChange(e);
      if (props.onChange)
        props.onChange(e);
    },
    [props, registerFields]
  );
  return /* @__PURE__ */ jsx37(checkbox_default, __spreadProps(__spreadValues({}, props), { ref, registerFields, name, onChange, error: !!error }));
}, "ForwardableFormCheckbox");
var FormCheckbox = React16.forwardRef(ForwardableFormCheckbox);
var form_checkbox_default = FormCheckbox;

// src/components/form_input.tsx
import React18, { useCallback as useCallback4 } from "react";
import { useFormContext as useFormContext3 } from "react-hook-form";

// src/components/input.tsx
import React17, { useCallback as useCallback3, useId as useId3, useState as useState2 } from "react";
import cn13 from "classnames";
import { ClipboardDocumentIcon, EyeIcon } from "@heroicons/react/24/outline";
import { EyeSlashIcon } from "@heroicons/react/20/solid";
import { jsx as jsx38, jsxs as jsxs27 } from "react/jsx-runtime";
var ForwardableInput = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, {
    registerFields,
    label,
    addonLeft,
    addonRight,
    overlayLeft,
    overlayRight,
    className,
    inputClassName,
    error,
    right,
    inputSize,
    skeleton,
    capitalize,
    help,
    autoSelect,
    autoCopy,
    skipSSRSkeleton,
    children
  } = _b, props = __objRest(_b, [
    "registerFields",
    "label",
    "addonLeft",
    "addonRight",
    "overlayLeft",
    "overlayRight",
    "className",
    "inputClassName",
    "error",
    "right",
    "inputSize",
    "skeleton",
    "capitalize",
    "help",
    "autoSelect",
    "autoCopy",
    "skipSSRSkeleton",
    "children"
  ]);
  props.autoCapitalize = props.autoCapitalize || "none";
  props.autoComplete = props.autoComplete || "off";
  props.autoCorrect = props.autoCorrect || "off";
  props.spellCheck = props.spellCheck || "false";
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton && !autoCopy) {
    skeleton = true;
  }
  props.disabled = props.disabled || skeleton;
  const defaultId = useId3();
  props.id = props.id || defaultId;
  help = help || children;
  if (autoCopy) {
    if (overlayRight) {
      console.error("'autoCopy' and 'overlayRight' cannot be used together");
    }
    overlayRight = /* @__PURE__ */ jsx38(ClipboardDocumentIcon, { className: "w-4 h-4 text-ds9-500 group-hover/ds9-input:text-ds9-900 dark:text-ds9-300 dark:group-hover/ds9-input:text-ds9-100 cursor-pointer transition-colors" });
    props.readOnly = true;
  }
  const toast = useToast();
  const onClick = useCallback3(
    (e) => {
      const target = e.target;
      if (autoSelect || autoCopy) {
        target.select();
      }
      if (autoCopy) {
        navigator.clipboard.writeText(target.value).then(() => toast("Copied!", "info"));
      }
      if (props.onClick) {
        props.onClick(e);
      }
    },
    [autoSelect, autoCopy, props, toast]
  );
  if (props.type === "password" && addonRight) {
    console.error("'addonRight' cannot be used with 'type=password'");
  }
  const [showPassword, setShowPassword] = useState2(false);
  const password = props.type === "password";
  const inputType = props.type === "password" && showPassword ? "text" : props.type;
  return /* @__PURE__ */ jsx38("div", { className: cn13("ds9-input", className), children: /* @__PURE__ */ jsxs27("label", { htmlFor: props.id, className: "w-full", children: [
    label ? /* @__PURE__ */ jsx38(label_default, { capitalize, skeleton, children: label }) : null,
    /* @__PURE__ */ jsxs27(
      "div",
      {
        className: cn13("w-full relative group/ds9-input", {
          flex: addonLeft || addonRight || password,
          "ds9-skeleton": skeleton
        }),
        children: [
          addonLeft ? /* @__PURE__ */ jsx38(
            "div",
            {
              className: cn13(
                "flex items-center justify-center whitespace-nowrap px-3 text-sm text-ds9-500 dark:text-ds9-400 rounded-l bg-ds9-50 dark:bg-ds9-900 border border-r-0",
                {
                  "border-ds9-200 dark:border-ds9-700": !error,
                  "border-ds9-error dark:border-ds9-error-light": error
                }
              ),
              children: addonLeft
            }
          ) : null,
          /* @__PURE__ */ jsxs27("div", { className: "w-full relative", children: [
            /* @__PURE__ */ jsx38(
              "input",
              __spreadProps(__spreadValues(__spreadValues({
                "aria-invalid": "false",
                className: cn13(
                  "text-sm leading-normal w-full inline-flex border transition focus:outline-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]",
                  {
                    rounded: !addonLeft && !addonRight && !password,
                    "rounded-r": addonLeft && !addonRight && !password,
                    "rounded-l": !addonLeft && (addonRight || password),
                    "text-right": right,
                    "h-10": inputSize === "m" || !inputSize,
                    "h-8": inputSize === "s",
                    "pl-3": !overlayLeft,
                    "pl-7": overlayLeft && inputSize === "s",
                    "pl-12": overlayLeft && inputSize !== "s",
                    "pr-3": !overlayRight,
                    "pr-7": overlayRight && inputSize === "s",
                    "pr-12": overlayRight && inputSize !== "s",
                    "dark:bg-ds9-background": !props.disabled && !props.readOnly,
                    "bg-ds9-50 dark:bg-ds9-900 cursor-not-allowed": props.disabled,
                    "bg-ds9-50 dark:bg-ds9-900 cursor-default": props.readOnly,
                    "border-ds9-200 dark:border-ds9-700 focus:border-ds9-500 dark:focus:border-ds9-400 placeholder:text-[rgb(102,102,102)] dark:placeholder:text-[rgb(117,117,117)]": !error,
                    "border-ds9-error dark:border-ds9-error-light placeholder:text-ds9-error dark:placeholder:text-ds9-error-light": error,
                    "cursor-pointer": autoCopy
                  },
                  inputClassName
                )
              }, registerFields), props), {
                ref: mergeRefs(ref, registerFields == null ? void 0 : registerFields.ref),
                type: inputType,
                onClick
              })
            ),
            overlayLeft ? /* @__PURE__ */ jsx38("div", { className: "absolute inset-y-3 left-3 flex items-center justify-center", children: overlayLeft }) : null,
            overlayRight ? /* @__PURE__ */ jsx38("div", { className: "absolute inset-y-3 right-3 flex items-center justify-center", children: overlayRight }) : null
          ] }),
          addonRight ? /* @__PURE__ */ jsx38(
            "div",
            {
              className: cn13(
                "flex items-center justify-center whitespace-nowrap px-3 text-sm text-ds9-500 dark:text-ds9-400 rounded-r bg-ds9-50 dark:bg-ds9-900 border border-l-0",
                {
                  "border-ds9-200 dark:border-ds9-700": !error,
                  "border-ds9-error dark:border-ds9-error-light": error
                }
              ),
              children: addonRight
            }
          ) : null,
          props.type === "password" ? /* @__PURE__ */ jsx38(
            "div",
            {
              className: cn13(
                "flex items-center justify-center whitespace-nowrap text-ds9-400 dark:text-ds9-500 rounded-r bg-ds9-50 dark:bg-ds9-900 border border-l-0",
                {
                  "border-ds9-200 dark:border-ds9-700": !error,
                  "border-ds9-error dark:border-ds9-error-light": error
                }
              ),
              children: /* @__PURE__ */ jsx38(
                "button",
                {
                  className: "flex items-center justify-center px-3 h-full hover:text-ds9-foreground transition-colors",
                  type: "button",
                  onClick: () => setShowPassword((v) => !v),
                  children: showPassword ? /* @__PURE__ */ jsx38(EyeIcon, { className: "w-4 h-4" }) : /* @__PURE__ */ jsx38(EyeSlashIcon, { className: "w-4 h-4" })
                }
              )
            }
          ) : null
        ]
      }
    ),
    help ? /* @__PURE__ */ jsx38("p", { className: "mt-2.5 text-[0.8125rem] leading-3 text-ds9-500 dark:text-ds9-400", children: /* @__PURE__ */ jsx38("span", { className: cn13({ "ds9-skeleton": skeleton }), children: help }) }) : null
  ] }) });
}, "ForwardableInput");
var Input = React17.forwardRef(ForwardableInput);
var input_default = Input;

// src/components/form_input.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var ForwardableFormInput = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { rules, name } = _b, props = __objRest(_b, ["rules", "name"]);
  const {
    register,
    formState: { errors }
  } = useFormContext3();
  const error = dig(errors, name);
  const registerFields = register(name, rules);
  const onChange = useCallback4(
    (e) => {
      registerFields.onChange(e);
      if (props.onChange)
        props.onChange(e);
    },
    [props, registerFields]
  );
  return /* @__PURE__ */ jsx39(input_default, __spreadProps(__spreadValues({}, props), { ref, registerFields, name, onChange, error: !!error }));
}, "ForwardableFormInput");
var FormInput = React18.forwardRef(ForwardableFormInput);
var form_input_default = FormInput;

// src/components/form_select.tsx
import "react";
import { useFormContext as useFormContext4, useController as useController2 } from "react-hook-form";

// src/components/select.tsx
import React19, { useId as useId4 } from "react";
import {
  autoPlacement,
  FloatingPortal as FloatingPortal2,
  offset as offset2,
  size as size2,
  useFloating as useFloating2
} from "@floating-ui/react";
import cn14 from "classnames";
import { Listbox, Transition as Transition3 } from "@headlessui/react";
import { ChevronDownIcon as ChevronDownIcon2 } from "@heroicons/react/20/solid";
import { Fragment as Fragment2, jsx as jsx40, jsxs as jsxs28 } from "react/jsx-runtime";
var Option3 = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  const funcClassName = typeof props.className === "function" ? props.className : () => "";
  props.className = (bag) => cn14(
    "cursor-pointer px-2 sm:px-3 py-2 h-12 sm:h-auto flex items-center leading-5 text-sm",
    {
      "text-ds9-500 dark:text-ds9-400": !bag.active,
      "bg-ds9-100 text-ds9-foreground dark:bg-ds9-800 rounded sm:rounded-none": bag.active
    },
    customClassName,
    funcClassName(bag)
  );
  return /* @__PURE__ */ jsx40(Listbox.Option, __spreadProps(__spreadValues({}, props), { children }));
}, "Option");
var Select = /* @__PURE__ */ __name(({
  id,
  selected,
  onSelected,
  selectedLabel,
  label,
  name,
  className,
  error,
  disabled,
  skeleton,
  size: selectSize,
  placement,
  strategy: floatingStrategy,
  skipSSRSkeleton,
  autoPlace,
  multiple,
  children
}) => {
  const { x, y, strategy, refs } = useFloating2({
    strategy: floatingStrategy,
    placement,
    middleware: [
      autoPlace ? autoPlacement(autoPlace) : null,
      offset2(8),
      size2({
        apply({ availableWidth, availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight - 10}px`,
            width: `${rects.reference.width}px`
          });
        }
      })
    ]
  });
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  disabled = disabled || skeleton;
  const defaultId = useId4();
  id = id || defaultId;
  const desktop = useMedia(`(min-width: 640px)`, true);
  return /* @__PURE__ */ jsx40(
    Listbox,
    {
      as: "div",
      className: cn14("relative", { "ds9-skeleton": skeleton }, className),
      value: selected,
      onChange: onSelected,
      disabled,
      multiple,
      children: ({ open }) => /* @__PURE__ */ jsxs28(Fragment2, { children: [
        /* @__PURE__ */ jsxs28("label", { htmlFor: id, className: "w-full", children: [
          label ? /* @__PURE__ */ jsx40("div", { className: "uppercase text-xs leading-5 mb-2 font-medium text-ds9-500 dark:text-ds9-400", children: label }) : null,
          /* @__PURE__ */ jsx40("div", { className: "w-full relative", children: /* @__PURE__ */ jsx40(
            Listbox.Button,
            {
              id,
              name,
              "aria-invalid": "false",
              ref: refs.setReference,
              className: cn14(
                "group text-sm leading-normal w-full pl-3 pr-10 inline-flex truncate items-center rounded border transition focus:outline-none focus:ring-0",
                {
                  "h-10": !selectSize || selectSize === "m",
                  "h-8": selectSize === "s",
                  "dark:bg-ds9-background": !disabled,
                  "bg-ds9-50 dark:bg-ds9-900 cursor-not-allowed": disabled,
                  "border-ds9-200 dark:border-ds9-700 focus:border-ds9-500 dark:focus:border-ds9-400 hover:border-ds9-500 dark:hover:border-ds9-400 placeholder:text-[rgb(102,102,102)] dark:placeholder:text-[rgb(117,117,117)]": !error,
                  "border-ds9-error dark:border-ds9-error-light placeholder:text-ds9-error dark:placeholder:text-ds9-error-light": error
                }
              ),
              children: /* @__PURE__ */ jsxs28(Fragment2, { children: [
                /* @__PURE__ */ jsx40("div", { className: "truncate", children: selectedLabel ? selectedLabel(selected) : /* @__PURE__ */ jsx40(Fragment2, { children: typeof selected === "string" ? selected : "" }) }),
                /* @__PURE__ */ jsx40("div", { className: "absolute inset-y-3 right-3 flex items-center justify-center text-ds9-300 group-hover:text-ds9-foreground dark:text-ds9-600 dark:group-hover:text-ds9-foreground", children: /* @__PURE__ */ jsx40(ChevronDownIcon2, { className: cn14("w-5 h-5 transition-transform", { "rotate-180": open }) }) })
              ] })
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx40(FloatingPortal2, { children: desktop ? open && /* @__PURE__ */ jsx40(
          Listbox.Options,
          {
            className: "z-40 w-full origin-top overflow-y-auto rounded-ds9 bg-ds9-background py-1 shadow-ds9-md dark:shadow-ds9-dark-md focus:outline-none",
            ref: refs.setFloating,
            style: {
              position: strategy,
              top: y != null ? y : 0,
              left: x != null ? x : 0,
              width: "max-content"
            },
            static: true,
            children
          }
        ) : /* @__PURE__ */ jsxs28(Transition3, { show: open, children: [
          /* @__PURE__ */ jsx40(
            Transition3.Child,
            {
              as: React19.Fragment,
              enter: "transition-opacity ease-linear duration-150",
              enterFrom: "opacity-0",
              enterTo: "opacity-100",
              leave: "transition-opacity ease-linear duration-150",
              leaveFrom: "opacity-100",
              leaveTo: "opacity-0",
              children: /* @__PURE__ */ jsx40("div", { className: "fixed inset-0 bg-ds9-black bg-opacity-25 dark:bg-opacity-75 z-40" })
            }
          ),
          /* @__PURE__ */ jsx40(
            Transition3.Child,
            {
              as: React19.Fragment,
              enter: "transition duration-150 ease-out",
              enterFrom: "transform translate-y-full",
              enterTo: "transform translate-y-0",
              leave: "transition duration-150 ease-out",
              leaveFrom: "transform translate-y-0",
              leaveTo: "transform translate-y-full",
              children: /* @__PURE__ */ jsx40(
                Listbox.Options,
                {
                  static: true,
                  className: "rounded-t-lg shadow-ds9-md dark:shadow-[0_0_0_1px] dark:shadow-ds9-700 bg-ds9-background ring-0 focus:outline-none fixed z-40 bottom-0 top-auto max-h-[75dvh] inset-x-0 overflow-y-auto",
                  children: /* @__PURE__ */ jsx40("div", { className: "p-2 rounded-ds9 border border-ds9-200 dark:border-ds9-700", children })
                }
              )
            }
          )
        ] }) })
      ] })
    }
  );
}, "Select");
Select.Option = Option3;
var select_default = Select;

// src/components/form_select.tsx
import { Listbox as Listbox2 } from "@headlessui/react";
import cn15 from "classnames";
import { jsx as jsx41 } from "react/jsx-runtime";
var Option4 = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  const funcClassName = typeof props.className === "function" ? props.className : () => "";
  props.className = (bag) => cn15(
    "cursor-pointer px-2 sm:px-3 py-2 h-12 sm:h-auto flex items-center leading-5 text-sm",
    {
      "text-ds9-500 dark:text-ds9-400": !bag.active,
      "bg-ds9-100 text-ds9-foreground dark:bg-ds9-800 rounded sm:rounded-none": bag.active
    },
    customClassName,
    funcClassName(bag)
  );
  return /* @__PURE__ */ jsx41(Listbox2.Option, __spreadProps(__spreadValues({}, props), { children }));
}, "Option");
var FormSelect = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { rules, name, onSelected } = _b, props = __objRest(_b, ["rules", "name", "onSelected"]);
  const { control, watch } = useFormContext4();
  const {
    field,
    formState: { errors }
  } = useController2({ name, control, rules });
  const error = dig(errors, name);
  const value = watch(name);
  return /* @__PURE__ */ jsx41(
    select_default,
    __spreadProps(__spreadValues({}, props), {
      selected: value,
      error: !!error,
      onSelected: (value2) => {
        field.onChange(value2);
        onSelected && onSelected(value2);
      }
    })
  );
}, "FormSelect");
FormSelect.Option = Option4;
var form_select_default = FormSelect;

// src/components/form_textarea.tsx
import { useCallback as useCallback5 } from "react";
import { useFormContext as useFormContext5 } from "react-hook-form";

// src/components/textarea.tsx
import { useId as useId5 } from "react";
import cn16 from "classnames";
import { jsx as jsx42, jsxs as jsxs29 } from "react/jsx-runtime";
var Textarea = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    registerFields,
    label,
    overlayLeft,
    overlayRight,
    className,
    inputClassName,
    error,
    capitalize,
    children,
    help,
    skeleton,
    skipSSRSkeleton
  } = _b, props = __objRest(_b, [
    "registerFields",
    "label",
    "overlayLeft",
    "overlayRight",
    "className",
    "inputClassName",
    "error",
    "capitalize",
    "children",
    "help",
    "skeleton",
    "skipSSRSkeleton"
  ]);
  props.autoCapitalize = props.autoCapitalize || "none";
  props.autoComplete = props.autoComplete || "off";
  props.autoCorrect = props.autoCorrect || "off";
  props.spellCheck = props.spellCheck || "false";
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  props.disabled = props.disabled || skeleton;
  const defaultId = useId5();
  props.id = props.id || defaultId;
  help = help || children;
  return /* @__PURE__ */ jsx42("div", { className: cn16("ds9-textarea", className), children: /* @__PURE__ */ jsxs29("label", { htmlFor: props.id, className: "w-full", children: [
    label ? /* @__PURE__ */ jsx42(label_default, { capitalize, skeleton, children: label }) : null,
    /* @__PURE__ */ jsxs29("div", { className: cn16("w-full relative", { "ds9-skeleton": skeleton }), children: [
      /* @__PURE__ */ jsx42(
        "textarea",
        __spreadValues(__spreadValues({
          "aria-invalid": "false",
          className: cn16(
            "text-sm leading-normal w-full inline-flex rounded border transition-colors focus:outline-none focus:ring-0",
            {
              "pl-3": !overlayLeft,
              "pl-12": overlayLeft,
              "pr-3": !overlayRight,
              "pr-12": overlayRight,
              "dark:bg-ds9-background": !props.disabled,
              "bg-ds9-50 dark:bg-ds9-900 cursor-not-allowed": props.disabled,
              "border-ds9-200 dark:border-ds9-700 focus:border-ds9-500 dark:focus:border-ds9-400 placeholder:text-[rgb(102,102,102)] dark:placeholder:text-[rgb(117,117,117)]": !error,
              "border-ds9-error dark:border-ds9-error-light placeholder:text-ds9-error dark:placeholder:text-ds9-error-light": error
            },
            inputClassName
          )
        }, registerFields), props)
      ),
      overlayLeft ? /* @__PURE__ */ jsx42("div", { className: "absolute top-3 left-3 flex items-center justify-center", children: overlayLeft }) : null,
      overlayRight ? /* @__PURE__ */ jsx42("div", { className: "absolute top-3 right-3 flex items-center justify-center", children: overlayRight }) : null
    ] }),
    help ? /* @__PURE__ */ jsx42("p", { className: "mt-2.5 text-[0.8125rem] leading-3 text-ds9-500 dark:text-ds9-400", children: /* @__PURE__ */ jsx42("span", { className: cn16({ "ds9-skeleton": skeleton }), children: help }) }) : null
  ] }) });
}, "Textarea");
var textarea_default = Textarea;

// src/components/form_textarea.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var FormTextarea = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { rules, name } = _b, props = __objRest(_b, ["rules", "name"]);
  const {
    register,
    formState: { errors }
  } = useFormContext5();
  const error = dig(errors, name);
  const registerFields = register(name, rules);
  const onChange = useCallback5(
    (e) => {
      registerFields.onChange(e);
      if (props.onChange)
        props.onChange(e);
    },
    [props, registerFields]
  );
  return /* @__PURE__ */ jsx43(textarea_default, __spreadProps(__spreadValues({}, props), { registerFields, name, onChange, error: !!error }));
}, "FormTextarea");
var form_textarea_default = FormTextarea;

// src/components/form_toggle.tsx
import "react";
import { useFormContext as useFormContext6, useController as useController3 } from "react-hook-form";

// src/components/toggle.tsx
import "react";
import cn17 from "classnames";
import { Switch } from "@headlessui/react";
import { jsx as jsx44, jsxs as jsxs30 } from "react/jsx-runtime";
var Toggle = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { checked, onChange, size: size6, label, labelLeft, skeleton, disabled } = _b, props = __objRest(_b, ["checked", "onChange", "size", "label", "labelLeft", "skeleton", "disabled"]);
  disabled = disabled || skeleton;
  return /* @__PURE__ */ jsx44(Switch.Group, { children: /* @__PURE__ */ jsxs30("div", { className: "inline-flex items-center gap-3", children: [
    labelLeft ? /* @__PURE__ */ jsx44(
      Switch.Label,
      {
        className: cn17("whitespace-nowrap select-none text-sm font-medium", {
          "ds9-skeleton": skeleton,
          "cursor-pointer": !disabled,
          "cursor-default pointer-events-none": disabled
        }),
        children: labelLeft
      }
    ) : null,
    /* @__PURE__ */ jsx44(
      Switch,
      __spreadProps(__spreadValues({}, props), {
        as: "button",
        checked,
        onChange,
        name: "test",
        className: "inline-flex items-center py-[3px] relative align-middle",
        disabled,
        children: /* @__PURE__ */ jsx44(
          "span",
          {
            "aria-hidden": "true",
            className: cn17(
              "relative rounded-[14px] border border-ds9-black dark:border-ds9-white border-opacity-[0.08] dark:border-opacity-[0.14] transition-colors",
              {
                "h-6 w-10": size6 === "m" || !size6,
                "h-[14px] w-7": size6 === "s",
                "bg-ds9-100 dark:bg-ds9-900 bg-clip-padding": !checked,
                "bg-ds9-success dark:bg-ds9-success-light": checked,
                "opacity-50": disabled && !skeleton,
                "pointer-events-none": disabled,
                "ds9-skeleton ds9-skeleton-round": skeleton
              }
            ),
            children: /* @__PURE__ */ jsx44(
              "div",
              {
                className: cn17(
                  "rounded-full border border-transparent shadow-[0_0_4px_rgba(0,0,0,.12),0_1px_1px_rgba(0,0,0,.16)] absolute top-1/2 left-0 -translate-y-1/2 transition-transform",
                  {
                    "w-[22px] h-[22px]": size6 === "m" || !size6,
                    "w-3 h-3": size6 === "s",
                    "bg-ds9-white dark:bg-ds9-400": !checked,
                    "bg-ds9-white dark:bg-ds9-200": checked,
                    "translate-x-4": checked && (size6 === "m" || !size6),
                    "translate-x-[14px]": checked && size6 === "s"
                  }
                )
              }
            )
          }
        )
      })
    ),
    label ? /* @__PURE__ */ jsx44(
      Switch.Label,
      {
        className: cn17("whitespace-nowrap select-none text-sm font-medium", {
          "ds9-skeleton": skeleton,
          "cursor-pointer": !disabled,
          "cursor-default pointer-events-none text-ds9-400 dark:text-ds9-500": disabled
        }),
        children: label
      }
    ) : null
  ] }) });
}, "Toggle");
var toggle_default = Toggle;

// src/components/form_toggle.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
var FormToggle = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { rules, name, onChange } = _b, props = __objRest(_b, ["rules", "name", "onChange"]);
  const { control, watch } = useFormContext6();
  const { field } = useController3({ name, control, rules });
  const value = watch(name);
  return /* @__PURE__ */ jsx45(
    toggle_default,
    __spreadProps(__spreadValues({}, props), {
      checked: value,
      onChange: (value2) => {
        field.onChange(value2);
        onChange && onChange(value2);
      }
    })
  );
}, "FormToggle");
var form_toggle_default = FormToggle;

// src/components/highlighter.tsx
import "react";
import cn18 from "classnames";
import { Fragment as Fragment3, jsx as jsx46 } from "react/jsx-runtime";
var chunkify = /* @__PURE__ */ __name((searches, text, caseSensitive = false) => {
  const matchingChunks = mergeChunks(findChunks(searches, text, caseSensitive));
  const allChunks = [];
  const totalLength = text ? text.length : 0;
  const append = /* @__PURE__ */ __name((start, end, highlight) => {
    if (end - start > 0) {
      allChunks.push({ start, end, highlight });
    }
  }, "append");
  if (matchingChunks.length === 0) {
    append(0, totalLength, false);
  } else {
    let lastIndex = 0;
    for (const chunk of matchingChunks) {
      append(lastIndex, chunk.start, false);
      append(chunk.start, chunk.end, true);
      lastIndex = chunk.end;
    }
    append(lastIndex, totalLength, false);
  }
  return allChunks;
}, "chunkify");
var findChunks = /* @__PURE__ */ __name((searches, text, caseSensitive) => {
  return searches.filter((search) => search).reduce((chunks, search) => {
    const regex = new RegExp(search, caseSensitive ? "g" : "gi");
    let match;
    while (match = regex.exec(text)) {
      const start = match.index;
      const end = regex.lastIndex;
      if (end > start) {
        chunks.push({ highlight: false, start, end });
      }
      if (match.index === regex.lastIndex) {
        regex.lastIndex++;
      }
    }
    return chunks;
  }, []);
}, "findChunks");
var mergeChunks = /* @__PURE__ */ __name((chunks) => {
  return chunks.sort((first, second) => first.start - second.start).reduce((processedChunks, nextChunk) => {
    const prevChunk = processedChunks.pop();
    if (!prevChunk) {
      return [nextChunk];
    }
    if (prevChunk && nextChunk.start <= prevChunk.end) {
      const end = Math.max(prevChunk.end, nextChunk.end);
      processedChunks.push({ highlight: false, start: prevChunk.start, end });
    } else {
      processedChunks.push(prevChunk, nextChunk);
    }
    return processedChunks;
  }, []);
}, "mergeChunks");
var escapeRegExp = /* @__PURE__ */ __name((string) => {
  return string.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
}, "escapeRegExp");
var Highlighter = /* @__PURE__ */ __name(({ text, search }) => {
  if (!search)
    return /* @__PURE__ */ jsx46(Fragment3, { children: text });
  const chunks = chunkify([escapeRegExp(search)], text);
  return /* @__PURE__ */ jsx46(Fragment3, { children: chunks.map((chunk, index) => /* @__PURE__ */ jsx46(
    "span",
    {
      className: cn18({
        "relative z-0 before:absolute before:inset-y-0 before:-inset-x-0.5 before:bg-yellow-100 before:-z-10 before:rounded before:border before:border-yellow-400 before:scale-y-110": chunk.highlight
      }),
      children: text.substring(chunk.start, chunk.end)
    },
    index
  )) });
}, "Highlighter");
var highlighter_default = Highlighter;

// src/components/inline_edit.tsx
import React25 from "react";
import cn19 from "classnames";
import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid";
import { jsx as jsx47, jsxs as jsxs31 } from "react/jsx-runtime";
var ForwardableInlineEdit = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { changed, showReset, className, update, reset, inputClassName } = _b, props = __objRest(_b, ["changed", "showReset", "className", "update", "reset", "inputClassName"]);
  const resetBtn = /* @__PURE__ */ jsx47(
    "button",
    {
      onClick: reset,
      disabled: props.disabled,
      type: "button",
      className: "text-ds9-500 hover:text-ds9-foreground transition-colors",
      title: "Reset",
      children: /* @__PURE__ */ jsx47(ArrowUturnLeftIcon, { className: "w-3 h-3" })
    }
  );
  return /* @__PURE__ */ jsxs31("form", { onSubmit: update, className: cn19("flex items-center gap-1", className), children: [
    /* @__PURE__ */ jsx47(
      input_default,
      __spreadProps(__spreadValues({
        inputSize: "s",
        overlayLeft: showReset && props.right ? resetBtn : null,
        overlayRight: showReset && !props.right ? resetBtn : null,
        className: inputClassName,
        onKeyDown: (e) => e.code === "Escape" && reset()
      }, props), {
        ref
      })
    ),
    /* @__PURE__ */ jsx47(button_default, { onClick: update, size: "s", disabled: props.disabled, className: cn19({ hidden: !changed }), type: "submit", children: "Save" })
  ] });
}, "ForwardableInlineEdit");
var InlineEdit = React25.forwardRef(ForwardableInlineEdit);
var inline_edit_default = InlineEdit;

// src/components/link.tsx
import "react";
import cn20 from "classnames";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { jsx as jsx48, jsxs as jsxs32 } from "react/jsx-runtime";
var Link2 = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children, external } = _b, props = __objRest(_b, ["children", "external"]);
  props.className = cn20(
    "cursor-pointer inline-flex items-center outline-none text-ds9-success dark:text-ds9-success-light hover:bg-gradient-to-b hover:from-current hover:to-current hover:bg-[size:100%_1px] hover:bg-no-repeat hover:bg-[position:0_100%]",
    props.className
  );
  if (external) {
    props.target = "_blank";
    props.rel = "noopener noreferrer";
  }
  return /* @__PURE__ */ jsxs32("a", __spreadProps(__spreadValues({}, props), { children: [
    children,
    external ? /* @__PURE__ */ jsx48(ArrowTopRightOnSquareIcon, { className: "ml-0.5 w-3.5 h-3.5" }) : null
  ] }));
}, "Link");
var link_default = Link2;

// src/components/list.tsx
import "react";
import cn21 from "classnames";
import { jsx as jsx49 } from "react/jsx-runtime";
var ListCell = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx49("div", { className: cn21("py-4 first-of-type:pt-0 last-of-type:pb-0 sm:py-0", className), children });
}, "ListCell");
var ListItem = /* @__PURE__ */ __name(({ children, className, manual }) => {
  return /* @__PURE__ */ jsx49(
    "div",
    {
      className: cn21(
        "flex flex-col sm:flex-row sm:items-center sm:gap-4 p-5 border-ds9-200 dark:border-ds9-700 divide-y divide-ds9-200 dark:divide-ds9-700 sm:divide-none",
        {
          "rounded-ds9 sm:rounded-none sm:first-of-type:rounded-t-ds9 last-of-type:rounded-b-ds9 border last-of-type:border-b sm:border-b-0": !manual
        },
        className
      ),
      children
    }
  );
}, "ListItem");
var List = /* @__PURE__ */ __name(({ children }) => {
  return /* @__PURE__ */ jsx49("div", { className: "space-y-4 sm:space-y-0", children });
}, "List");
List.Cell = ListCell;
List.Item = ListItem;
var list_default = List;

// src/components/locale_badge.tsx
import "react";
import cn22 from "classnames";
import { jsx as jsx50, jsxs as jsxs33 } from "react/jsx-runtime";
var LocaleBadge = /* @__PURE__ */ __name(({ locale, inverted, skeleton, className }) => {
  return /* @__PURE__ */ jsxs33(
    "div",
    {
      className: cn22(
        "flex shrink-0 items-center rounded-sm overflow-hidden h-4 w-[63.33px]",
        {
          "bg-ds9-200 dark:bg-ds9-700 text-ds9-foreground": !inverted,
          "bg-ds9-700 text-ds9-background dark:bg-ds9-200": inverted,
          "ds9-skeleton ds9-skeleton-rounded": skeleton
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsx50(flag_default, { locale, className: cn22("h-4", { "opacity-0": skeleton }) }),
        /* @__PURE__ */ jsxs33("div", { className: "flex items-center justify-center text-xs leading-none h-4 w-[42px] px-px tracking-tight", children: [
          /* @__PURE__ */ jsx50("span", { className: "font-medium", children: locale.substring(0, 2) }),
          /* @__PURE__ */ jsx50(
            "span",
            {
              className: cn22({ "text-ds9-500 dark:text-ds9-200": !inverted, "text-ds9-200 dark:text-ds9-500": inverted }),
              children: "-"
            }
          ),
          /* @__PURE__ */ jsx50("span", { className: "font-medium", children: locale.substring(3, 5) })
        ] })
      ]
    }
  );
}, "LocaleBadge");
var locale_badge_default = LocaleBadge;

// src/components/menu.tsx
import React30, { useRef as useRef2 } from "react";
import twcn3 from "classnames";
import {
  FloatingPortal as FloatingPortal4,
  offset as offset4,
  size as size4,
  useFloating as useFloating4,
  shift,
  useHover as useHover2,
  safePolygon as safePolygon2,
  useInteractions as useInteractions2,
  FloatingArrow as FloatingArrow2,
  arrow as arrow2,
  flip as flip2
} from "@floating-ui/react";
import { Menu as HeadlessMenu, Transition as Transition5 } from "@headlessui/react";
import { ChevronDownIcon as ChevronDownIcon3, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";

// src/components/tooltip.tsx
import {
  FloatingArrow,
  FloatingPortal as FloatingPortal3,
  arrow,
  flip,
  offset as offset3,
  safePolygon,
  size as size3,
  useClick,
  useFloating as useFloating3,
  useHover,
  useInteractions
} from "@floating-ui/react";
import { Transition as Transition4 } from "@headlessui/react";
import React29, { useRef, useState as useState3 } from "react";
import { Fragment as Fragment4, jsx as jsx51, jsxs as jsxs34 } from "react/jsx-runtime";
var Tooltip = /* @__PURE__ */ __name(({
  className,
  contents,
  delay,
  maxWidth,
  offset: offsetValue,
  placement,
  children
}) => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState3(false);
  const { x, y, strategy, refs, context } = useFloating3({
    placement: placement || "top",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset3(offsetValue != null ? offsetValue : 8),
      flip(),
      arrow({
        element: arrowRef
      }),
      size3({
        apply({ availableHeight, elements }) {
          var _a;
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight - 10}px`
          });
          Object.assign(((_a = elements.floating.querySelector(":scope > div")) == null ? void 0 : _a.style) || {}, {
            maxHeight: `${availableHeight - 10}px`
          });
        }
      })
    ]
  });
  const click = useClick(context, { ignoreMouse: true });
  const hover = useHover(context, {
    handleClose: safePolygon(),
    delay: {
      open: delay
    }
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover]);
  if (!children) {
    return null;
  }
  return /* @__PURE__ */ jsxs34(Fragment4, { children: [
    /* @__PURE__ */ jsx51("div", __spreadProps(__spreadValues({ className, ref: refs.setReference }, getReferenceProps()), { children })),
    /* @__PURE__ */ jsx51(FloatingPortal3, { children: /* @__PURE__ */ jsx51(
      Transition4,
      {
        show: isOpen,
        as: React29.Fragment,
        enter: "transition ease-linear duration-100",
        enterFrom: "opacity-0 scale-90",
        enterTo: "opacity-100",
        leave: "transition ease-linear duration-100",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0 scale-90",
        children: /* @__PURE__ */ jsxs34(
          "div",
          __spreadProps(__spreadValues({
            ref: refs.setFloating,
            style: {
              position: strategy,
              top: y != null ? y : 0,
              left: x != null ? x : 0,
              width: "max-content",
              maxWidth: maxWidth || 250,
              zIndex: 41
            }
          }, getFloatingProps()), {
            children: [
              /* @__PURE__ */ jsx51(FloatingArrow, { ref: arrowRef, context, className: "fill-ds9-foreground" }),
              /* @__PURE__ */ jsx51("div", { className: "overflow-auto rounded-ds9 bg-ds9-foreground text-ds9-background px-3 py-2 text-sm text-center", children: contents })
            ]
          })
        )
      }
    ) })
  ] });
}, "Tooltip");
var tooltip_default = Tooltip;

// src/components/menu.tsx
import { Fragment as Fragment5, jsx as jsx52, jsxs as jsxs35 } from "react/jsx-runtime";
var MenuDivider = /* @__PURE__ */ __name(() => {
  return /* @__PURE__ */ jsx52("div", { className: "my-3 border-t border-ds9-200 dark:border-ds9-700" });
}, "MenuDivider");
var MenuItem = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    children,
    disabled,
    disabledReason,
    red,
    description,
    prefix,
    suffix
  } = _b, props = __objRest(_b, [
    "children",
    "disabled",
    "disabledReason",
    "red",
    "description",
    "prefix",
    "suffix"
  ]);
  const item = /* @__PURE__ */ jsx52(HeadlessMenu.Item, { disabled, children: ({ active, disabled: disabled2 }) => {
    const className = twcn3(
      "group block px-2 text-sm cursor-pointer select-none",
      {
        "bg-ds9-200 dark:bg-ds9-700 rounded": active,
        "text-ds9-foreground": !disabled2 && !red,
        "pointer-events-none text-ds9-500 dark:text-ds9-400": disabled2,
        "text-ds9-error dark:text-ds9-error-light": red && !disabled2,
        "py-2": description
      },
      props.className
    );
    const labelClassName = twcn3("flex-1 flex items-center justify-between whitespace-nowrap", {
      "h-12 sm:h-9": !description
    });
    const descriptionClassName = "mt-2 text-ds9-500 dark:text-ds9-400 max-w-xs";
    switch (props.as) {
      case "link":
        return /* @__PURE__ */ jsxs35(Link, __spreadProps(__spreadValues({}, props), { className, children: [
          /* @__PURE__ */ jsxs35(
            "div",
            {
              className: twcn3("flex items-center whitespace-nowrap", {
                "justify-between": suffix,
                "gap-2": prefix || suffix
              }),
              children: [
                prefix ? prefix : null,
                /* @__PURE__ */ jsx52("div", { className: labelClassName, children }),
                suffix ? suffix : null
              ]
            }
          ),
          description ? /* @__PURE__ */ jsx52("p", { className: descriptionClassName, children: description }) : null
        ] }));
      default:
        return /* @__PURE__ */ jsxs35("a", __spreadProps(__spreadValues({}, props), { className, children: [
          /* @__PURE__ */ jsxs35(
            "div",
            {
              className: twcn3("flex items-center whitespace-nowrap", {
                "justify-between": suffix,
                "gap-2": prefix || suffix
              }),
              children: [
                prefix ? prefix : null,
                /* @__PURE__ */ jsx52("div", { className: labelClassName, children }),
                suffix ? suffix : null
              ]
            }
          ),
          description ? /* @__PURE__ */ jsx52("p", { className: descriptionClassName, children: description }) : null
        ] }));
    }
  } });
  const desktop = useMedia(`(min-width: 640px)`, true);
  if (disabled && disabledReason) {
    return /* @__PURE__ */ jsx52(
      tooltip_default,
      {
        contents: disabledReason,
        placement: desktop ? "left" : "top",
        offset: desktop ? 16 : void 0,
        delay: 250,
        children: item
      }
    );
  }
  return item;
}, "MenuItem");
var MenuStaticItem = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx52(
    "div",
    __spreadProps(__spreadValues({}, props), {
      className: twcn3(
        "flex items-center justify-between whitespace-nowrap px-2 text-sm text-ds9-foreground",
        props.className
      ),
      children
    })
  );
}, "MenuStaticItem");
var Menu = /* @__PURE__ */ __name(({
  skeleton,
  disabled,
  className,
  button,
  buttonClassName,
  buttonSize,
  label,
  variant,
  placement,
  skipSSRSkeleton,
  children
}) => {
  if (buttonSize === "s" && variant !== "context") {
    console.warn("Menu: buttonSize='small' is only supported with variant='context'");
  }
  let defaultButton = /* @__PURE__ */ jsxs35(Fragment5, { children: [
    label,
    " ",
    /* @__PURE__ */ jsx52(ChevronDownIcon3, { className: "w-5 h-5", "aria-hidden": "true" })
  ] });
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled && !skipSSRSkeleton) {
    skeleton = true;
  }
  disabled = disabled || skeleton;
  let defaultButtonClassName = twcn3(
    "rounded-ds9 font-medium px-3 max-w-full flex items-center justify-center gap-2 transition text-sm h-10",
    {
      "bg-[rgb(23,23,23)] text-ds9-background hover:bg-[rgb(56,56,56)] hover:shadow-[0_0_0_1px_rgb(235,235,235)] dark:bg-[rgb(237,237,237)] dark:text-[rgb(10,10,10)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0)] dark:hover:bg-[rgb(204,204,204)] dark:hover:shadow-[0_0_0_1px_rgb(31,31,31)]": !disabled,
      "cursor-not-allowed bg-[rgb(242,242,242)] shadow-[0_0_0_1px_rgb(235,235,235)] text-[rgb(143,143,143)] dark:bg-[rgb(26,26,26)] dark:shadow-[0_0_0_1px_rgb(46,46,46)]": disabled,
      "ds9-skeleton ds9-skeleton-rounded": skeleton
    },
    buttonClassName
  );
  if (variant === "action") {
    defaultButton = /* @__PURE__ */ jsx52(EllipsisHorizontalIcon, { className: "w-[20px] h-[20px]", "aria-hidden": "true" });
    defaultButtonClassName = twcn3(
      "rounded-ds9 font-medium h-8 w-8 max-w-full flex items-center justify-center gap-2 transition       text-[rgb(23,23,23)] bg-ds9-background shadow-[0_0_0_1px_hsl(0deg_0%_0%_/_8%)] hover:bg-[rgb(242,242,242)]       hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12)] dark:bg-[rgb(10,10,10)]       dark:shadow-[0_0_0_1px_rgba(255_255_255_/_14%)] dark:text-[rgb(237,237,237)] dark:hover:text-[rgb(237,237,237)]       dark:hover:bg-[rgb(31,31,31)] dark:hover:shadow-[0_0_0_1px_hsla(0,0%,100%,.17)]",
      {
        "ds9-skeleton ds9-skeleton-rounded": skeleton
      },
      buttonClassName
    );
  } else if (variant === "context" || button) {
    defaultButton = /* @__PURE__ */ jsx52(
      EllipsisHorizontalIcon,
      {
        className: twcn3("text-ds9-foreground", {
          "w-4 h-4": buttonSize === "s",
          "w-[18px] h-[18px]": !buttonSize || buttonSize === "m"
        }),
        "aria-hidden": "true"
      }
    );
    defaultButtonClassName = twcn3(
      "flex items-center justify-center transition-colors hover:bg-ds9-200 hover:text-ds9-foreground dark:hover:bg-ds9-700",
      {
        "h-6 w-6 rounded-[4px]": buttonSize === "s",
        "h-8 w-8 rounded-[6px]": !buttonSize || buttonSize === "m",
        "ds9-skeleton": skeleton
      },
      buttonClassName
    );
  }
  const arrowRef = useRef2(null);
  const { x, y, strategy, refs, context } = useFloating4({
    placement: placement || (variant === "action" || variant === "context" ? "bottom-end" : "bottom-start"),
    middleware: [
      offset4(9),
      flip2(),
      size4({
        apply({ availableWidth, availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight - 10}px`,
            minWidth: `${rects.reference.width}px`
          });
        }
      }),
      shift(),
      arrow2({
        element: arrowRef
      })
    ]
  });
  const hover = useHover2(context, {
    handleClose: safePolygon2()
  });
  const { getReferenceProps, getFloatingProps } = useInteractions2([hover]);
  const desktop = useMedia(`(min-width: 640px)`, true);
  return /* @__PURE__ */ jsx52(HeadlessMenu, { as: "div", className: twcn3("ds9-menu relative flex", className), children: ({ open }) => /* @__PURE__ */ jsxs35(Fragment5, { children: [
    /* @__PURE__ */ jsx52(
      HeadlessMenu.Button,
      __spreadProps(__spreadValues({
        className: defaultButtonClassName,
        disabled,
        ref: refs.setReference
      }, getReferenceProps()), {
        children: button || defaultButton
      })
    ),
    /* @__PURE__ */ jsx52(FloatingPortal4, { children: /* @__PURE__ */ jsx52(Transition5, { show: open, children: desktop ? /* @__PURE__ */ jsx52(
      Transition5.Child,
      {
        as: React30.Fragment,
        enter: "transition ease-linear duration-100",
        enterFrom: "opacity-0",
        enterTo: "opacity-100",
        leave: "transition ease-linear duration-100",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0",
        children: /* @__PURE__ */ jsxs35(
          HeadlessMenu.Items,
          __spreadProps(__spreadValues({
            static: true,
            className: "ds9-headless-menu z-40 rounded-ds9 shadow-ds9-md dark:shadow-none bg-ds9-background border border-ds9-200 dark:border-ds9-700 ring-0 focus:outline-none",
            ref: refs.setFloating,
            style: {
              position: strategy,
              top: y != null ? y : 0,
              left: x != null ? x : 0,
              width: "max-content"
            }
          }, getFloatingProps()), {
            children: [
              variant === "action" ? /* @__PURE__ */ jsx52(
                FloatingArrow2,
                {
                  ref: arrowRef,
                  context,
                  strokeWidth: 1,
                  className: "[&>path:first-of-type]:stroke-ds9-200 fill-ds9-background dark:[&>path:first-of-type]:stroke-ds9-700"
                }
              ) : null,
              /* @__PURE__ */ jsx52("div", { className: "p-2", children })
            ]
          })
        )
      }
    ) : /* @__PURE__ */ jsxs35(Fragment5, { children: [
      /* @__PURE__ */ jsx52(
        Transition5.Child,
        {
          as: React30.Fragment,
          enter: "transition-opacity ease-linear duration-100",
          enterFrom: "opacity-0",
          enterTo: "opacity-100",
          leave: "transition-opacity ease-linear duration-100",
          leaveFrom: "opacity-100",
          leaveTo: "opacity-0",
          children: /* @__PURE__ */ jsx52("div", { className: "fixed inset-0 bg-ds9-black bg-opacity-25 dark:bg-opacity-75 z-40" })
        }
      ),
      /* @__PURE__ */ jsx52(
        Transition5.Child,
        {
          as: React30.Fragment,
          enter: "transition duration-100 ease-out",
          enterFrom: "transform translate-y-full",
          enterTo: "transform translate-y-0",
          leave: "transition duration-100 ease-out",
          leaveFrom: "transform translate-y-0",
          leaveTo: "transform translate-y-full",
          children: /* @__PURE__ */ jsx52(
            HeadlessMenu.Items,
            {
              static: true,
              className: "ds9-headless-menu rounded-t-lg shadow-ds9-md dark:shadow-[0_0_0_1px] dark:shadow-ds9-700 bg-ds9-background ring-0 focus:outline-none fixed z-40 bottom-0 top-auto inset-x-0",
              children: /* @__PURE__ */ jsx52("div", { className: "p-2 rounded-ds9 border border-ds9-200 dark:border-ds9-700", children })
            }
          )
        }
      )
    ] }) }) })
  ] }) });
}, "Menu");
Menu.Item = MenuItem;
Menu.StaticItem = MenuStaticItem;
Menu.Divider = MenuDivider;
var menu_default = Menu;

// src/components/modal.tsx
import React31 from "react";
import { Dialog as Dialog2, Transition as Transition6 } from "@headlessui/react";
import cn23 from "classnames";
import { jsx as jsx53, jsxs as jsxs36 } from "react/jsx-runtime";
var ModalTitle = /* @__PURE__ */ __name(({ children, center }) => {
  return /* @__PURE__ */ jsx53("h3", { className: cn23("text-2xl leading-6 font-semibold mb-6", { "text-center": center }), children });
}, "ModalTitle");
var ModalBody = /* @__PURE__ */ __name(({ children }) => {
  return /* @__PURE__ */ jsx53("div", { className: "overflow-y-auto text-sm p-6 sm:p-8 bg-ds9-background first-of-type:rounded-t-lg", children });
}, "ModalBody");
var ModalInset = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx53(
    "main",
    {
      className: cn23(
        "-mx-6 -mb-6 sm:-mx-8 sm:-mb-8 p-6 bg-ds9-50 dark:bg-ds9-900 border-t border-ds9-200 dark:border-ds9-700",
        className
      ),
      children
    }
  );
}, "ModalInset");
var ModalFooter = /* @__PURE__ */ __name(({ children }) => {
  return /* @__PURE__ */ jsx53("footer", { className: "sticky bottom-0 rounded-b-lg flex justify-between p-4 bg-ds9-50 dark:bg-ds9-900 after:absolute after:-z-10 after:inset-0 after:shadow-[0_0_0_1px_rgba(0,0,0,0.07)] dark:after:shadow-[0_0_0_1px_hsla(0,0%,100%,0.2)]", children });
}, "ModalFooter");
var Modal = /* @__PURE__ */ __name(({
  children,
  width,
  static: staticDialog,
  animateInitial,
  open,
  setOpen,
  disableClose,
  initialFocus,
  afterClose
}) => {
  const desktop = use_media_default(`(min-width: 640px)`, true);
  return /* @__PURE__ */ jsx53(Transition6, { show: open != null ? open : staticDialog, as: React31.Fragment, appear: animateInitial, afterLeave: afterClose, children: /* @__PURE__ */ jsxs36(
    Dialog2,
    {
      static: staticDialog,
      as: "div",
      className: "ds9-modal relative z-30",
      onClose: disableClose ? () => null : setOpen,
      initialFocus,
      children: [
        /* @__PURE__ */ jsx53(
          Transition6.Child,
          {
            as: React31.Fragment,
            enter: "transition-opacity ease-linear duration-150",
            enterFrom: "opacity-0",
            enterTo: "opacity-100",
            leave: "transition-opacity ease-linear duration-150",
            leaveFrom: "opacity-100",
            leaveTo: "opacity-0",
            children: /* @__PURE__ */ jsx53("div", { className: "fixed inset-0 bg-ds9-black bg-opacity-25 dark:bg-opacity-75" })
          }
        ),
        desktop ? /* @__PURE__ */ jsx53("div", { className: "fixed inset-0 z-30 overflow-y-auto", children: /* @__PURE__ */ jsx53("div", { className: "flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0", children: /* @__PURE__ */ jsx53(
          Transition6.Child,
          {
            as: React31.Fragment,
            enter: "transition duration-150 ease-out",
            enterFrom: "transform opacity-0 scale-75",
            enterTo: "transform opacity-100 scale-100",
            leave: "transition duration-150 ease-in",
            leaveFrom: "transform opacity-100 scale-100",
            leaveTo: "transform opacity-0 scale-75",
            children: /* @__PURE__ */ jsx53(
              Dialog2.Panel,
              {
                className: "relative flex flex-col max-h-[95vh] transform overflow-hidden rounded-lg text-left shadow-ds9-lg dark:shadow-ds9-dark-lg transition-all",
                style: { width },
                children
              }
            )
          }
        ) }) }) : /* @__PURE__ */ jsx53(
          Transition6.Child,
          {
            as: React31.Fragment,
            enter: "transition duration-150 ease-out",
            enterFrom: "transform translate-y-full",
            enterTo: "transform translate-y-0",
            leave: "transition duration-150 ease-out",
            leaveFrom: "transform translate-y-0",
            leaveTo: "transform translate-y-full",
            children: /* @__PURE__ */ jsx53("div", { className: "overflow-hidden rounded-t-lg shadow-ds9-md dark:shadow-[0_0_0_1px] dark:shadow-ds9-700 bg-ds9-background ring-0 focus:outline-none fixed z-30 bottom-0 top-auto inset-x-0", children: /* @__PURE__ */ jsx53(Dialog2.Panel, { className: "flex flex-col max-h-[95vh]", children }) })
          }
        )
      ]
    }
  ) });
}, "Modal");
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Inset = ModalInset;
Modal.Footer = ModalFooter;
var modal_default = Modal;

// src/components/note.tsx
import "react";
import cn24 from "classnames";
import { jsx as jsx54, jsxs as jsxs37 } from "react/jsx-runtime";
var Note = /* @__PURE__ */ __name(({ children, className }) => {
  return /* @__PURE__ */ jsx54(
    "div",
    {
      className: cn24(
        "px-3 py-1 min-h-[40px] flex items-center rounded-ds9 border border-ds9-200 dark:border-ds9-700 text-sm leading-6",
        className
      ),
      children: /* @__PURE__ */ jsxs37("span", { children: [
        /* @__PURE__ */ jsx54("span", { className: "leading-normal", children: /* @__PURE__ */ jsx54("b", { children: "Note:" }) }),
        " ",
        children
      ] })
    }
  );
}, "Note");
var note_default = Note;

// src/components/overflow_list.tsx
import {
  FloatingArrow as FloatingArrow3,
  arrow as arrow3,
  flip as flip3,
  offset as offset5,
  safePolygon as safePolygon3,
  size as size5,
  useFloating as useFloating5,
  useHover as useHover3,
  useInteractions as useInteractions3
} from "@floating-ui/react";
import { Transition as Transition7 } from "@headlessui/react";
import React33, { useRef as useRef3, useState as useState4 } from "react";
import { jsx as jsx55, jsxs as jsxs38 } from "react/jsx-runtime";
var OverflowList = /* @__PURE__ */ __name(({ className, children }) => {
  const arrowRef = useRef3(null);
  const [isOpen, setIsOpen] = useState4(false);
  const { x, y, strategy, refs, context } = useFloating5({
    placement: "top",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset5(8),
      flip3(),
      arrow3({
        element: arrowRef
      }),
      size5({
        apply({ availableHeight, elements }) {
          var _a;
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight - 10}px`
          });
          Object.assign(((_a = elements.floating.querySelector(":scope > div")) == null ? void 0 : _a.style) || {}, {
            maxHeight: `${availableHeight - 10}px`
          });
        }
      })
    ]
  });
  const hover = useHover3(context, {
    handleClose: safePolygon3()
  });
  const { getReferenceProps, getFloatingProps } = useInteractions3([hover]);
  if (!children) {
    return null;
  }
  return /* @__PURE__ */ jsxs38("div", { className, children: [
    /* @__PURE__ */ jsxs38(
      "div",
      __spreadProps(__spreadValues({
        className: "text-sm leading-none font-medium rounded-full bg-ds9-foreground dark:bg-ds9-300 text-ds9-background px-2 h-5 flex items-center justify-center tnum",
        ref: refs.setReference
      }, getReferenceProps()), {
        children: [
          "+",
          React33.Children.count(children)
        ]
      })
    ),
    /* @__PURE__ */ jsx55(
      Transition7,
      {
        show: isOpen,
        as: React33.Fragment,
        enter: "transition ease-linear duration-100",
        enterFrom: "opacity-0 scale-90",
        enterTo: "opacity-100",
        leave: "transition ease-linear duration-100",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0 scale-90",
        children: /* @__PURE__ */ jsxs38(
          "div",
          __spreadProps(__spreadValues({
            ref: refs.setFloating,
            style: {
              position: strategy,
              top: y != null ? y : 0,
              left: x != null ? x : 0,
              width: "max-content",
              zIndex: 2
            }
          }, getFloatingProps()), {
            children: [
              /* @__PURE__ */ jsx55(FloatingArrow3, { ref: arrowRef, context, className: "fill-ds9-foreground" }),
              /* @__PURE__ */ jsx55("div", { className: "overflow-auto rounded-ds9 bg-ds9-foreground text-ds9-background px-3 py-2", children })
            ]
          })
        )
      }
    )
  ] });
}, "OverflowList");
var overflow_list_default = OverflowList;

// src/components/page_header.tsx
import React34, { useCallback as useCallback6, useRef as useRef4 } from "react";
import twcn4 from "classnames";
import { useLocation } from "react-router-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { jsx as jsx56, jsxs as jsxs39 } from "react/jsx-runtime";
var cleanState = /* @__PURE__ */ __name((state) => {
  const clean = __spreadValues({}, state);
  Object.keys(clean).forEach((key) => {
    if (clean[key] === void 0 || clean[key] === null) {
      delete clean[key];
    }
  });
  return clean;
}, "cleanState");
var ForwardableNavItem = /* @__PURE__ */ __name((_a, ref) => {
  var _b = _a, { children, keepBackState, __ds9_docs } = _b, props = __objRest(_b, ["children", "keepBackState", "__ds9_docs"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  const location = __ds9_docs ? { state: {} } : useLocation();
  if (keepBackState && location.state && location.state.backState && !objEmpty(location.state.backState)) {
    const state = location.state.backState;
    props.state = { backState: state };
  }
  props.className = ({ isActive }) => twcn4(
    "cursor-pointer inline-block py-4 px-3 relative text-sm leading-tight transition-colors outline-none -mb-px",
    {
      "text-ds9-foreground": isActive,
      "text-ds9-600 dark:text-ds9-400 hover:text-ds9-foreground dark:hover:text-ds9-foreground": !isActive,
      "before:absolute before:content-[''] before:h-0 before:border-b-2 before:border-ds9-foreground before:left-2 before:right-2 before:bottom-0": isActive
    },
    customClassName
  );
  return /* @__PURE__ */ jsx56(NavLink, __spreadProps(__spreadValues({}, props), { ref, __ds9_docs, children }));
}, "ForwardableNavItem");
var NavItem = React34.forwardRef(ForwardableNavItem);
var PageHeader = /* @__PURE__ */ __name(({
  title,
  decoration,
  subtitle,
  skeleton,
  details,
  backLink,
  secondRow,
  menu,
  nav,
  children,
  __ds9_docs
}) => {
  const location = __ds9_docs ? { state: {} } : useLocation();
  if (backLink && location.state && location.state.backState && !objEmpty(location.state.backState)) {
    const state = location.state.backState;
    const params = new URLSearchParams(cleanState(state));
    backLink = `${backLink}?${params.toString()}`;
  }
  const navHover = useRef4(null);
  const navMouseOver = useCallback6((e) => {
    const left = e.currentTarget.offsetLeft;
    const width = e.currentTarget.clientWidth;
    requestAnimationFrame(() => {
      if (navHover.current) {
        navHover.current.style.transform = `translateX(${left}px)`;
        navHover.current.style.width = `${width}px`;
      }
    });
  }, []);
  return /* @__PURE__ */ jsx56(
    "header",
    {
      className: twcn4("ds9-pageheader border-b border-ds9-200 bg-ds9-background dark:border-ds9-700", {
        "pt-12": nav,
        "py-12": !backLink && !nav,
        "pt-12 pb-6": backLink && !nav
      }),
      children: /* @__PURE__ */ jsx56(container_default, { className: "sm:flex sm:items-center sm:justify-between", children: /* @__PURE__ */ jsxs39("div", { className: "min-w-0 flex-1", children: [
        /* @__PURE__ */ jsxs39("div", { className: "flex flex-col sm:flex-row sm:items-center gap-6", children: [
          /* @__PURE__ */ jsxs39("div", { className: "flex-1 flex items-center gap-4", children: [
            /* @__PURE__ */ jsxs39("h1", { className: "flex-1 flex items-center gap-2 text-2xl font-medium leading-7 text-ds9-foreground sm:truncate sm:text-[32px] sm:leading-10 sm:tracking-tight", children: [
              /* @__PURE__ */ jsx56("span", { className: twcn4({ "ds9-skeleton": skeleton }), children: title }),
              decoration
            ] }),
            menu ? /* @__PURE__ */ jsx56("div", { className: "flex space-x-4", children: menu }) : null
          ] }),
          React34.Children.count(children) > 0 ? /* @__PURE__ */ jsx56("div", { className: "flex space-x-4", children }) : null
        ] }),
        subtitle ? /* @__PURE__ */ jsx56("p", { className: "mt-4 text-sm text-ds9-500 dark:text-ds9-400", children: subtitle }) : null,
        details ? /* @__PURE__ */ jsx56("div", { className: "mt-6 flex -m-3 flex-wrap", children: details.map(({ label, value, title: title2, tooltip }) => /* @__PURE__ */ jsx56("div", { className: "p-3 basis-1/2 sm:basis-1/3 lg:basis-1/4", children: /* @__PURE__ */ jsxs39("dl", { children: [
          /* @__PURE__ */ jsxs39("dt", { className: "mb-2 flex items-center gap-1", children: [
            /* @__PURE__ */ jsx56("span", { className: "text-xs leading-[14px] font-medium uppercase text-ds9-500 dark:text-ds9-400", children: label }),
            tooltip ? /* @__PURE__ */ jsx56(tooltip_default, { contents: tooltip, children: /* @__PURE__ */ jsx56(InformationCircleIcon, { className: "w-3 h-3 group-hover:text-ds9-success-light" }) }) : null
          ] }),
          /* @__PURE__ */ jsx56(
            "dd",
            {
              className: twcn4("text-sm leading-4 font-medium text-ds9-foreground", {
                "pt-1": typeof value !== "object"
              }),
              title: title2,
              children: value != null ? value : /* @__PURE__ */ jsx56("span", { className: twcn4({ "ds9-skeleton": skeleton }), children: "-" })
            }
          )
        ] }) }, label)) }) : null,
        secondRow ? /* @__PURE__ */ jsx56("div", { className: "mt-4", children: secondRow }) : null,
        backLink ? /* @__PURE__ */ jsx56("div", { className: "mt-6", children: /* @__PURE__ */ jsx56(
          Link,
          {
            to: backLink,
            className: "header-back-link hover:underline text-sky-600 dark:text-sky-400",
            __ds9_docs,
            children: /* @__PURE__ */ jsxs39("small", { className: "text-sm leading-normal", children: [
              /* @__PURE__ */ jsx56("span", { className: "font-ds9arial align-top", children: "\u2190" }),
              " Back"
            ] })
          }
        ) }) : null,
        nav && nav.length > 0 ? /* @__PURE__ */ jsx56("div", { className: "mt-6 ml-[-7px] flex relative z-0", children: /* @__PURE__ */ jsxs39("nav", { className: "flex group", children: [
          /* @__PURE__ */ jsx56(
            "div",
            {
              ref: navHover,
              className: "absolute left-0 right-0 top-2 h-8 w-0 -z-10 rounded bg-ds9-200 dark:bg-ds9-700 opacity-0 group-hover:opacity-100 transition-all"
            }
          ),
          nav.map((navpr, i) => /* @__PURE__ */ jsx56(NavItem, __spreadProps(__spreadValues({}, navpr), { onMouseEnter: navMouseOver, children: navpr.children }), i))
        ] }) }) : null
      ] }) })
    }
  );
}, "PageHeader");
var page_header_default = PageHeader;

// src/components/progress_circle.tsx
import "react";
import cn25 from "classnames";
import { jsx as jsx57, jsxs as jsxs40 } from "react/jsx-runtime";
var ProgressCircle = /* @__PURE__ */ __name(({ inverted, light, dark, progress }) => {
  const themeForced = light || dark;
  progress = progress / 100;
  return /* @__PURE__ */ jsx57("div", { "aria-label": "Loading...", role: "progressbar", children: /* @__PURE__ */ jsxs40("svg", { className: "h-8 w-8 -rotate-90", viewBox: "0 0 27.5 27.5", children: [
    /* @__PURE__ */ jsx57(
      "circle",
      {
        cx: "13.75",
        cy: "13.75",
        r: "12",
        strokeWidth: "3.5",
        fill: "none",
        className: cn25({
          "stroke-ds9-200 dark:stroke-ds9-800": !inverted && !themeForced,
          "stroke-ds9-800 dark:stroke-ds9-200": inverted && !themeForced,
          "stroke-ds9-200": light,
          "stroke-ds9-800": dark
        })
      }
    ),
    /* @__PURE__ */ jsx57(
      "circle",
      {
        cx: "13.75",
        cy: "13.75",
        r: "12",
        strokeWidth: "3.5",
        fill: "none",
        className: cn25("transition-[stroke-dasharray]", {
          "stroke-ds9-800 dark:stroke-ds9-200": !inverted && !themeForced,
          "stroke-ds9-200 dark:stroke-ds9-800": inverted && !themeForced,
          "stroke-ds9-800": light,
          "stroke-ds9-200": dark
        }),
        pathLength: 1,
        strokeDasharray: `${progress} 1px`
      }
    )
  ] }) });
}, "ProgressCircle");
var progress_circle_default = ProgressCircle;

// src/components/radio_group.tsx
import "react";
import cn26 from "classnames";
import {
  RadioGroup as HeadlessRadioGroup
} from "@headlessui/react";
import { Fragment as Fragment6, jsx as jsx58, jsxs as jsxs41 } from "react/jsx-runtime";
var RadioItem = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { label, labelClassName } = _b, props = __objRest(_b, ["label", "labelClassName"]);
  const customClassName = typeof props.className === "string" ? props.className : "";
  const funcClassName = typeof props.className === "function" ? props.className : () => "";
  props.className = (bag) => cn26("inline-flex text-sm group", customClassName, funcClassName(bag));
  return /* @__PURE__ */ jsx58(HeadlessRadioGroup.Option, __spreadProps(__spreadValues({}, props), { "data-value": props.value, children: ({ checked }) => /* @__PURE__ */ jsxs41(Fragment6, { children: [
    /* @__PURE__ */ jsxs41("span", { className: "cursor-pointer flex items-center p-0.5 -m-0.5", children: [
      "\xA0",
      /* @__PURE__ */ jsx58(
        "span",
        {
          className: cn26("relative rounded-full w-[1rem] h-[1rem] border transition-colors", {
            "border-ds9-500 dark:border-ds9-400 group-hover:border-ds9-foreground": !checked,
            "border-ds9-foreground": checked
          }),
          children: /* @__PURE__ */ jsx58(
            "span",
            {
              className: cn26(
                "absolute top-1/2 left-1/2 rounded-full w-[0.5rem] h-[0.5rem] -translate-x-1/2 -translate-y-1/2 transition-transform",
                {
                  "scale-0 bg-ds9-500 dark:bg-ds9-400": !checked,
                  "scale-100 bg-ds9-foreground": checked
                }
              )
            }
          )
        }
      )
    ] }),
    label ? /* @__PURE__ */ jsx58("span", { className: cn26("ml-2 cursor-pointer", labelClassName), children: label }) : null
  ] }) }));
}, "RadioItem");
var RadioGroup = /* @__PURE__ */ __name(({ value, onChange, label, name, disabled, children, className }) => {
  return /* @__PURE__ */ jsxs41(HeadlessRadioGroup, { value, onChange, name, disabled, className, children: [
    label ? /* @__PURE__ */ jsx58(HeadlessRadioGroup.Label, { className: "block uppercase text-xs leading-5 mb-2 font-medium text-ds9-500 dark:text-ds9-400", children: label }) : null,
    /* @__PURE__ */ jsx58("div", { className: "flex flex-col space-y-3", children })
  ] });
}, "RadioGroup");
RadioGroup.Item = RadioItem;
var radio_group_default = RadioGroup;

// src/components/tag.tsx
import "react";
import cn27 from "classnames";
import { jsx as jsx59 } from "react/jsx-runtime";
var Tag = /* @__PURE__ */ __name(({ children, skeleton, className }) => {
  return /* @__PURE__ */ jsx59("div", { className: "flex truncate h-8 text-ds9-600 dark:text-ds9-300", children: /* @__PURE__ */ jsx59(
    "div",
    {
      className: cn27(
        "bg-ds9-50 dark:bg-ds9-900 border border-ds9-200 dark:border-ds9-700 rounded-ds9 text-sm px-1.5 flex items-center truncate",
        { "ds9-skeleton ds9-skeleton-rounded": skeleton },
        className
      ),
      children
    }
  ) });
}, "Tag");
var tag_default = Tag;

// src/components/theme_switcher.tsx
import React38, { useEffect, useId as useId6, useState as useState5 } from "react";
import { MoonIcon, SunIcon, ComputerDesktopIcon, CheckIcon as CheckIcon2 } from "@heroicons/react/24/outline";
import cn28 from "classnames";
import {
  MoonIcon as MoonIconMini,
  SunIcon as SunIconMini,
  ComputerDesktopIcon as ComputerDesktopIconMini
} from "@heroicons/react/16/solid";
import { Fragment as Fragment7, jsx as jsx60, jsxs as jsxs42 } from "react/jsx-runtime";
var ThemeSwitcher = /* @__PURE__ */ __name(({ className, top, defaultTheme, skipSystem, variant }) => {
  const { theme: adminTheme } = useDS9Context();
  const [theme, setTheme] = useState5(defaultTheme || 0 /* system */);
  useEffect(() => {
    setTheme(adminTheme.current());
  }, [adminTheme]);
  useEffect(() => {
    const handleTheme = /* @__PURE__ */ __name((theme2) => {
      setTheme(theme2);
    }, "handleTheme");
    adminTheme.addChangeListener(handleTheme);
    return () => adminTheme.removeChangeListener(handleTheme);
  }, [adminTheme]);
  const updateTheme = /* @__PURE__ */ __name((value) => {
    adminTheme.update(value);
  }, "updateTheme");
  const themeOptions = [
    { label: "Light", value: 1 /* light */, icon: SunIcon },
    { label: "Dark", value: 2 /* dark */, icon: MoonIcon }
  ];
  if (!skipSystem) {
    themeOptions.unshift({ label: "System", value: 0 /* system */, icon: ComputerDesktopIcon });
  }
  const id = useId6();
  if (variant === "toggle") {
    return /* @__PURE__ */ jsxs42(
      "fieldset",
      {
        className: cn28(
          "shadow-[rgba(0,0,0,0.08)_0px_0px_0px_1px] dark:shadow-[rgba(255,255,255,0.145)_0px_0px_0px_1px] m-0 flex h-8 w-fit rounded-full border-0 p-0",
          className
        ),
        children: [
          /* @__PURE__ */ jsx60("legend", { className: "sr-only", children: "Select a display theme:" }),
          /* @__PURE__ */ jsx60(
            "input",
            {
              "aria-label": "system",
              id: `theme-switch-system-${id}`,
              type: "radio",
              value: "system",
              className: "sr-only",
              defaultChecked: theme === 0 /* system */
            }
          ),
          /* @__PURE__ */ jsx60("span", { className: "h-full", children: /* @__PURE__ */ jsxs42(
            "label",
            {
              htmlFor: `theme-switch-system-${id}`,
              className: cn28(
                "relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors",
                {
                  "text-ds9-foreground shadow-[oklch(0.939_0_0)_0px_0px_0px_1px,rgba(0,0,0,0.05)_0px_1px_2px_0px] dark:shadow-[oklch(0.301_0_0)_0px_0px_0px_1px,rgba(255,255,255,0.06)_0px_1px_2px_0px]": theme === 0 /* system */,
                  "text-ds9-500 hover:text-ds9-foreground": theme !== 0 /* system */
                }
              ),
              onClick: () => updateTheme(0 /* system */),
              children: [
                /* @__PURE__ */ jsx60("span", { className: "sr-only", children: "system" }),
                /* @__PURE__ */ jsx60(ComputerDesktopIconMini, { className: "h-4 w-4 flex-shrink-0" })
              ]
            }
          ) }),
          /* @__PURE__ */ jsxs42("span", { className: "h-full", children: [
            /* @__PURE__ */ jsx60(
              "input",
              {
                "aria-label": "light",
                id: `theme-switch-light-${id}`,
                type: "radio",
                value: "light",
                className: "sr-only",
                defaultChecked: theme === 1 /* light */
              }
            ),
            /* @__PURE__ */ jsxs42(
              "label",
              {
                htmlFor: `theme-switch-light-${id}`,
                className: cn28(
                  "relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors",
                  {
                    "text-ds9-foreground shadow-[oklch(0.939_0_0)_0px_0px_0px_1px,rgba(0,0,0,0.05)_0px_1px_2px_0px] dark:shadow-[oklch(0.301_0_0)_0px_0px_0px_1px,rgba(255,255,255,0.06)_0px_1px_2px_0px]": theme === 1 /* light */,
                    "text-ds9-500  hover:text-ds9-foreground": theme !== 1 /* light */
                  }
                ),
                onClick: () => updateTheme(1 /* light */),
                children: [
                  /* @__PURE__ */ jsx60("span", { className: "sr-only", children: "light" }),
                  /* @__PURE__ */ jsx60(SunIconMini, { className: "h-4 w-4 flex-shrink-0" })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs42("span", { className: "h-full", children: [
            /* @__PURE__ */ jsx60(
              "input",
              {
                "aria-label": "dark",
                id: `theme-switch-dark-${id}`,
                type: "radio",
                value: "dark",
                className: "sr-only",
                defaultChecked: theme === 2 /* dark */
              }
            ),
            /* @__PURE__ */ jsxs42(
              "label",
              {
                htmlFor: `theme-switch-dark-${id}`,
                className: cn28(
                  "relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors",
                  {
                    "text-ds9-foreground shadow-[oklch(0.939_0_0)_0px_0px_0px_1px,rgba(0,0,0,0.05)_0px_1px_2px_0px] dark:shadow-[oklch(0.301_0_0)_0px_0px_0px_1px,rgba(255,255,255,0.06)_0px_1px_2px_0px]": theme === 2 /* dark */,
                    "text-ds9-500  hover:text-ds9-foreground": theme !== 2 /* dark */
                  }
                ),
                onClick: () => updateTheme(2 /* dark */),
                children: [
                  /* @__PURE__ */ jsx60("span", { className: "sr-only", children: "dark" }),
                  /* @__PURE__ */ jsx60(MoonIconMini, { className: "h-4 w-4 flex-shrink-0" })
                ]
              }
            )
          ] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsx60("div", { className: cn28("relative", className), children: /* @__PURE__ */ jsx60(
    select_default,
    {
      placement: top ? "top" : "bottom",
      selected: theme,
      onSelected: updateTheme,
      selectedLabel: (theme2) => /* @__PURE__ */ jsxs42("span", { className: "flex items-center", children: [
        theme2 === 0 /* system */ ? /* @__PURE__ */ jsx60(ComputerDesktopIcon, { className: "h-5 w-5 flex-shrink-0 rounded-full dark:text-ds9-500 dark:group-hover:text-ds9-foreground" }) : null,
        theme2 === 2 /* dark */ ? /* @__PURE__ */ jsx60(MoonIcon, { className: "h-5 w-5 flex-shrink-0 rounded-full dark:text-ds9-500 dark:group-hover:text-ds9-foreground" }) : null,
        theme2 === 1 /* light */ ? /* @__PURE__ */ jsx60(SunIcon, { className: "h-5 w-5 flex-shrink-0 rounded-full dark:text-ds9-500 dark:group-hover:text-ds9-foreground" }) : null,
        /* @__PURE__ */ jsxs42("span", { className: "ml-3 block truncate", children: [
          theme2 === 0 /* system */ ? "System" : null,
          theme2 === 2 /* dark */ ? "Dark" : null,
          theme2 === 1 /* light */ ? "Light" : null
        ] })
      ] }),
      children: themeOptions.map((option) => /* @__PURE__ */ jsx60(select_default.Option, { className: "relative py-2 pl-3 pr-9", value: option.value, children: ({ selected, active }) => /* @__PURE__ */ jsxs42(Fragment7, { children: [
        /* @__PURE__ */ jsxs42("div", { className: "flex items-center", children: [
          option.icon ? React38.createElement(option.icon, {
            className: "flex-shrink-0 h-5 w-5 rounded-full"
          }) : null,
          /* @__PURE__ */ jsx60(
            "span",
            {
              className: cn28("block truncate", {
                "font-semibold": selected,
                "font-normal": !selected,
                "ml-8": !option.icon,
                "ml-3": option.icon
              }),
              children: option.label || option.value
            }
          )
        ] }),
        selected ? /* @__PURE__ */ jsx60(
          "span",
          {
            className: cn28("absolute inset-y-0 right-0 flex items-center pr-4", {
              "text-ds9-foreground": active,
              "text-indigo-600 dark:text-indigo-400": !active
            }),
            children: /* @__PURE__ */ jsx60(CheckIcon2, { className: "h-5 w-5", "aria-hidden": "true" })
          }
        ) : null
      ] }) }, option.value))
    }
  ) });
}, "ThemeSwitcher");
var theme_switcher_default = ThemeSwitcher;

// src/components/time.tsx
import { useEffect as useEffect2, useState as useState6 } from "react";
import { jsx as jsx61 } from "react/jsx-runtime";
var TimeSinceLive = /* @__PURE__ */ __name(({ time, short, className }) => {
  const date = new Date(typeof time === "number" ? time : Date.parse(time));
  const [timeSince, setTimeSince] = useState6(sinceHuman(time, short));
  useEffect2(() => {
    const timer = setInterval(() => {
      setTimeSince(sinceHuman(time, short));
    }, 1e3);
    return () => clearInterval(timer);
  }, [time, short]);
  return /* @__PURE__ */ jsx61("span", { className, title: date.toString(), children: timeSince });
}, "TimeSinceLive");
var TimeSince = /* @__PURE__ */ __name((props) => {
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled) {
    return /* @__PURE__ */ jsx61("span", { className: props.className, children: "-" });
  }
  return /* @__PURE__ */ jsx61(TimeSinceLive, __spreadValues({}, props));
}, "TimeSince");
var TimeHumanLive = /* @__PURE__ */ __name(({ time, capitalize, className }) => {
  const date = new Date(typeof time === "number" ? time : Date.parse(time));
  const [currentTimeHuman, setTimeHuman] = useState6(timeHuman(time, capitalize));
  useEffect2(() => {
    const timer = setInterval(() => {
      setTimeHuman(timeHuman(time, capitalize));
    }, 1e3 * 60);
    return () => clearInterval(timer);
  }, [time, capitalize]);
  return /* @__PURE__ */ jsx61("span", { className, title: date.toString(), children: currentTimeHuman });
}, "TimeHumanLive");
var TimeHuman = /* @__PURE__ */ __name((props) => {
  const ssrDisabled = useSSRDisabled();
  if (ssrDisabled) {
    return /* @__PURE__ */ jsx61("span", { className: props.className, children: "-" });
  }
  return /* @__PURE__ */ jsx61(TimeHumanLive, __spreadValues({}, props));
}, "TimeHuman");

// src/components/tiptap_display.tsx
import { useEffect as useEffect3, useMemo as useMemo2 } from "react";
import cn29 from "classnames";
import "@tiptap/core";
import { generateHTML } from "@tiptap/html";
import parse from "html-react-parser";
import { EditorContent, useEditor } from "@tiptap/react";
import { Fragment as Fragment8, jsx as jsx62 } from "react/jsx-runtime";
var TiptapDisplay = /* @__PURE__ */ __name(({ fragment, className, json, extensions }) => {
  const output = useMemo2(() => {
    return parse(generateHTML(json, extensions));
  }, [json, extensions]);
  return fragment ? /* @__PURE__ */ jsx62(Fragment8, { children: output }) : /* @__PURE__ */ jsx62("div", { className: cn29("ds9-tiptap-display", className), children: output });
}, "TiptapDisplay");
var TiptapDisplayInteractive = /* @__PURE__ */ __name(({ fragment, className, json, extensions }) => {
  const ssr = useSSRDisabled();
  const editor = useEditor({
    editable: false,
    injectCSS: false,
    extensions,
    content: json
  });
  useEffect3(() => {
    if (editor) {
      queueMicrotask(() => {
        editor.commands.setContent(json);
      });
    }
  }, [editor, json]);
  if (ssr) {
    return /* @__PURE__ */ jsx62(TiptapDisplay, { fragment, className, json, extensions });
  }
  return /* @__PURE__ */ jsx62(EditorContent, { editor, className: cn29("ds9-tiptap-display", className) });
}, "TiptapDisplayInteractive");
var tiptap_display_default = TiptapDisplay;

// src/components/tiptap_editor.tsx
import React41, { useEffect as useEffect4, useRef as useRef5, useState as useState7 } from "react";
import cn30 from "classnames";
import { useEditor as useEditor2, EditorContent as EditorContent2 } from "@tiptap/react";
import "@tiptap/core";
import { CheckIcon as CheckIcon3 } from "@heroicons/react/20/solid";

// src/icons/editor/align-center.tsx
import { jsx as jsx63 } from "react/jsx-runtime";
var SvgAlignCenter = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx63("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx63("path", { d: "M491 76H21c-8.3 0-15-6.7-15-15V21c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0 286.7H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0-143.4H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zM391 506H121c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h270c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15z" }) })), "SvgAlignCenter");
var align_center_default = SvgAlignCenter;

// src/icons/editor/align-left.tsx
import { jsx as jsx64 } from "react/jsx-runtime";
var SvgAlignLeft = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx64("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx64("path", { d: "M491 76H21c-8.3 0-15-6.7-15-15V21c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0 286.7H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zM291 506H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h270c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm200-286.7H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15z" }) })), "SvgAlignLeft");
var align_left_default = SvgAlignLeft;

// src/icons/editor/align-no.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var SvgAlignNo = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx65("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx65("path", { d: "M56 217.67v-13.22l28.22 28.22H71c-8.28 0-15-6.72-15-15zm0 76.66c0-8.28 6.72-15 15-15h59.88l70 70H71c-8.28 0-15-6.71-15-15zM247.55 396l70 70H71c-8.28 0-15-6.72-15-15v-40c0-8.28 6.72-15 15-15zm254.062 77.321-28.291 28.291c-5.86 5.85-15.351 5.85-21.211 0L10.388 59.89c-5.85-5.86-5.85-15.351 0-21.211l28.291-28.291c5.86-5.85 15.351-5.85 21.211 0l35.604 35.604h345.514c8.28 0 15.001 6.72 15.001 15.001v40.002c0 8.28-6.72 15.001-15.001 15.001H165.496l46.662 46.672h228.849c8.28 0 15.001 6.71 15.001 15.001v40.002c0 8.28-6.72 15.001-15.001 15.001H282.161l46.672 46.662h112.175c8.28 0 15.001 6.72 15.001 15.001v40.002c0 8.29-6.72 15.001-15.001 15.001h-42.172L501.612 452.11c5.851 5.86 5.851 15.351 0 21.211z" }) })), "SvgAlignNo");
var align_no_default = SvgAlignNo;

// src/icons/editor/align-right.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var SvgAlignRight = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx66("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx66("path", { d: "M491 76H21c-8.3 0-15-6.7-15-15V21c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0 286.7H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0-143.4H21c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h470c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15zm0 286.7H221c-8.3 0-15-6.7-15-15v-40c0-8.3 6.7-15 15-15h270c8.3 0 15 6.7 15 15v40c0 8.3-6.7 15-15 15z" }) })), "SvgAlignRight");
var align_right_default = SvgAlignRight;

// src/icons/editor/bold.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var SvgBold = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx67("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx67("path", { d: "M403.321 235.911a136.16 136.16 0 0 0 30.134-72.779C441.691 77.975 374.872 6 291.635 6H66.211c-8.284 0-15 6.716-15 15v59.286c0 8.284 6.716 15 15 15h38.426v321.429H66.211c-8.284 0-15 6.716-15 15V491c0 8.284 6.716 15 15 15h236.473c79.018 0 149.71-57.757 157.366-136.607 5.291-54.074-18.292-102.746-56.729-133.482zM193.689 95.286h97.946c29.587 0 53.571 23.985 53.571 53.571s-23.985 53.571-53.571 53.571h-97.946zm97.946 321.428h-97.946v-125h97.946c34.518 0 62.5 27.982 62.5 62.5s-27.982 62.5-62.5 62.5z" }) })), "SvgBold");
var bold_default = SvgBold;

// src/icons/editor/heading.tsx
import { jsx as jsx68 } from "react/jsx-runtime";
var SvgHeading = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx68("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx68("path", { d: "M456 89.333v333.333h33.333c9.205 0 16.667 7.462 16.667 16.667v33.333c0 9.205-7.462 16.667-16.667 16.667H322.667c-9.205 0-16.667-7.462-16.667-16.667v-33.333c0-9.205 7.462-16.667 16.667-16.667H356V289.333H156v133.333h33.333c9.205 0 16.667 7.462 16.667 16.667v33.333c0 9.205-7.462 16.667-16.667 16.667H22.667C13.462 489.333 6 481.871 6 472.667v-33.333c0-9.205 7.462-16.667 16.667-16.667H56V89.333H22.667C13.462 89.333 6 81.871 6 72.667V39.333c0-9.205 7.462-16.667 16.667-16.667h166.667c9.205 0 16.667 7.462 16.667 16.667v33.333c0 9.205-7.462 16.667-16.667 16.667H156v133.333h200V89.333h-33.333c-9.205 0-16.667-7.462-16.667-16.667V39.333c0-9.205 7.462-16.667 16.667-16.667h166.667c9.205 0 16.667 7.462 16.667 16.667v33.333c0 9.205-7.462 16.667-16.667 16.667z" }) })), "SvgHeading");
var heading_default = SvgHeading;

// src/icons/editor/italic.tsx
import { jsx as jsx69 } from "react/jsx-runtime";
var SvgItalic = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx69("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx69("path", { d: "M434.571 23.857v35.714c0 9.862-7.995 17.857-17.857 17.857H346.67l-89.286 357.143h52.188c9.862 0 17.857 7.995 17.857 17.857v35.714c0 9.862-7.995 17.857-17.857 17.857H95.286c-9.862 0-17.857-7.995-17.857-17.857v-35.714c0-9.862 7.995-17.857 17.857-17.857h70.045l89.286-357.143H202.43c-9.862 0-17.857-7.995-17.857-17.857V23.857C184.573 13.995 192.568 6 202.43 6h214.286c9.861 0 17.855 7.995 17.855 17.857z" }) })), "SvgItalic");
var italic_default = SvgItalic;

// src/icons/editor/link.tsx
import { jsx as jsx70, jsxs as jsxs43 } from "react/jsx-runtime";
var SvgLink = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs43("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: [
  /* @__PURE__ */ jsx70("path", { d: "M324.958 187.046c58.347 58.407 57.546 152.048.352 209.56a10.76 10.76 0 0 1-.352.361l-65.625 65.625c-57.881 57.881-152.05 57.873-209.922 0-57.881-57.871-57.881-152.05 0-209.921l36.236-36.236c9.609-9.609 26.158-3.223 26.654 10.357.633 17.307 3.736 34.694 9.463 51.485 1.939 5.686.554 11.975-3.694 16.223l-12.78 12.78c-27.369 27.369-28.228 71.933-1.128 99.57 27.367 27.909 72.35 28.075 99.927.498l65.625-65.615c27.53-27.53 27.415-72.028 0-99.443-3.614-3.607-7.255-6.41-10.099-8.368a15.66 15.66 0 0 1-6.784-12.311c-.387-10.319 3.27-20.953 11.424-29.107l20.561-20.561c5.392-5.392 13.85-6.054 20.102-1.69a148.956 148.956 0 0 1 20.04 16.793z" }),
  /* @__PURE__ */ jsx70("path", { d: "M462.59 49.408c-57.872-57.873-152.041-57.881-209.922 0l-65.625 65.625c-.117.117-.244.244-.352.361-57.193 57.512-57.995 151.153.352 209.56a148.906 148.906 0 0 0 20.04 16.793c6.252 4.363 14.711 3.7 20.102-1.69l20.561-20.561c8.154-8.154 11.811-18.788 11.424-29.107a15.659 15.659 0 0 0-6.784-12.31c-2.844-1.958-6.484-4.761-10.099-8.368-27.415-27.415-27.53-71.913 0-99.443l65.625-65.615c27.577-27.577 72.559-27.411 99.927.498 27.1 27.637 26.242 72.201-1.128 99.57l-12.78 12.78c-4.248 4.248-5.634 10.537-3.694 16.223 5.727 16.791 8.83 34.179 9.463 51.485.497 13.58 17.045 19.967 26.654 10.357l36.236-36.236c57.88-57.872 57.88-152.051 0-209.922z" })
] })), "SvgLink");
var link_default2 = SvgLink;

// src/icons/editor/list-bullet.tsx
import { jsx as jsx71 } from "react/jsx-runtime";
var SvgListBullet = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx71("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx71("path", { d: "M491 134.75H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15zM491 291H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15zm0 156.25H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15zM52.875 209.125C26.987 209.125 6 230.112 6 256s20.987 46.875 46.875 46.875S99.75 281.888 99.75 256s-20.987-46.875-46.875-46.875zm0 156.25C26.987 365.375 6 386.362 6 412.25s20.987 46.875 46.875 46.875S99.75 438.138 99.75 412.25s-20.987-46.875-46.875-46.875zm0-312.5C26.987 52.875 6 73.862 6 99.75s20.987 46.875 46.875 46.875S99.75 125.638 99.75 99.75 78.763 52.875 52.875 52.875z" }) })), "SvgListBullet");
var list_bullet_default = SvgListBullet;

// src/icons/editor/list-numbered.tsx
import { jsx as jsx72 } from "react/jsx-runtime";
var SvgListNumbered = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx72("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx72("path", { d: "m66.322 397.602 17.09-19.678a19.453 19.453 0 0 0 4.951-13.857v-3.232c0-7.178-3.75-11.084-11.074-11.084H21.625a7.812 7.812 0 0 0-7.812 7.812v15.625A7.812 7.812 0 0 0 21.625 381H43.92a153.764 153.764 0 0 0-10.742 12.021l-5.479 6.836c-3.906 4.951-5.127 9.893-2.734 14.531l1.025 1.885c2.93 5.625 6.143 7.695 11.963 7.695h4.619c10.088 0 15.566 2.383 15.566 8.877 0 4.609-4.102 8.027-14.023 8.027a40.557 40.557 0 0 1-15.107-3.047c-6.338-3.789-11.465-3.418-15.234 3.047l-5.459 9.092c-3.633 5.986-3.115 11.445 2.568 15.566 7.529 4.58 19.902 9.219 36.133 9.219 33.359 0 47.363-22.217 47.363-43.086-.029-14.042-8.906-29.061-28.057-34.061zM17.807 318.5h66.318a7.812 7.812 0 0 0 7.812-7.812v-15.625a7.812 7.812 0 0 0-7.812-7.812H46.352c3.213-10.049 47.207-18.242 47.207-55.117 0-28.379-24.414-38.633-43.428-38.633-20.859 0-33.008 9.766-39.512 18.311-4.268 5.459-2.93 10.586 2.734 15.01l8.379 6.719c5.479 4.453 10.742 2.412 15.742-2.383a13.12 13.12 0 0 1 9.238-3.75c3.252 0 9.062 1.523 9.062 8.545C55.805 248.373 6 257.279 6 303.451v3.906c0 7.237 4.961 11.143 11.807 11.143zm3.818-156.25h62.5a7.812 7.812 0 0 0 7.812-7.812v-15.625a7.812 7.812 0 0 0-7.812-7.812H68.5V45.062a7.812 7.812 0 0 0-7.812-7.812H37.25a7.813 7.813 0 0 0-6.973 4.316l-7.812 15.625A7.813 7.813 0 0 0 29.437 68.5h7.812V131H21.625a7.812 7.812 0 0 0-7.812 7.812v15.625a7.812 7.812 0 0 0 7.812 7.813zM491 134.75H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15zM491 291H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15zm0 156.25H177.25c-8.284 0-15-6.716-15-15v-40c0-8.284 6.716-15 15-15H491c8.284 0 15 6.716 15 15v40c0 8.284-6.716 15-15 15z" }) })), "SvgListNumbered");
var list_numbered_default = SvgListNumbered;

// src/icons/editor/more.tsx
import { jsx as jsx73, jsxs as jsxs44 } from "react/jsx-runtime";
var SvgMore = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsxs44("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24" }, props), { children: [
  /* @__PURE__ */ jsx73("path", { fill: "none", d: "M0 0h24v24H0z" }),
  /* @__PURE__ */ jsx73("path", { d: "M12 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 14c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-7c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" })
] })), "SvgMore");
var more_default = SvgMore;

// src/icons/editor/paragraph.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var SvgParagraph = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx74("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx74("path", { d: "M488.143 23.857v35.714c0 9.862-7.995 17.857-17.857 17.857h-53.571v410.714c0 9.862-7.995 17.857-17.857 17.857h-35.714c-9.862 0-17.857-7.995-17.857-17.857V77.429h-35.714v410.714c0 9.862-7.995 17.857-17.857 17.857H256c-9.862 0-17.857-7.995-17.857-17.857v-125h-35.714c-98.622 0-178.571-79.949-178.571-178.571S103.806 6 202.429 6h267.857c9.862 0 17.857 7.995 17.857 17.857z" }) })), "SvgParagraph");
var paragraph_default = SvgParagraph;

// src/icons/editor/redo.tsx
import { jsx as jsx75 } from "react/jsx-runtime";
var SvgRedo = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx75("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx75("path", { d: "M256.459 6c66.803.12 127.457 26.445 172.237 69.239l36.003-36.003C479.941 23.995 506 34.79 506 56.344v135.14c0 13.362-10.832 24.194-24.194 24.194h-135.14c-21.554 0-32.349-26.059-17.108-41.301l42.087-42.087c-31.113-29.132-71.372-45.269-114.143-45.638-93.143-.805-171.656 74.573-170.851 170.808.764 91.291 74.776 167.895 169.349 167.895 41.459 0 80.642-14.796 111.521-41.891 4.781-4.195 12.002-3.94 16.5.557l39.982 39.982c4.911 4.911 4.668 12.918-.486 17.574C379.188 481.618 320.442 506 256 506 117.929 506 6.001 394.072 6 256.002 5.999 118.09 118.546 5.753 256.459 6z" }) })), "SvgRedo");
var redo_default = SvgRedo;

// src/icons/editor/underline.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var SvgUnderline = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx76("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx76("path", { d: "M467.272 440.993H44.728c-8.976 0-16.252 7.276-16.252 16.252v32.503c0 8.976 7.276 16.252 16.252 16.252h422.544c8.976 0 16.252-7.276 16.252-16.252v-32.503c0-8.976-7.276-16.252-16.252-16.252zM60.667 71.111h32.555v162.777c0 89.751 73.026 162.777 162.777 162.777s162.777-73.026 162.777-162.777V71.111h32.555c8.99 0 16.278-7.288 16.278-16.278V22.278c0-8.99-7.288-16.278-16.278-16.278h-146.5c-8.99 0-16.278 7.288-16.278 16.278v32.555c0 8.99 7.288 16.278 16.278 16.278h32.555v162.777c0 44.95-36.439 81.389-81.389 81.389s-81.389-36.439-81.389-81.389V71.111h32.555c8.99 0 16.278-7.288 16.278-16.278V22.278c0-8.99-7.288-16.278-16.278-16.278h-146.5c-8.99 0-16.278 7.288-16.278 16.278v32.555c.004 8.99 7.292 16.278 16.282 16.278z" }) })), "SvgUnderline");
var underline_default = SvgUnderline;

// src/icons/editor/undo.tsx
import { jsx as jsx77 } from "react/jsx-runtime";
var SvgUndo = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx77("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx77("path", { d: "M506 256.002C505.999 394.072 394.071 506 256 506c-64.442 0-123.188-24.382-167.517-64.423-5.154-4.655-5.397-12.662-.486-17.574l39.982-39.982c4.498-4.497 11.719-4.752 16.5-.557 30.879 27.095 70.062 41.891 111.521 41.891 94.573 0 168.585-76.604 169.349-167.895.805-96.235-77.708-171.613-170.851-170.808-42.771.369-83.03 16.506-114.143 45.638l42.087 42.087c15.241 15.242 4.447 41.301-17.108 41.301H30.194C16.832 215.678 6 204.846 6 191.484V56.344C6 34.79 32.059 23.995 47.301 39.236l36.003 36.003C128.085 32.445 188.738 6.12 255.541 6 393.454 5.753 506.001 118.09 506 256.002z" }) })), "SvgUndo");
var undo_default = SvgUndo;

// src/icons/editor/unformat.tsx
import { jsx as jsx78 } from "react/jsx-runtime";
var SvgUnformat = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx78("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx78("path", { d: "M268.497 380.995h-8.727l7.234-21.695-52.414-40.492-20.711 62.187h-25.383c-6.904 0-12.5 5.596-12.5 12.5v25c0 6.904 5.596 12.5 12.5 12.5h99.999c6.904 0 12.5-5.596 12.5-12.5v-25c.002-6.903-5.594-12.5-12.498-12.5zm232.671 32.891-200.64-155.085 38.414-115.304h92.054v25c0 6.904 5.596 12.5 12.5 12.5h25c6.904 0 12.5-5.596 12.5-12.5v-75c0-6.904-5.596-12.5-12.5-12.5H143.498c-6.904 0-12.5 5.596-12.5 12.5v34.297L41.514 58.638c-5.448-4.24-13.302-3.26-17.542 2.189l-.005.007L8.632 80.575c-4.236 5.45-3.254 13.302 2.195 17.539l459.654 355.248c5.448 4.24 13.302 3.26 17.542-2.189l.005-.007 15.344-19.742c4.234-5.451 3.248-13.303-2.204-17.538zM248.115 218.309l-67.117-51.875v-22.937h92.054z" }) })), "SvgUnformat");
var unformat_default = SvgUnformat;

// src/icons/editor/unlink.tsx
import { jsx as jsx79 } from "react/jsx-runtime";
var SvgUnlink = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx79("svg", __spreadProps(__spreadValues({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512" }, props), { children: /* @__PURE__ */ jsx79("path", { d: "M302.955 402.394c4.576 4.576 4.576 11.996 0 16.573l-43.627 43.627c-57.874 57.873-152.044 57.877-209.923 0-57.875-57.876-57.875-152.047 0-209.922l43.628-43.628c4.576-4.576 11.996-4.576 16.573 0l38.67 38.67c4.576 4.576 4.576 11.996 0 16.573l-43.628 43.627c-27.414 27.415-27.414 72.021 0 99.436 27.414 27.414 72.021 27.415 99.437 0l43.627-43.627c4.576-4.576 11.996-4.576 16.573 0zm174.082 96.742 22.097-22.097c9.153-9.153 9.153-23.993 0-33.145L68.107 12.865c-9.153-9.153-23.993-9.153-33.145 0L12.864 34.963c-9.153 9.153-9.153 23.993 0 33.145l431.028 431.028c9.153 9.152 23.993 9.152 33.145 0zM247.713 148.277c4.576 4.576 11.996 4.576 16.573 0l43.627-43.627c27.414-27.417 72.021-27.415 99.437 0 27.414 27.415 27.414 72.021 0 99.436l-43.628 43.627c-4.576 4.576-4.576 11.996 0 16.573l38.67 38.67c4.576 4.576 11.996 4.576 16.573 0l43.628-43.628c57.876-57.876 57.876-152.046 0-209.922-57.877-57.875-152.046-57.875-209.923 0l-43.627 43.627c-4.576 4.576-4.576 11.996 0 16.573z" }) })), "SvgUnlink");
var unlink_default = SvgUnlink;

// src/icons/editor/video.tsx
import { jsx as jsx80 } from "react/jsx-runtime";
var SvgVideo = /* @__PURE__ */ __name((props) => /* @__PURE__ */ jsx80(
  "svg",
  __spreadProps(__spreadValues({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "currentColor",
    className: "video_svg__w-6 video_svg__h-6",
    viewBox: "0 0 24 24"
  }, props), {
    children: /* @__PURE__ */ jsx80("path", { d: "M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5zm15.44 14.25-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06z" })
  })
), "SvgVideo");
var video_default = SvgVideo;

// src/icons/editor/index.ts
var EditorIcons = {
  AlignCenter: align_center_default,
  AlignLeft: align_left_default,
  AlignNo: align_no_default,
  AlignRight: align_right_default,
  Bold: bold_default,
  Heading: heading_default,
  Italic: italic_default,
  Link: link_default2,
  ListBullet: list_bullet_default,
  ListNumbered: list_numbered_default,
  More: more_default,
  Paragraph: paragraph_default,
  Redo: redo_default,
  Underline: underline_default,
  Undo: undo_default,
  Unformat: unformat_default,
  Unlink: unlink_default,
  Video: video_default
};
var editor_default = EditorIcons;

// src/components/tiptap_editor.tsx
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link3 from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Typography from "@tiptap/extension-typography";
import TextAlign from "@tiptap/extension-text-align";
import { jsx as jsx81, jsxs as jsxs45 } from "react/jsx-runtime";
var defaultNodes = [
  { node: "paragraph", param: "paragraph", label: "Paragraph" },
  { node: "heading", param: 1, label: "Heading 1" },
  { node: "heading", param: 2, label: "Heading 2" },
  { node: "heading", param: 3, label: "Heading 3" }
];
var defaultExtensions = [
  StarterKit.configure(),
  Placeholder,
  Underline,
  Link3.configure({
    openOnClick: false,
    HTMLAttributes: { target: "_self", rel: "noopener" }
  }),
  Subscript,
  Superscript,
  Highlight,
  Image.configure({ inline: true }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false
  })
];
var commandWithSelection = /* @__PURE__ */ __name((editor, before) => {
  const selection = wordSelection(editor);
  let chain = editor == null ? void 0 : editor.chain().focus();
  if (before) {
    chain = before(chain);
  }
  if (selection) {
    chain = chain == null ? void 0 : chain.command((c) => c.commands.setTextSelection({ from: selection.from, to: selection.to }));
  }
  return chain;
}, "commandWithSelection");
var wordSelection = /* @__PURE__ */ __name((editor) => {
  var _a, _b, _c, _d;
  if (!(editor == null ? void 0 : editor.state.selection.empty)) {
    return null;
  }
  for (const range of editor.state.selection.ranges || []) {
    const { $from, $to } = range;
    let from = $from.pos;
    let to = $to.pos;
    const start = $from.nodeBefore;
    const end = $to.nodeAfter;
    if ((start == null ? void 0 : start.isText) && (end == null ? void 0 : end.isText) && start.text && end.text) {
      const spaceStart = ((_b = (_a = start.text.match(/\S+/g)) == null ? void 0 : _a.at(-1)) == null ? void 0 : _b.length) || 0;
      const spaceEnd = ((_d = (_c = end.text.match(/\S+/g)) == null ? void 0 : _c.at(0)) == null ? void 0 : _d.length) || 0;
      if (from - spaceStart < to) {
        from -= spaceStart;
        to += spaceEnd;
      }
      return { from, to };
    }
  }
  return null;
}, "wordSelection");
var EditorContext = React41.createContext(null);
var useEditorContext = /* @__PURE__ */ __name(() => React41.useContext(EditorContext), "useEditorContext");
var EditorFocusedContext = React41.createContext(false);
var useEditorFocusContext = /* @__PURE__ */ __name(() => React41.useContext(EditorFocusedContext), "useEditorFocusContext");
var applyNode = /* @__PURE__ */ __name((editor, option) => {
  switch (option.node) {
    case "paragraph": {
      return editor == null ? void 0 : editor.chain().focus().command((c) => c.commands.setNode("paragraph", { style: option.param })).run();
    }
    case "heading": {
      return editor == null ? void 0 : editor.chain().focus().toggleHeading({ level: option.param }).run();
    }
    case "root":
      return editor == null ? void 0 : editor.chain().focus().command((c) => c.commands.setNode("div")).run();
  }
}, "applyNode");
var cmdBold = /* @__PURE__ */ __name((editor) => {
  var _a;
  (_a = commandWithSelection(editor)) == null ? void 0 : _a.toggleBold().run();
}, "cmdBold");
var cmdItalic = /* @__PURE__ */ __name((editor) => {
  var _a;
  (_a = commandWithSelection(editor)) == null ? void 0 : _a.toggleItalic().run();
}, "cmdItalic");
var cmdUnderline = /* @__PURE__ */ __name((editor) => {
  var _a;
  (_a = commandWithSelection(editor)) == null ? void 0 : _a.toggleUnderline().run();
}, "cmdUnderline");
var cmdAlign = /* @__PURE__ */ __name((editor, align) => {
  editor == null ? void 0 : editor.chain().focus().setTextAlign(align).run();
}, "cmdAlign");
var cmdLink = /* @__PURE__ */ __name((editor, link) => {
  var _a;
  (_a = commandWithSelection(editor, (c) => c == null ? void 0 : c.extendMarkRange("link"))) == null ? void 0 : _a.setLink({ href: link }).run();
}, "cmdLink");
var cmdUnlink = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().unsetLink().run();
}, "cmdUnlink");
var cmdYtVideo = /* @__PURE__ */ __name((editor, src) => {
  return editor == null ? void 0 : editor.chain().focus().command((c) => c.commands.setYoutubeVideo({ src })).run();
}, "cmdYtVideo");
var cmdBulletList = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().toggleBulletList().run();
}, "cmdBulletList");
var cmdOrderedList = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().toggleOrderedList().run();
}, "cmdOrderedList");
var cmdReset = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().clearNodes().unsetAllMarks().run();
}, "cmdReset");
var cmdUndo = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().undo().run();
}, "cmdUndo");
var cmdRedo = /* @__PURE__ */ __name((editor) => {
  editor == null ? void 0 : editor.chain().focus().redo().run();
}, "cmdRedo");
var MenuSet = /* @__PURE__ */ __name(({ id, hidden, children }) => {
  return /* @__PURE__ */ jsx81(
    "div",
    {
      "data-menuid": id,
      className: cn30("flex-shrink-0 flex overflow-hidden gap-0.5", {
        "invisible pointer-events-none": hidden
      }),
      children
    }
  );
}, "MenuSet");
var MenuButton = /* @__PURE__ */ __name(({ id, title, icon, active, disabled, hidden, cmd }) => {
  const editor = useEditorContext();
  const focused = useEditorFocusContext();
  const Icon = editor_default[icon];
  const onClick = /* @__PURE__ */ __name((e) => {
    e.preventDefault();
    cmd(editor);
  }, "onClick");
  return /* @__PURE__ */ jsx81(
    "button",
    {
      "data-menuid": id,
      className: cn30(
        "flex-shrink-0 cursor-pointer h-8 px-1.5 flex items-center justify-center rounded disabled:pointer-events-none disabled:opacity-50 focus:outline-none",
        {
          "invisible pointer-events-none": hidden,
          "hover:bg-slate-600 hover:text-ds9-background dark:hover:bg-ds9-200": focused,
          "hover:bg-slate-300 dark:hover:bg-ds9-200 dark:hover:text-ds9-background": !focused,
          "text-ds9-background bg-slate-600 dark:bg-ds9-200": active && focused
        }
      ),
      tabIndex: 0,
      title,
      disabled,
      onClick,
      type: "button",
      children: /* @__PURE__ */ jsx81(Icon, { className: "h-4 w-4 fill-current" })
    }
  );
}, "MenuButton");
var Divider2 = /* @__PURE__ */ __name(() => {
  return /* @__PURE__ */ jsx81("div", { className: "ds9-tiptap-divider border-l border-ds9-200 dark:border-ds9-700" });
}, "Divider");
var buttonList = /* @__PURE__ */ __name((editor, openLinkPrompt, openYtVideoPrompt, showLists = true) => {
  const basic = [];
  if (editor == null ? void 0 : editor.commands.setBold) {
    basic.push({
      id: "bold",
      icon: "Bold",
      title: "Bold",
      cmd: cmdBold,
      active: editor == null ? void 0 : editor.isActive("bold")
    });
  }
  if (editor == null ? void 0 : editor.commands.setItalic) {
    basic.push({
      id: "italic",
      icon: "Italic",
      title: "Italic",
      cmd: cmdItalic,
      active: editor == null ? void 0 : editor.isActive("italic")
    });
  }
  if (editor == null ? void 0 : editor.commands.setUnderline) {
    basic.push({
      id: "underline",
      icon: "Underline",
      title: "Underline",
      cmd: cmdUnderline,
      active: editor == null ? void 0 : editor.isActive("underline")
    });
  }
  const aligns = (editor == null ? void 0 : editor.commands.setTextAlign) ? [
    {
      set: "aligns",
      buttons: [
        {
          icon: "AlignLeft",
          title: "Left align",
          cmd: (e) => cmdAlign(e, "left"),
          active: editor == null ? void 0 : editor.isActive({ textAlign: "left" })
        },
        {
          icon: "AlignCenter",
          title: "Center align",
          cmd: (e) => cmdAlign(e, "center"),
          active: editor == null ? void 0 : editor.isActive({ textAlign: "center" })
        },
        {
          icon: "AlignRight",
          title: "Right align",
          cmd: (e) => cmdAlign(e, "right"),
          active: editor == null ? void 0 : editor.isActive({ textAlign: "right" })
        }
      ]
    }
  ] : [];
  const links = (editor == null ? void 0 : editor.commands.setLink) ? [
    {
      set: "links",
      buttons: [
        {
          icon: "Link",
          title: "Link",
          cmd: openLinkPrompt,
          active: editor == null ? void 0 : editor.isActive("link")
        },
        {
          icon: "Unlink",
          title: "Remove link",
          cmd: cmdUnlink,
          disabled: !(editor == null ? void 0 : editor.isActive("link"))
        }
      ]
    }
  ] : [];
  const youtube = [];
  if (editor == null ? void 0 : editor.commands.setYoutubeVideo) {
    youtube.push({
      id: "youtube",
      icon: "Video",
      title: "YouTube Video",
      cmd: openYtVideoPrompt,
      active: editor == null ? void 0 : editor.isActive("youtubeVideo")
    });
  }
  const lists = [];
  if (showLists && (editor == null ? void 0 : editor.commands.toggleBulletList)) {
    lists.push({
      id: "listbullet",
      icon: "ListBullet",
      title: "Bullet list",
      cmd: cmdBulletList,
      active: editor == null ? void 0 : editor.isActive("bulletList"),
      disabled: !(editor == null ? void 0 : editor.can().toggleBulletList())
    });
  }
  if (showLists && (editor == null ? void 0 : editor.commands.toggleOrderedList)) {
    lists.push({
      id: "listnumbered",
      icon: "ListNumbered",
      title: "Numbered list",
      cmd: cmdOrderedList,
      active: editor == null ? void 0 : editor.isActive("orderedList"),
      disabled: !(editor == null ? void 0 : editor.can().toggleOrderedList())
    });
  }
  const misc = [
    {
      id: "unformat",
      icon: "Unformat",
      title: "Remove formatting",
      cmd: cmdReset
    },
    {
      set: "undos",
      buttons: [
        {
          icon: "Undo",
          title: "Undo",
          cmd: cmdUndo,
          disabled: !(editor == null ? void 0 : editor.can().undo())
        },
        {
          icon: "Redo",
          title: "Redo",
          cmd: cmdRedo,
          disabled: !(editor == null ? void 0 : editor.can().redo())
        }
      ]
    }
  ];
  return [
    ...basic,
    "divider",
    ...aligns,
    "divider",
    ...links,
    "divider",
    ...youtube,
    "divider",
    ...lists,
    "divider",
    ...misc
  ];
}, "buttonList");
var Menu2 = /* @__PURE__ */ __name(({
  nodes,
  lists,
  nodeOptions,
  overflowOpen,
  disabled,
  stickyMenu,
  setOverflowOpen
}) => {
  const editor = useEditorContext();
  const buttonMenu = useRef5(null);
  const [visibilityMap, setVisibilityMap] = useState7({});
  const handleIntersection = /* @__PURE__ */ __name((entries) => {
    const updatedEntries = {};
    entries.forEach((entry) => {
      if (entry.target instanceof HTMLElement) {
        const targetid = entry.target.dataset.menuid;
        if (targetid) {
          if (entry.isIntersecting) {
            updatedEntries[targetid] = true;
          } else {
            updatedEntries[targetid] = false;
          }
        }
      }
    });
    setVisibilityMap((prev) => __spreadValues(__spreadValues({}, prev), updatedEntries));
  }, "handleIntersection");
  useEffect4(() => {
    var _a;
    const observer = new IntersectionObserver(handleIntersection, {
      root: buttonMenu.current,
      threshold: 1
    });
    Array.from(((_a = buttonMenu.current) == null ? void 0 : _a.children) || []).forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.menuid) {
        observer.observe(item);
      }
    });
    return () => observer.disconnect();
  }, []);
  let activeOption;
  if (nodes) {
    if (editor == null ? void 0 : editor.isActive("heading")) {
      const level = editor.getAttributes("heading")["level"];
      activeOption = nodeOptions.find((option) => option.node === "heading" && option.param === level);
    } else if (editor == null ? void 0 : editor.isActive("paragraph")) {
      const style = editor.getAttributes("paragraph")["style"];
      activeOption = nodeOptions.find((option) => option.node === "paragraph" && option.param === style);
    } else if (editor == null ? void 0 : editor.isActive("div")) {
      activeOption = nodeOptions.find((option) => option.node === "root");
    }
  }
  const [link, setLink] = useState7("");
  const [linkOpen, setLinkOpen] = useState7(false);
  const openLinkPrompt = /* @__PURE__ */ __name(() => {
    const current = editor == null ? void 0 : editor.getAttributes("link").href;
    setLink(current || "");
    setLinkOpen(true);
  }, "openLinkPrompt");
  const confirmLink = /* @__PURE__ */ __name((e) => {
    e.preventDefault();
    setLinkOpen(false);
    cmdLink(editor, link);
  }, "confirmLink");
  const [ytVideoUrl, setYtVideoUrl] = useState7("");
  const [ytVideoValid, setYtVideoValid] = useState7(false);
  const [ytVideoOpen, setYtVideoOpen] = useState7(false);
  const updateYtVideoSrc = /* @__PURE__ */ __name((url) => {
    setYtVideoUrl(url);
    setYtVideoValid(isValidYoutubeUrl(url));
  }, "updateYtVideoSrc");
  const openYtVideoPrompt = /* @__PURE__ */ __name(() => {
    const current = editor == null ? void 0 : editor.getAttributes("youtubeVideo").src;
    updateYtVideoSrc(current || "");
    setYtVideoOpen(true);
  }, "openYtVideoPrompt");
  const confirmYtVideo = /* @__PURE__ */ __name((e) => {
    e.preventDefault();
    if (!ytVideoValid) {
      return;
    }
    setYtVideoOpen(false);
    cmdYtVideo(editor, ytVideoUrl);
  }, "confirmYtVideo");
  const buttons = buttonList(editor, openLinkPrompt, openYtVideoPrompt, lists);
  const hasOverflow = Object.keys(visibilityMap).some((key) => visibilityMap[key] === false);
  return /* @__PURE__ */ jsxs45(
    "div",
    {
      className: cn30("flex items-center gap-0.5 p-1 bg-ds9-50 dark:bg-ds9-900 rounded-ds9", {
        "sticky top-0 z-10": stickyMenu
      }),
      children: [
        nodes ? /* @__PURE__ */ jsx81(
          select_default,
          {
            selected: activeOption || nodeOptions[0],
            onSelected: (option) => option && applyNode(editor, option),
            selectedLabel: (option) => (option == null ? void 0 : option.shortLabel) || (option == null ? void 0 : option.label) || "Text",
            size: "s",
            className: "w-40 bg-ds9-background",
            disabled,
            children: nodeOptions.map((option, i) => /* @__PURE__ */ jsx81(select_default.Option, { value: option, children: ({ selected }) => /* @__PURE__ */ jsxs45("div", { className: "flex justify-between gap-2", children: [
              /* @__PURE__ */ jsx81("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsx81(
                "span",
                {
                  className: cn30("whitespace-nowrap truncate", {
                    "font-medium": !selected,
                    "font-semibold": selected
                  }),
                  children: option.label
                }
              ) }),
              selected ? /* @__PURE__ */ jsx81(CheckIcon3, { className: "h-5 w-5 flex-shrink-0", "aria-hidden": "true" }) : null
            ] }) }, i))
          }
        ) : null,
        /* @__PURE__ */ jsxs45("div", { ref: buttonMenu, className: "ml-0.5 flex gap-0.5 overflow-hidden min-w-0", children: [
          buttons.map((button, i) => {
            if (button === "divider") {
              return /* @__PURE__ */ jsx81(Divider2, {}, i);
            }
            if ("set" in button) {
              return /* @__PURE__ */ jsx81(MenuSet, { id: button.set, hidden: visibilityMap[button.set] === false, children: button.buttons.map((setButton, i2) => /* @__PURE__ */ jsx81(MenuButton, __spreadProps(__spreadValues({}, setButton), { disabled: disabled || setButton.disabled }), i2)) }, i);
            }
            return /* @__PURE__ */ jsx81(
              MenuButton,
              __spreadProps(__spreadValues({}, button), {
                hidden: button.id ? visibilityMap[button.id] === false : false,
                disabled: disabled || button.disabled
              }),
              i
            );
          }),
          hasOverflow ? /* @__PURE__ */ jsx81(Divider2, {}) : null
        ] }),
        hasOverflow ? /* @__PURE__ */ jsxs45("div", { className: "relative flex-shrink-0 flex gap-0.5", children: [
          /* @__PURE__ */ jsx81(
            MenuButton,
            {
              id: "overflow",
              icon: "More",
              title: "More",
              cmd: () => setOverflowOpen((s) => !s),
              active: overflowOpen,
              disabled
            }
          ),
          overflowOpen ? /* @__PURE__ */ jsx81("div", { className: "absolute bottom-full right-0 mb-1 z-20 bg-ds9-background border border-ds9-500 dark:border-ds9-400 rounded-ds9 px-2 py-2 overflow-auto flex gap-0.5", children: buttons.map((button, i) => {
            if (button === "divider") {
              return /* @__PURE__ */ jsx81(Divider2, {}, i);
            }
            if ("set" in button) {
              if (visibilityMap[button.set] !== false) {
                return null;
              }
              return /* @__PURE__ */ jsx81(MenuSet, { id: button.set, children: button.buttons.map((setButton, i2) => /* @__PURE__ */ jsx81(MenuButton, __spreadProps(__spreadValues({}, setButton), { disabled: disabled || setButton.disabled }), i2)) }, i);
            }
            if (button.id && visibilityMap[button.id] !== false) {
              return null;
            }
            return /* @__PURE__ */ jsx81(MenuButton, __spreadProps(__spreadValues({}, button), { disabled: disabled || button.disabled }), i);
          }) }) : null
        ] }) : null,
        /* @__PURE__ */ jsx81(modal_default, { width: "500px", open: linkOpen, setOpen: setLinkOpen, children: /* @__PURE__ */ jsxs45("form", { onSubmit: confirmLink, children: [
          /* @__PURE__ */ jsxs45(modal_default.Body, { children: [
            /* @__PURE__ */ jsx81(modal_default.Title, { children: "Link" }),
            /* @__PURE__ */ jsx81("main", { children: /* @__PURE__ */ jsx81(
              input_default,
              {
                label: "URL",
                placeholder: "https://skyltmax.se/signomatic",
                value: link,
                onChange: (e) => setLink(e.target.value)
              }
            ) })
          ] }),
          /* @__PURE__ */ jsxs45(modal_default.Footer, { children: [
            /* @__PURE__ */ jsx81(button_default, { onClick: () => setLinkOpen(false), children: "Cancel" }),
            /* @__PURE__ */ jsx81(button_default, { variant: "primary", onClick: confirmLink, type: "submit", children: "Update" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx81(modal_default, { width: "500px", open: ytVideoOpen, setOpen: setYtVideoOpen, children: /* @__PURE__ */ jsxs45("form", { onSubmit: confirmYtVideo, children: [
          /* @__PURE__ */ jsxs45(modal_default.Body, { children: [
            /* @__PURE__ */ jsx81(modal_default.Title, { children: "YouTube Video" }),
            /* @__PURE__ */ jsx81("main", { children: /* @__PURE__ */ jsx81(
              input_default,
              {
                label: "Video URL",
                placeholder: "https://www.youtube.com/watch?v=5ch94AaPZRQ",
                value: ytVideoUrl,
                onChange: (e) => updateYtVideoSrc(e.target.value),
                error: !ytVideoValid ? !isBlank(ytVideoUrl) : void 0
              }
            ) })
          ] }),
          /* @__PURE__ */ jsxs45(modal_default.Footer, { children: [
            /* @__PURE__ */ jsx81(button_default, { onClick: () => setYtVideoOpen(false), children: "Cancel" }),
            /* @__PURE__ */ jsx81(button_default, { variant: "primary", onClick: confirmYtVideo, type: "submit", disabled: !ytVideoValid, children: (editor == null ? void 0 : editor.isActive("youtubeVideo")) ? "Update" : "Insert" })
          ] })
        ] }) })
      ]
    }
  );
}, "Menu");
var TiptapEditor = /* @__PURE__ */ __name(({
  nodeOptions,
  extensions,
  className,
  contentClassName,
  label,
  labelCapitalize,
  content,
  nodes = true,
  lists = true,
  stickyMenu,
  disabled,
  skeleton,
  onChange
}) => {
  const [focused, setFocused] = useState7(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState7(false);
  const ref = useRef5(null);
  const lastUpdate = useRef5();
  const triggerChange = /* @__PURE__ */ __name((content2) => {
    lastUpdate.current = content2;
    onChange == null ? void 0 : onChange(content2);
  }, "triggerChange");
  disabled = disabled || skeleton;
  const extensionStack = extensions || defaultExtensions;
  const editor = useEditor2({
    extensions: extensionStack,
    content: typeof content === "object" && objEmpty(content) ? "" : content,
    onUpdate: ({ editor: editor2 }) => {
      triggerChange(editor2.getJSON());
    },
    onCreate: ({ editor: editor2 }) => {
      triggerChange(editor2.getJSON());
    },
    onFocus: () => {
      setFocused(true);
    }
  });
  useEffect4(() => {
    if (!editor || typeof content === "object" && objEmpty(content))
      return;
    if (content && lastUpdate.current !== content) {
      queueMicrotask(() => {
        editor.commands.setContent(content);
      });
    }
  }, [editor, content]);
  useEffect4(() => {
    const handler = /* @__PURE__ */ __name((e) => {
      if (!ref.current)
        return;
      if (showOverflowMenu && e.target instanceof Element && !ref.current.contains(e.target)) {
        setShowOverflowMenu(false);
      }
      if (e.target instanceof Element && !ref.current.contains(e.target)) {
        setFocused(false);
      }
    }, "handler");
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, [showOverflowMenu]);
  return /* @__PURE__ */ jsxs45("div", { children: [
    label ? /* @__PURE__ */ jsx81(label_default, { capitalize: labelCapitalize, onClick: () => editor == null ? void 0 : editor.chain().focus().run(), children: label }) : null,
    /* @__PURE__ */ jsxs45(
      "div",
      {
        ref,
        className: cn30(
          "ds9-tiptap-editor relative form-textarea text-sm leading-normal rounded-ds9 border p-1 bg-ds9-background transition-colors",
          {
            "border-ds9-200 dark:border-ds9-700": !focused,
            "border-ds9-500 dark:border-ds9-400": focused,
            "pointer-events-none": disabled
          },
          className
        ),
        children: [
          /* @__PURE__ */ jsx81(EditorContext.Provider, { value: editor, children: /* @__PURE__ */ jsx81(EditorFocusedContext.Provider, { value: focused, children: editor ? /* @__PURE__ */ jsx81(
            Menu2,
            {
              nodes,
              lists,
              nodeOptions: nodeOptions || defaultNodes,
              overflowOpen: showOverflowMenu,
              setOverflowOpen: setShowOverflowMenu,
              stickyMenu,
              disabled
            }
          ) : null }) }),
          skeleton ? /* @__PURE__ */ jsx81("div", { className: "ds9-skeleton h-5 m-1" }) : null,
          !skeleton ? /* @__PURE__ */ jsx81(EditorContent2, { editor, className: contentClassName }) : null
        ]
      }
    )
  ] });
}, "TiptapEditor");
var tiptap_editor_default = TiptapEditor;
export {
  autocomplete_default as Autocomplete,
  avatar_default as Avatar,
  badge_default as Badge,
  button_default as Button,
  ButtonContent,
  button_link_default as ButtonLink,
  checkbox_default as Checkbox,
  container_default as Container,
  DS9Context,
  DS9Provider,
  divider_default as Divider,
  empty_state_default as EmptyState,
  fieldset_default as Fieldset,
  flag_default as Flag,
  flyout_default as Flyout,
  form_default as Form,
  form_autocomplete_default as FormAutocomplete,
  form_checkbox_default as FormCheckbox,
  form_input_default as FormInput,
  form_select_default as FormSelect,
  form_textarea_default as FormTextarea,
  form_toggle_default as FormToggle,
  highlighter_default as Highlighter,
  inline_edit_default as InlineEdit,
  input_default as Input,
  label_default as Label,
  link_default as Link,
  list_default as List,
  locale_badge_default as LocaleBadge,
  menu_default as Menu,
  modal_default as Modal,
  note_default as Note,
  overflow_list_default as OverflowList,
  page_header_default as PageHeader,
  progress_default as Progress,
  progress_circle_default as ProgressCircle,
  radio_group_default as RadioGroup,
  select_default as Select,
  spinner_default as Spinner,
  tag_default as Tag,
  textarea_default as Textarea,
  ThemeManager,
  ThemeOption,
  theme_switcher_default as ThemeSwitcher,
  TimeHuman,
  TimeSince,
  tiptap_display_default as TiptapDisplay,
  TiptapDisplayInteractive,
  tiptap_editor_default as TiptapEditor,
  toast_default as Toast,
  toggle_default as Toggle,
  tooltip_default as Tooltip,
  defaultExtensions,
  useCheckbox,
  useDS9Context,
  useEditorContext,
  useEditorFocusContext
};
//# sourceMappingURL=index.js.map